/**
 * @file        utils.js
 * @description This file contains all the common functions which are used in the application.
 * @version     0.1.0
 * @copyright   This computer program, as defined in the Copyright, Designs and Patents Act 1998 and the Software Directive (2009/24/EC), 
                is the copyright of Logic Valley Ltd, a wholly owned subsidiary of Marston (Holdings) Ltd.
 * @license     All rights reserved.
 */
// System defined variables
import React from 'react'
import { DateTime } from 'luxon'
import _ from 'lodash'
import axios from 'axios'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import CryptoJS from 'crypto-js'
import { parse } from 'date-fns'

// Custom defined variables
import homeGrey from '../assets/static/Marston_NPS_Grey/Home_Grey.svg'
import homeBlue from '../assets/static/Marston_NPS_Blue/Home_Blue.svg'
import reportsGrey from '../assets/static/Marston_NPS_Grey/Reports_Grey.svg'
import reportsBlue from '../assets/static/Marston_NPS_Blue/Reports_Blue.svg'
import hhdMessageGrey from '../assets/static/Marston_NPS_Grey/HHDMessage_Grey.svg'
import hhdMessageBlue from '../assets/static/Marston_NPS_Blue/HHDMessage_Blue.svg'
import approvalQueueGrey from '../assets/static/Marston_NPS_Grey/ApprovalQueue_Grey.svg'
import approvalQueueBlue from '../assets/static/Marston_NPS_Blue/ApprovalQueue_Blue.svg'
import configurationGrey from '../assets/static/Marston_NPS_Grey/Configuration_Grey.svg'
import configurationBlue from '../assets/static/Marston_NPS_Blue/Configuration_Blue.svg'
import searchGrey from '../assets/static/Marston_NPS_Grey/Search_Grey.svg'
import searchBlue from '../assets/static/Marston_NPS_Blue/Search_blue.svg'
import paymentPlanBlue from '../assets/static/Marston_NPS_Blue/PaymentPlan_Blue.svg'
import paymentPlanGrey from '../assets/static/Marston_NPS_Grey/PaymentPlan_Grey.svg'
import clientSetupGrey from '../assets/static/Marston_NPS_Grey/Client_Setup.svg'
import clientSetupBlue from '../assets/static/Marston_NPS_Blue/ClientSetup_Blue.svg'
import workflowManagerGrey from '../assets/static/Marston_NPS_Grey/WorkflowManager_Grey.svg'
import workflowManagerBlue from '../assets/static/Marston_NPS_Blue/WorkflowManager_Blue.svg'
import myWorkflowGrey from '../assets/static/Marston_NPS_Grey/MyWorkflow_Grey.svg'
import myWorkflowBlue from '../assets/static/Marston_NPS_Blue/MyWorkflow_Blue.svg'
import noticeProcessingGrey from '../assets/static/Marston_NPS_Grey/Notice_Processing_Grey.svg'
import noticeProcessingBlue from '../assets/static/Marston_NPS_Blue/Notice_Processing_Blue.svg'
//import representationGrey from '../assets/static/Marston_NPS_Grey/RepresentationReview_Grey.svg'
//import representationBlue from '../assets/static/Marston_NPS_Blue/RepresentationReview_Blue.svg'
import wokinghamGrey from '../assets/static/Marston_NPS_Grey/Wokingham_Grey.svg'
import wokinghamBlue from '../assets/static/Marston_NPS_Blue/Wokingham_Blue.svg'
import liveTrackingGrey from '../assets/static/Marston_NPS_Grey/LiveTracking_Grey.svg'
import liveTrackingBlue from '../assets/static/Marston_NPS_Blue/LiveTracking_Blue.svg'
import enforcementMapEnable from '../assets/static/Marston_NPS_Blue/EnforcementMap_Blue.svg'
import enforcementMapDisable from '../assets/static/Marston_NPS_Grey/EnforcementMap_Grey.svg'
import { createTheme } from '@mui/material'
import manualCaseBlue from '../assets/static/Marston_NPS_Blue/manualCase_Blue.svg'
import manualCaseGrey from '../assets/static/Marston_NPS_Grey/Manual_case_Grey.svg'
import CR_ConfigBlue from '../assets/static/Marston_NPS_Blue/CR_Config_Blue.svg'
import CR_ConfigGrey from '../assets/static/Marston_NPS_Grey/CR_Config_grey.svg'
import clampAndRemovalBlue from '../assets/static/Marston_NPS_Blue/ClampAndRemovalBlue.svg'
import clampRemovalGrey from '../assets/static/Marston_NPS_Grey/ClampRemovalGrey.svg'
import AIRABlueIcon from '../assets/static/Marston_NPS_Blue/Aira_blueIcon.svg'
import AIRAGreyIcon from '../assets/static/Marston_NPS_Grey/Aira_greyIcon.svg'
import preCasesBlue from '../assets/static/Marston_NPS_Blue/preCasesBlue.svg'
import preCasesGrey from '../assets/static/Marston_NPS_Grey/preCasesGrey.svg'
import printAdminBlue from '../assets/static/Marston_NPS_Blue/printAdminBlue.svg'
import printAdminGrey from '../assets/static/Marston_NPS_Grey/printAdminGrey.svg'
import cctvConfurationGrey from '../assets/static/Marston_NPS_Grey/cctvConfurationGrey.svg'
import cctvConfigurationBlue from '../assets/static/Marston_NPS_Blue/cctvConfigurationBlue.svg'
import cctvReviewBlue from '../assets/static/Marston_NPS_Blue/cctvReviewBlue.svg'
import cctvReviewGrey from '../assets/static/Marston_NPS_Grey/cctvReviewGrey.svg'
import caseLockBlue from '../assets/static/caseLockBlue.svg'
import caseLockGrey from '../assets/static/caseLockGrey.svg'

// function to sort string and numeric values in array of objects based on a key -- start
function sortArrayOfObjects(arrayOfObjectsToSort, sortByKey) {
  // Guard: Return empty array if input is not a valid array
  if (!Array.isArray(arrayOfObjectsToSort)) {
    return []
  }
  let arrayOfObjects = arrayOfObjectsToSort
  // If Array has NONE property Shift before sort and unshift after sort
  let hasNoneProperty = false
  if (
    arrayOfObjects &&
    arrayOfObjects?.length > 0 &&
    (arrayOfObjects[0]?.Name === '-- None --' ||
      arrayOfObjects[0]?.id === '-- None --')
  ) {
    hasNoneProperty = true
    arrayOfObjects.shift()
  }
  const stringArr = [] // will have null, undefined and others
  const numericArr = [] // will have numeric data
  arrayOfObjects?.forEach((obj) => {
    // Separate arrayOfObjects data to numeric and string array
    if (
      obj[sortByKey] !== undefined &&
      obj[sortByKey] !== null &&
      isNaN(obj[sortByKey]?.toString().trim() ? obj[sortByKey] : '_') // since IsNaN('  ') returns  -> false, making it true with underscore
    )
      stringArr.push(obj)
    else numericArr.push(obj)
  })

  // string sort -> if same string -> small case will have higher priority
  stringArr.sort((a, b) => {
    const data =
      a[sortByKey] && a[sortByKey] !== null && a[sortByKey] !== undefined
        ? a[sortByKey]?.toString()
        : null
    const compareData =
      b[sortByKey] && b[sortByKey] !== null && b[sortByKey] !== undefined
        ? b[sortByKey]?.toString()
        : null

    if (data?.toLowerCase() === compareData?.toLowerCase()) {
      let returnData = 0
      for (
        let dataCharIndex = 0;
        dataCharIndex < data?.length;
        dataCharIndex++
      ) {
        for (
          let compareDataCharIndex = 0;
          compareDataCharIndex < compareData?.length;
          compareDataCharIndex++
        ) {
          if (
            data.charCodeAt(data[dataCharIndex]) <
            compareData.charCodeAt(compareData[compareDataCharIndex])
          ) {
            compareDataCharIndex = compareData?.length
            dataCharIndex = data?.length
            returnData = 1
          }
          if (
            data.charCodeAt(data[dataCharIndex]) >
            compareData.charCodeAt(compareData[compareDataCharIndex])
          ) {
            compareDataCharIndex = compareData?.length
            dataCharIndex = data?.length
            returnData = -1
          }
        }
      }
      return returnData
    }
    if (data?.toLowerCase() < compareData?.toLowerCase()) return -1
    if (data?.toLowerCase() > compareData?.toLowerCase()) return 1
  })

  // numeric sort
  numericArr.sort((a, b) => parseFloat(a[sortByKey]) - parseFloat(b[sortByKey]))
  Array.prototype.push.apply(numericArr, stringArr)
  arrayOfObjects = numericArr

  if (hasNoneProperty)
    arrayOfObjects.unshift({ Name: '-- None --', id: '-- None --' })
  return arrayOfObjects
}
// function to sort string and numeric values in array of objects based on a key -- end

/* This function was developed for alert show to user when they goback on edited stage */
/* istanbul ignore next */
const apiResponseCode = {
  successCode: 200,
  internalServerError: 500,
  badRequest: 400,
  notFound: 404,
  forbidden: 403,
  unauthorized: 401, //tokenExpired
  noContent: 204,
  zeroCode: 0,
  successResponse: 201,
  timeOutResponse: 504,
}
const apiDomainName = {
  userPermission: 'usePermission',
  privateLand: {
    name: 'privateLand',
    apiURLKey: 'PrivateLandAPIURL',
  },
}
const handleBeforeUnload = (e) => {
  e.preventDefault()
  const message =
    'Are you sure wish to go back? You will lose all entered information.'
  e.returnValue = message
  return message
}
/* istanbul ignore next */
const removeKeyFromObject = (
  obj,
  keysToRemove = [],
  removeKeysWithIncludes = []
) => {
  let objClone = _.cloneDeep(obj)
  if (!Array.isArray(obj)) objClone = [objClone]

  return objClone.map((objItem) => {
    keysToRemove.map((keyItem) => delete objItem[keyItem])
    if (removeKeysWithIncludes && removeKeysWithIncludes.length > 0) {
      const keyList = Object.keys(objItem)
      keyList.forEach((key) => {
        removeKeysWithIncludes.map((keyItem) => {
          const selectedKey = key.includes(keyItem)
          if (selectedKey && objItem[key]) delete objItem[key]
        })
      })
    }
    return objItem
  })
}

const convertArrOfObjectToArrOfString = (objArray, requiredKey) => {
  const ArrayToString = (value) => value[requiredKey]
  const stringArray = objArray?.map(ArrayToString)

  return stringArray || []
}
/* istanbul ignore next */
/* will inclue in lazyload implementation */
const confirmwindow = (conformText) => {
  if (
    window.confirm(
      conformText ||
        'Are you sure wish to go back? You will lose all entered information.'
    )
  ) {
    return true
  }
  return false
}
const utcDateFormat = (PickerType) => {
  if (PickerType === 'Date') {
    return 'yyyy-MM-dd'
  }
  return 'yyyy-MM-dd HH:mm:ss'
}
// This will contain roles for each menuItem. And this spread to menuList data
const menuAccessRoles = {
  // revealBI: ['SuperAdmin', 'OrganisationAdmin', 'ContractAdmin'],
  home: [
    'SuperAdmin',
    'OrganisationAdmin',
    'ContractAdmin',
    'BOManager',
    'QCBOUser',
    'BOUser',
    'SeniorBOUser',
    'NoticeProcessing',
    'Client',
    'ReadOnly',
    'CEOManager',
    'ClientReadOnly',
    'PoundOperator',
    'PoundManager',
    'SimpleAdmin',
    'AIRAManager',
    'SimpleBOUser',
    'CCTVReviewer',
    'PermitUser',
    'SummaryAgent',
    'SummaryManager',
    'TfLEnforcementViewer',
  ],

  selectedContract: [
    'SuperAdmin',
    'OrganisationAdmin',
    'ContractAdmin',
    'BOManager',
    'QCBOUser',
    'BOUser',
    'SeniorBOUser',
    'NoticeProcessing',
    'Client',
    'ReadOnly',
    'CEOManager',
    'ClientReadOnly',
    'SimpleAdmin',
    'PoundOperator',
    'PoundManager',
    'SimpleBOUser',
    'CCTVReviewer',
    'PermitUser',
    'TfLEnforcementViewer',
  ],
  globalSearch: [
    'SuperAdmin',
    'BOManager',
    'QCBOUser',
    'BOUser',
    'SeniorBOUser',
    'NoticeProcessing',
    'Client',
    'ReadOnly',
    'ClientReadOnly',
    'CEOManager',
    'PoundOperator',
    'PoundManager',
    'SimpleAdmin',
    'SimpleBOUser',
    'PermitUser',
    'TfLEnforcementViewer',
    'CCTVReviewer',
  ],
  paymentPlan: [
    'SuperAdmin',
    'BOManager',
    'QCBOUser',
    'BOUser',
    'SeniorBOUser',
    'Client',
    'NoticeProcessing',
    'CEOManager',
    'SimpleAdmin',
  ],
  reporting: [
    'SuperAdmin',
    'OrganisationAdmin',
    'ContractAdmin',
    'BOManager',
    'QCBOUser',
    'NoticeProcessing',
    'Client',
    'ReadOnly',
    'CEOManager',
    'PoundManager',
    'SimpleAdmin',
  ],
  myWorkFlow: [
    'SuperAdmin',
    'BOManager',
    'QCBOUser',
    'BOUser',
    'SeniorBOUser',
    'Client',
    'SimpleAdmin',
    'SummaryAgent',
    'SummaryManager',
  ],
  workflowManager: [
    'SuperAdmin',
    'BOManager',
    'QCBOUser',
    'SeniorBOUser',
    'NoticeProcessing',
    'SimpleAdmin',
  ],
  approvalQueue: ['SuperAdmin', 'BOManager', 'QCBOUser', 'SimpleAdmin'],
  noticeProcessing: [
    'SuperAdmin',
    'BOManager',
    'NoticeProcessing',
    'SimpleAdmin',
  ],
  abandonedVehicles: ['SuperAdmin', 'BOManager', 'CEOManager'],
  debtReview: ['Client'],
  configuration: [
    'SuperAdmin',
    'OrganisationAdmin',
    'ContractAdmin',
    'SimpleAdmin',
  ],
  clientSetup: ['SuperAdmin', 'OrganisationAdmin', 'SimpleAdmin'],
  hhdMessage: [
    'SuperAdmin',
    'BOManager',
    'QCBOUser',
    'BOUser',
    'SeniorBOUser',
    'NoticeProcessing',
    'Client',
    'CEOManager',
    'PoundOperator',
    'PoundManager',
    'SimpleAdmin',
  ],
  manualCase: [
    'SuperAdmin',
    'OrganisationAdmin',
    'ContractAdmin',
    'BOManager',
    'NoticeProcessing',
    'CEOManager',
    'SimpleAdmin',
  ],
  manualReviewPage: [
    'ContractAdmin',
    'OrganisationAdmin',
    'CCTVReviewer',
    'BOUser',
    'BOManager',
    'SuperAdmin',
  ],
  preCases: [
    'SuperAdmin',
    'SimpleAdmin',
    'BOManager',
    'QCBOUser',
    'BOUser',
    'SeniorBOUser',
    'NoticeProcessing',
  ],
  caseReservationForPreCases: [
    'SuperAdmin',
    'SimpleAdmin',
    'BOManager',
    'QCBOUser',
    'BOUser',
    'SeniorBOUser',
    'NoticeProcessing',
  ],
  representationReview: ['SuperAdmin', 'BOManager', 'Client', 'SimpleAdmin'],
  tracking: [
    'BOManager',
    'CEOManager',
    'SuperAdmin',
    'OrganisationAdmin',
    'ContractAdmin',
    'SimpleAdmin',
    'SeniorBOUser',
    'Client',
    'QCBOUser',
    'BOUser',
    'NoticeProcessing',
    'ReadOnly',
    'ClientReadOnly',
    'PoundOperator',
    'PoundManager',
    'AIRAManager',
    'SimpleBOUser',
    'CCTVReviewer',
    'PermitUser',
    'SummaryAgent',
    'SummaryManager',
    'TfLEnforcementViewer',
  ],
  crConfiguration: [
    'SimpleAdmin',
    'SuperAdmin',
    'OrganisationAdmin',
    'ContractAdmin',
  ],
  clampAndRemoval: [
    'BOManager',
    'CEOManager',
    'SuperAdmin',
    'SimpleAdmin',
    'QCBOUser',
    'BOUser',
    'SeniorBOUser',
    'NoticeProcessing',
    'Client',
    'ReadOnly',
    'PoundOperator',
    'PoundManager',
  ],
  precaseAllocation: ['SuperAdmin', 'BOManager', 'SimpleAdmin'],
  airaConfiguration: ['SuperAdmin', 'OrganisationAdmin', 'AIRAManager'],
  printAdmin: ['SuperAdmin'],
  caseLock: ['SuperAdmin'],
  cctvConfiguration: [
    'SuperAdmin',
    'OrganisationAdmin',
    'ContractAdmin',
    'SimpleAdmin',
  ],
  cctvReview: ['CCTVReviewer', 'BOUser', 'BOManager', 'SuperAdmin'],
  cctvReviewMenu: [
    'CCTVReviewer',
    'BOUser',
    'SuperAdmin',
    'BOManager',
    'ContractAdmin',
    'OrganisationAdmin',
  ],
  discardedCases: ['SuperAdmin', 'ContractAdmin', 'OrganisationAdmin'],
  cctvReview: [
    'CCTVReviewer',
    'BOUser',
    'SeniorBOUser',
    'BOManager',
    'SuperAdmin',
  ],
  enforcementMap: [
    'SuperAdmin',
    'BOManager',
    'CEOManager',
    'BOUser',
    'TfLEnforcementViewer',
  ],
}
const subMenuAccessRoles = {
  debtReview: [
    ...menuAccessRoles?.noticeProcessing,
    ...menuAccessRoles?.debtReview,
  ],
  discardedCases: [...menuAccessRoles?.discardedCases],
  monitoring: [
    'SuperAdmin',
    'OrganisationAdmin',
    'ContractAdmin',
    'BOManager',
    'QCBOUser',
    'NoticeProcessing',
    'ReadOnly',
    'CEOManager',
    'SimpleAdmin',
  ],

  geoFenceLog: [
    'SuperAdmin',
    'BOManager',
    'CEOManager',
    'SimpleAdmin',
    'TfLEnforcementViewer',
  ],
  briefing: ['SuperAdmin', 'CEOManager'],
  hotspot: [
    'BOManager',
    'SuperAdmin',
    'Client',
    'CEOManager',
    'TfLEnforcementViewer',
  ],
  trackingAccess: [
    'SuperAdmin',
    'OrganisationAdmin',
    'ContractAdmin',
    'SimpleAdmin',
  ],

  reportingCaseStatus: [...menuAccessRoles?.reporting, 'TfLEnforcementViewer'],
  reportingEnforcementTeam: [
    ...menuAccessRoles?.reporting,
    'TfLEnforcementViewer',
  ],
  faultsAccess: [...menuAccessRoles?.hhdMessage, 'TfLEnforcementViewer'],
}
const contractTypes = {
  pcnName: 'pcn',
  fpnName: 'fpn',
  airaName: 'aira',
  permits: 'apply',
  privateLand: 'privateland',
  summarisation: 'summarisation',
  scotlandPcnName: 'scotlandpcn',
  abandonedVehicle: 'abandonedvehicle',
  ecnName: 'ecn',
}
const menuList = [
  {
    menuindex: 0,
    menuItem: 'Home',
    menuItemId: 'Home',
    redirectURL: '/',
    imageURLactive: homeBlue,
    imageURLinactive: homeGrey,
    roles: [...menuAccessRoles?.home],
    contractRequired: false,
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.summarisation,
    ],
    isHavingSubmenu: false,
  },
  {
    menuenable: true,
    menuindex: 0,
    menuItem: `__SelectedContractName__`,
    menuItemId: 'wokinghampcn',
    redirectURL: '/caseoverview',
    imageURLactive: wokinghamBlue,
    imageURLinactive: wokinghamGrey,
    openCollapse: false,
    roles: [...menuAccessRoles?.selectedContract],
    contractRequired: true,
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.summarisation,
    ],
    isHavingSubmenu: false,
  },
  {
    menuenable: true,
    menuindex: 1,
    menuItem: `Search`,
    menuItemId: 'Search',
    redirectURL: '/search',
    imageURLactive: searchBlue,
    imageURLinactive: searchGrey,
    openCollapse: false,
    roles: [...menuAccessRoles?.globalSearch],
    contractRequired: true,
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.summarisation,
    ],
    isHavingSubmenu: false,
  },
  {
    menuenable: true,
    menuindex: 3,
    menuItem: `Payment Plan`,
    menuItemId: 'paymentPlan',
    redirectURL: '/paymentplan',
    imageURLactive: paymentPlanBlue,
    imageURLinactive: paymentPlanGrey,
    openCollapse: false,
    roles: [...menuAccessRoles?.paymentPlan],
    contractRequired: true,
    displayBasedOnFlagKey: 'isPaymentPlanEnabled',
    contractType: [contractTypes?.pcnName],
    isHavingSubmenu: false,
  },
  {
    menuenable: true,
    menuindex: 2,
    menuItem: `Reporting`,
    menuItemId: 'reporting',
    imageURLactive: reportsBlue,
    imageURLinactive: reportsGrey,
    openCollapse: false,
    //redirectURL: '/reporting',
    roles: [...menuAccessRoles?.reporting, 'TfLEnforcementViewer'],
    contractRequired: true,
    subMenuSpecified: true,
    contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
    isHavingSubmenu: true,
    MenuGroupitem: [
      {
        submenuenable: true,
        menuItem: 'Favourites',
        menuItemId: 'favourites',
        redirectURL: '/favourites',
        hasSubMenuCount: true,
        roles: [...menuAccessRoles?.reporting, 'TfLEnforcementViewer'],
        contractType: [contractTypes?.pcnName],
      },
      {
        submenuenable: true,
        menuItem: 'Case Status',
        menuItemId: 'casestatus',
        redirectURL: '/casestatus',
        roles: [...menuAccessRoles?.reporting, 'TfLEnforcementViewer'],
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
      {
        submenuenable: true,
        menuItem: 'Correspondence Out',
        menuItemId: 'outboundcorrespondence',
        redirectURL: '/outboundcorrespondence',
        roles: [...menuAccessRoles?.reporting],
        contractType: [contractTypes?.pcnName],
      },
      {
        submenuenable: true,
        menuItem: 'Correspondence In',
        menuItemId: 'inboundcorrespondence',
        redirectURL: '/inboundcorrespondence',
        roles: [...menuAccessRoles?.reporting],
        contractType: [contractTypes?.pcnName],
      },
      {
        submenuenable: true,
        menuItem: 'Enforcement Team',
        menuItemId: 'enforcementteam',
        redirectURL: '/enforcementteam',
        roles: [...menuAccessRoles?.reporting, 'TfLEnforcementViewer'],
        contractType: [contractTypes?.pcnName],
      },
      {
        submenuenable: true,
        menuItem: 'Financial',
        menuItemId: 'financial',
        redirectURL: '/financial',
        roles: [...menuAccessRoles?.reporting],
        contractType: [contractTypes?.pcnName],
      },
      {
        submenuenable: true,
        menuItem: 'Audit & Tracking',
        menuItemId: 'auditandtracking',
        redirectURL: '/auditandtracking',
        roles: [...menuAccessRoles?.reporting],
        contractType: [contractTypes?.pcnName],
      },
      {
        submenuenable: true,
        menuItem: 'Monitoring',
        menuItemId: 'monitoring',
        redirectURL: '/monitoring',
        roles: [...menuAccessRoles?.reporting],
        contractType: [contractTypes?.pcnName],
      },
      {
        submenuenable: true,
        menuItem: 'Performance Monitoring',
        menuItemId: 'performancemonitoring',
        redirectURL: '/performancemonitoring',
        roles: [...menuAccessRoles?.reporting],
        contractType: [contractTypes?.pcnName],
      },
    ],
    openCollapse: true,
  },
  // {
  //   menuenable: true,
  //   menuindex: 2,
  //   menuItem: `Reveal BI`,
  //   menuItemId: 'revealBI',
  //   imageURLactive: reportsBlue,
  //   imageURLinactive: reportsGrey,
  //   openCollapse: false,
  //   roles: [...menuAccessRoles?.revealBI],
  //   contractRequired: true,
  //   subMenuSpecified: true,
  //   contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
  //   isHavingSubmenu: true,
  //   MenuGroupitem: [
  //     {
  //       submenuenable: true,
  //       menuItem: 'CEO Tracking',
  //       menuItemId: 'revealCeoTracking',
  //       redirectURL: '/revealdashboard?dashboardId=ceotracking',
  //       roles: [...menuAccessRoles?.revealBI],
  //       contractType: [contractTypes?.pcnName],
  //     },
  //     {
  //       submenuenable: true,
  //       menuItem: 'CEO Heatmap',
  //       menuItemId: 'revealCeoHeatmap',
  //       redirectURL: '/revealdashboard?dashboardId=ceoheatmap',
  //       roles: [...menuAccessRoles?.revealBI],
  //       contractType: [contractTypes?.pcnName],
  //     },
  //     {
  //       submenuenable: true,
  //       menuItem: 'User Activity',
  //       menuItemId: 'revealUserActivity',
  //       redirectURL: '/revealdashboard?dashboardId=useractivity',
  //       roles: [...menuAccessRoles?.revealBI],
  //       contractType: [contractTypes?.pcnName],
  //     },
  //     {
  //       submenuenable: true,
  //       menuItem: 'Street Log',
  //       menuItemId: 'revealStreetLog',
  //       redirectURL: '/revealdashboard?dashboardId=onstreetlog',
  //       roles: [...menuAccessRoles?.revealBI],
  //       contractType: [contractTypes?.pcnName],
  //     },
  //     {
  //       submenuenable: true,
  //       menuItem: 'Create Dashboard',
  //       menuItemId: 'revealCreateDashboard',
  //       redirectURL: '/revealdashboard?new=true',
  //       roles: [...menuAccessRoles?.revealBI],
  //       contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
  //     },
  //   ],
  //   openCollapse: true,
  // },
  {
    menuenable: true,
    menuindex: 3,
    menuItem: `My Workflow`,
    menuItemId: 'myWorkflow',
    redirectURL: '/myworkflow',
    imageURLactive: myWorkflowBlue,
    imageURLinactive: myWorkflowGrey,
    openCollapse: false,
    roles: [...menuAccessRoles?.myWorkFlow],
    contractRequired: true,
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.summarisation,
      contractTypes?.summarization,
    ],
    isHavingSubmenu: false,
  },
  {
    menuenable: true,
    menuindex: 4,
    menuItem: `Rep.Review`,
    menuItemId: 'repReview',
    redirectURL: '/repReview',
    imageURLactive: myWorkflowBlue,
    imageURLinactive: myWorkflowGrey,
    openCollapse: false,
    roles: [...menuAccessRoles?.representationReview],
    contractRequired: true,
    displayBasedOnFlagKey: 'clientWorkFlow',
    contractType: [contractTypes?.pcnName],
    isHavingSubmenu: false,
  },
  {
    menuenable: true,
    menuindex: 5,
    menuItem: `Workflow Manager`,
    menuItemId: 'workflowmanager',
    redirectURL: '/workflowmanager',
    imageURLactive: workflowManagerBlue,
    imageURLinactive: workflowManagerGrey,
    openCollapse: false,
    roles: [...menuAccessRoles?.workflowManager],
    contractRequired: true,
    contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
    isHavingSubmenu: false,
  },
  {
    menuenable: true,
    menuindex: 6,
    menuItem: `Approval Queue`,
    menuItemId: 'approvalQueue',
    redirectURL: '/approvalQueueList',
    imageURLactive: approvalQueueBlue,
    imageURLinactive: approvalQueueGrey,
    openCollapse: false,
    roles: [...menuAccessRoles?.approvalQueue],
    contractRequired: true,
    contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
    isHavingSubmenu: false,
    displayBasedOnFlagKey: 'isCorrespondenceApprovalLimitEnabled',
  },
  {
    menuenable: true,
    menuindex: 7,
    menuItem: `Notice Processing`,
    menuItemId: 'noticeProcessing',
    //redirectURL: '/noticeprocessing',
    imageURLactive: noticeProcessingBlue,
    imageURLinactive: noticeProcessingGrey,
    openCollapse: false,
    roles: [
      ...menuAccessRoles?.noticeProcessing,
      ...menuAccessRoles.debtReview,
    ],
    contractRequired: true,
    subMenuSpecified: true,
    contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
    isHavingSubmenu: true,
    MenuGroupitem: [
      {
        submenuenable: true,
        menuItem: 'Debt Registration',
        menuItemId: 'debt',
        redirectURL: '/debt',
        roles: [...menuAccessRoles?.noticeProcessing],
        contractType: [contractTypes?.pcnName],
      },
      {
        submenuenable: true,
        menuItem: 'Warrant Request',
        menuItemId: 'warrant',
        redirectURL: '/warrant',
        roles: [...menuAccessRoles?.noticeProcessing],
        contractType: [contractTypes?.pcnName],
      },
      {
        submenuenable: true,
        menuItem: 'Enforcement Agency',
        menuItemId: 'bailiff',
        redirectURL: '/bailiffbatch',
        roles: [...menuAccessRoles?.noticeProcessing],
        contractType: [contractTypes?.pcnName],
      },
      {
        submenuenable: true,
        menuItem: 'EA Message',
        menuItemId: 'bailiffMessage',
        redirectURL: '/bailiffmessage',
        roles: [...menuAccessRoles?.noticeProcessing],
        contractType: [contractTypes?.pcnName],
      },
      {
        submenuenable: true,
        menuItem: 'Pre Debt',
        menuItemId: 'preDebt',
        redirectURL: '/predebt',
        roles: [...menuAccessRoles?.noticeProcessing],
        contractType: [contractTypes?.pcnName],
      },
      {
        submenuenable: true,
        menuItem: 'Debt Review',
        menuItemId: 'debtReview',
        redirectURL: '/debtreview',
        roles: [...subMenuAccessRoles?.debtReview],
        contractType: [contractTypes?.pcnName],
      },
      {
        submenuenable: true,
        menuItem: 'DVLA',
        menuItemId: 'dvla',
        redirectURL: '/dvla',
        roles: [...menuAccessRoles?.noticeProcessing],
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
      {
        submenuenable: true,
        menuItem: 'Print Batches',
        menuItemId: 'printBatches',
        redirectURL: '/printbatchlist',
        roles: [...menuAccessRoles?.noticeProcessing],
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
      {
        submenuenable: true,
        menuItem: 'Foreign Debt',
        menuItemId: 'foreignDebt',
        redirectURL: '/foreigndebt',
        displayBasedOnFlagKey: 'isInternationalDebtRecovery',
        roles: [...menuAccessRoles?.noticeProcessing],
        contractType: [contractTypes?.pcnName],
      },
      {
        submenuenable: true,
        menuItem: 'Scan Import',
        menuItemId: 'scanImport',
        redirectURL: '/scanimport',
        roles: [...menuAccessRoles?.noticeProcessing],
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
      // {
      //   submenuenable: true,
      //   menuItem: 'Awaiting Print',
      //   menuItemId: 'awaitingPrint',
      //   redirectURL: '/awaitingprint',
      // },
      // {
      //   submenuenable: true,
      //   menuItem: 'Financial',
      //   menuItemId: 'financial',
      //   redirectURL: '/financial',
      // },
      {
        submenuenable: true,
        menuItem: 'Bulk Update',
        menuItemId: 'bulkUpdate',
        redirectURL: '/bulkupdatelist',
        roles: [...menuAccessRoles?.noticeProcessing],
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
      {
        submenuenable: true,
        menuItem: 'Payments',
        menuItemId: 'partpayment',
        redirectURL: '/partpayment',
        displayBasedOnFlagKey: 'isPaymentMonitoringEnabled',
        roles: [...menuAccessRoles?.noticeProcessing],
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
      {
        submenuenable: true,
        menuItem: 'Debtor Review',
        menuItemId: 'debtorreview',
        redirectURL: '/debtorreview',
        displayBasedOnFlagKey: 'isDebtorReviewEnabled',
        roles: [...menuAccessRoles?.noticeProcessing],
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
      {
        submenuenable: true,
        menuItem: 'Warrant Review',
        menuItemId: 'warrantreview',
        redirectURL: '/warrantreview',
        roles: [...menuAccessRoles?.noticeProcessing],
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
    ],
    openCollapse: true,
  },
  {
    menuenable: true,
    menuindex: 7,
    menuItem: 'Configuration',
    menuItemId: 'Configuration',
    imageURLactive: configurationBlue,
    imageURLinactive: configurationGrey,
    roles: [...menuAccessRoles?.configuration],
    contractRequired: false,
    contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
    isHavingSubmenu: true,
    MenuGroupitem: [
      {
        submenuenable: true,
        menuItem: 'Users',
        menuItemId: 'Users',
        redirectURL: '/manageuser',
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
      {
        submenuenable: true,
        menuItem: 'Templates',
        menuItemId: 'template',
        redirectURL: '/templates',
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
      {
        submenuenable: true,
        menuItem: 'Zone',
        menuItemId: 'zone',
        redirectURL: '/zone',
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
      {
        submenuenable: true,
        menuItem: 'Location',
        menuItemId: 'Location',
        redirectURL: '/location',
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
      {
        submenuenable: true,
        menuItem: 'Controlled Parking Zone',
        menuItemId: 'cpzList',
        redirectURL: '/cpzlist',
        contractType: [contractTypes?.pcnName, contractTypes?.privateLand],
      },
      {
        submenuenable: true,
        menuItem: 'Vehicle Type',
        menuItemId: 'vehicleType',
        redirectURL: '/vehicletype',
        contractType: [contractTypes?.pcnName],
      },

      // {
      //   submenuenable: true,
      //   menuItem: 'Country',
      //   menuItemId: 'country',
      // },
      {
        submenuenable: true,
        menuItem: 'Contravention Codes',
        menuItemId: 'contraventioncodes',
        redirectURL: '/contraventions',
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
      // {
      //   submenuenable: true,
      //   menuItem: 'Vehicle Makes',
      //   menuItemId: 'vehiclemakes',
      // },
      // {
      //   submenuenable: true,
      //   menuItem: 'Vehicle Colours',
      //   menuItemId: 'vehiclecolours',
      // },
      {
        submenuenable: true,
        menuItem: 'FAQ',
        menuItemId: 'FAQ',
        redirectURL: '/faqList',
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
      {
        submenuenable: true,
        menuItem: 'Cancellation Reasons',
        menuItemId: 'cancellationReason',
        redirectURL: '/cancellationreason',
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
      {
        submenuenable: true,
        menuItem: 'CP Rep. Grounds',
        menuItemId: 'groundsList',
        redirectURL: '/groundslist',
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
      {
        submenuenable: true,
        menuItem: 'Hold',
        menuItemId: 'holdReasons',
        redirectURL: '/holdreasonslist',
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
      {
        submenuenable: true,
        menuItem: 'Geo Fencing Setup',
        menuItemId: 'geofencingsetuplist',
        redirectURL: '/geofencingsetuplist',
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
    ],
    openCollapse: true,
  },
  {
    menuenable: true,
    menuindex: 8,
    menuItem: 'Client Setup',
    menuItemId: 'clientsetup',
    imageURLactive: clientSetupBlue,
    imageURLinactive: clientSetupGrey,
    roles: [...menuAccessRoles?.clientSetup],
    contractRequired: false,
    contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
    isHavingSubmenu: true,
    MenuGroupitem: [
      {
        submenuenable: true,
        menuItem: 'Organisation',
        menuItemId: 'organisation',
        redirectURL: '/organisationlist',
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
      {
        submenuenable: true,
        menuItem: 'Contract',
        menuItemId: 'contract',
        redirectURL: '/contractListPage',
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
      {
        submenuenable: true,
        menuItem: 'Branding',
        menuItemId: 'branding',
        redirectURL: '/brandinglistpage',
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
      {
        submenuenable: true,
        menuItem: 'Prefix',
        menuItemId: 'prefix',
        redirectURL: '/prefix',
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
      {
        submenuenable: true,
        menuItem: 'Enforcement Agency',
        menuItemId: 'bailiff',
        redirectURL: '/bailiff',
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
      {
        submenuenable: true,
        menuItem: 'Purge',
        menuItemId: 'Purge',
        redirectURL: '/purge',
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
      {
        submenuenable: true,
        menuItem: 'Integration',
        menuItemId: 'integration',
        redirectURL: '/integration',
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
    ],
    openCollapse: true,
  },
  {
    menuenable: true,
    menuindex: 9,
    menuItem: `Messaging`,
    menuItemId: 'hhdMessage',
    //redirectURL: '/hhdmessage',
    imageURLactive: hhdMessageBlue,
    imageURLinactive: hhdMessageGrey,
    openCollapse: false,
    roles: [...menuAccessRoles?.hhdMessage, 'TfLEnforcementViewer'],
    contractRequired: true,
    contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
    isHavingSubmenu: true,
    subMenuSpecified: true,
    MenuGroupitem: [
      {
        submenuenable: true,
        menuItem: 'Backoffice',
        menuItemId: 'backOffice',
        redirectURL: '/backoffice',
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
        roles: [...menuAccessRoles?.hhdMessage],
      },
      {
        submenuenable: true,
        menuItem: 'On Street',
        menuItemId: 'onStreet',
        redirectURL: '/onstreet',
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
        roles: [...menuAccessRoles?.hhdMessage],
      },
      {
        submenuenable: true,
        menuItem: 'Tasks',
        menuItemId: 'tasks',
        redirectURL: '/tasks',
        displayBasedOnFlagKey: 'isCreateTask',
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
        roles: [...menuAccessRoles?.hhdMessage],
      },
      {
        submenuenable: true,
        menuItem: 'Briefing',
        menuItemId: 'briefing',
        redirectURL: '/briefinglist',
        displayBasedOnFlagKey: 'isBriefingEnabled',
        roles: [...subMenuAccessRoles?.briefing],
        contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
      },
      {
        submenuenable: true,
        menuItem: 'Faults',
        menuItemId: 'faults',
        redirectURL: '/faults',
        contractType: [contractTypes?.pcnName],
        roles: [...menuAccessRoles?.hhdMessage, 'TfLEnforcementViewer'],
      },
    ],
    openCollapse: true,
  },
  {
    menuenable: true,
    menuindex: 10,
    menuItem: 'Tracking',
    menuItemId: 'tracking',
    imageURLactive: clientSetupBlue,
    imageURLinactive: clientSetupGrey,
    openCollapse: true,
    roles: [
      ...menuAccessRoles?.tracking,
      ...subMenuAccessRoles?.hotspot,
      ...subMenuAccessRoles?.trackingAccess,
    ],
    contractRequired: true,
    contractType: [contractTypes?.pcnName],
    subMenuSpecified: true,
    isHavingSubmenu: true,
    isTrackingMenuEnabled: true,
    isTrackingSubMenuEnabled: true,

    MenuGroupitem: [
      {
        submenuenable: true,
        menuItem: 'Officer Tracking',
        menuItemId: 'officerTracking',
        redirectURL: '/officertracking',
        isTrackingMenuEnabled: true,
        contractType: [contractTypes?.pcnName],
        roles: [...menuAccessRoles?.tracking],
      },
      {
        submenuenable: true,
        menuItem: 'Geo Fence Log',
        menuItemId: 'geoFenceLog',
        redirectURL: '/geofencelog',
        isTrackingMenuEnabled: true,
        contractType: [contractTypes?.pcnName],
        roles: [...subMenuAccessRoles?.geoFenceLog],
      },
      {
        submenuenable: true,
        menuItem: 'Hotspot',
        menuItemId: 'hotspot',
        redirectURL: '/hotspot',
        isTrackingMenuEnabled: true,
        displayBasedOnFlagKey: 'isHotSpotEnabled',
        contractType: [contractTypes?.pcnName],
        roles: [...subMenuAccessRoles?.hotspot],
      },
      {
        submenuenable: true,
        menuItem: 'Tracking Access',
        menuItemId: 'trackingAccess',
        redirectURL: '/trackingaccess',
        displayBasedOnFlagKey: 'isTrackingSubMenuEnabled',
        contractType: [contractTypes?.pcnName],
        roles: [...subMenuAccessRoles?.trackingAccess],
      },
    ],
  },
  {
    menuenable: true,
    menuindex: 11,
    menuItem: `Manual Case`,
    menuItemId: 'manualCase',
    imageURLactive: manualCaseBlue,
    imageURLinactive: manualCaseGrey,
    openCollapse: false,
    roles: [...menuAccessRoles?.manualCase],
    contractRequired: true,
    isManualCaseEnabled: true,
    displayBasedOnFlagKey: 'isManualCaseEnabled',
    contractType: [contractTypes?.pcnName],
    isHavingSubmenu: true,
    MenuGroupitem: [
      {
        submenuenable: true,
        menuItem: 'Manual case creation',
        menuItemId: 'manualCaseCreation',
        redirectURL: '/manualcasecreation',
        contractType: [contractTypes?.pcnName],
      },
      {
        submenuenable: true,
        menuItem: 'Case Reservation',
        menuItemId: 'pcnNumberReservation',
        redirectURL: '/casenumberreservation',
        contractType: [contractTypes?.pcnName],
      },
    ],
    openCollapse: true,
  },
  {
    menuenable: true,
    menuindex: 11,
    menuItem: `Manual Case`,
    menuItemId: 'manualCase',
    imageURLactive: manualCaseBlue,
    imageURLinactive: manualCaseGrey,
    openCollapse: false,
    roles: [...menuAccessRoles?.caseReservationForPreCases],
    contractRequired: true,
    contractType: [contractTypes?.fpnName],
    isHavingSubmenu: true,
    MenuGroupitem: [
      {
        submenuenable: true,
        menuItem: 'Case Reservation',
        menuItemId: 'caseNumberReservation',
        redirectURL: '/casenumberreservation',
        contractType: [contractTypes?.fpnName],
      },
    ],
    openCollapse: true,
  },
  {
    menuenable: true,
    menuindex: 12,
    menuItem: `Clamp & Removal`,
    menuItemId: 'clampAndRemoval',
    imageURLactive: clampAndRemovalBlue,
    imageURLinactive: clampRemovalGrey,
    openCollapse: false,
    roles: [...menuAccessRoles?.clampAndRemoval],
    contractRequired: true,
    contractType: [contractTypes?.pcnName],
    isHavingSubmenu: true,
    isCandREnabled: true,
    displayBasedOnFlagKey: 'isCandREnabled',
    MenuGroupitem: [
      {
        submenuenable: true,
        menuItem: 'Jobs',
        menuItemId: 'jobs',
        redirectURL: '/jobs',
        contractType: [contractTypes?.pcnName],
      },
      {
        submenuenable: true,
        menuItem: 'Truck Mapping',
        menuItemId: 'truckMapping',
        redirectURL: '/truckmapping',
        contractType: [contractTypes?.pcnName],
      },
      {
        submenuenable: true,
        menuItem: 'Pounds',
        menuItemId: 'pounds',
        redirectURL: '/pounds',
        contractType: [contractTypes?.pcnName],
      },
      {
        submenuenable: true,
        menuItem: 'C&R Reporting',
        menuItemId: 'crReporting',
        redirectURL: '/crreporting',
        contractType: [contractTypes?.pcnName],
      },
    ],
    openCollapse: true,
  },
  {
    menuenable: true,
    menuindex: 13,
    menuItem: `CR Configuration`,
    menuItemId: 'crConfiguration',
    imageURLactive: CR_ConfigBlue,
    imageURLinactive: CR_ConfigGrey,
    openCollapse: false,
    roles: [...menuAccessRoles?.crConfiguration],
    contractRequired: false,
    contractType: [contractTypes?.pcnName],
    isHavingSubmenu: true,
    MenuGroupitem: [
      {
        submenuenable: true,
        menuItem: 'Pound Location',
        menuItemId: 'poundLocation',
        redirectURL: '/poundlocation',
        contractType: [contractTypes?.pcnName],
      },
      {
        submenuenable: true,
        menuItem: 'Cancellation Reasons',
        menuItemId: 'cancellationreason',
        redirectURL: '/cancellationreasonlist',
        contractType: [contractTypes?.pcnName],
      },
      {
        submenuenable: true,
        menuItem: 'Priority Settings',
        menuItemId: 'prioritysettings',
        redirectURL: '/prioritysettingslist',
        contractType: [contractTypes?.pcnName],
      },
      {
        submenuenable: true,
        menuItem: 'Truck',
        menuItemId: 'truck',
        redirectURL: '/trucklist',
        contractType: [contractTypes?.pcnName],
      },
      {
        submenuenable: true,
        menuItem: 'Charge',
        menuItemId: 'charge',
        redirectURL: '/chargelist',
        contractType: [contractTypes?.pcnName],
      },
      {
        submenuenable: true,
        menuItem: 'Release Reasons',
        menuItemId: 'releaseReason',
        redirectURL: '/releasereasonlist',
        contractType: [contractTypes?.pcnName],
      },
    ],
    openCollapse: true,
  },
  {
    menuenable: true,
    menuindex: 13,
    menuItem: `AIRA Configuration`,
    menuItemId: 'airaConfiguration',
    imageURLactive: AIRABlueIcon,
    imageURLinactive: AIRAGreyIcon,
    openCollapse: false,
    roles: [...menuAccessRoles?.airaConfiguration],
    contractRequired: false,
    contractType: [contractTypes?.pcnName],
    isHavingSubmenu: true,
    MenuGroupitem: [
      {
        submenuenable: true,
        menuItem: 'Reason Config',
        menuItemId: 'reasonconfiguration',
        redirectURL: '/reasonconfiguration',
        contractType: [contractTypes?.pcnName],
        projectType: 'AIRA',
      },
      {
        submenuenable: true,
        menuItem: 'Question Master List',
        menuItemId: 'questionMasterList',
        redirectURL: '/questionmasterlist',
        contractType: [contractTypes?.pcnName],
        projectType: 'AIRA',
      },
      {
        submenuenable: true,
        menuItem: 'PCN Type Creation',
        menuItemId: 'pcnTypeCreation',
        redirectURL: '/pcntypecreation',
        contractType: [contractTypes?.pcnName],
        projectType: 'AIRA',
      },
      {
        submenuenable: true,
        menuItem: 'Team Creation',
        menuItemId: 'teamCreation',
        redirectURL: '/teamcreation',
        contractType: [contractTypes?.pcnName],
        projectType: 'AIRA',
      },
      {
        submenuenable: true,
        contractType: [contractTypes?.pcnName],
        projectType: 'AIRA',
        menuindex: 14,
        menuItem: `Reports`,
        menuItemId: 'reports',
        redirectURL: '/reports',
        imageURLactive: AIRABlueIcon,
        imageURLinactive: AIRAGreyIcon,
        openCollapse: false,
        roles: [...menuAccessRoles?.airaConfiguration],
        contractRequired: false,
        contractType: [contractTypes?.pcnName],
        isHavingSubmenu: true,
        MenuGroupitem: [
          {
            submenuenable: true,
            menuItem: 'Question Master List',
            menuItemId: 'questionMasterList',
            redirectURL: '/questionmasterlist',
            contractType: [contractTypes?.pcnName],
            projectType: 'AIRA',
          },
        ],
      },
    ],
    openCollapse: true,
  },
  {
    menuenable: true,
    menuindex: 14,
    menuItem: `Pre-Cases`,
    menuItemId: 'preCases',
    imageURLactive: preCasesBlue,
    imageURLinactive: preCasesGrey,
    openCollapse: false,
    roles: [...menuAccessRoles?.preCases],
    contractRequired: true,
    contractType: [contractTypes?.fpnName],
    isHavingSubmenu: false,
    redirectURL: '/precases',
    openCollapse: true,
  },
  {
    menuenable: true,
    menuindex: 15,
    menuItem: `Print Admin`,
    menuItemId: 'printAdmin',
    imageURLactive: printAdminBlue,
    imageURLinactive: printAdminGrey,
    openCollapse: false,
    roles: [...menuAccessRoles?.printAdmin],
    contractRequired: false,
    contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
    isHavingSubmenu: false,
    redirectURL: '/printadmin',
    openCollapse: true,
  },
]

/* istanbul ignore next */
// getting the browser name
function browserIdentify() {
  var ua = navigator.userAgent
  var tem
  var browserName =
    ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) ||
    []
  if (/trident/i.test(browserName[1])) {
    tem = /\brv[ :]+(\d+)/g.exec(ua) || []
    return 'IE ' + (tem[1] || '')
  }
  if (browserName[1] === 'Chrome') {
    tem = ua.match(/\b(OPR|Edge)\/(\d+)/)
    if (tem !== null) return tem.slice(1).join(' ').replace('OPR', 'Opera')
  }
  browserName = browserName[2]
    ? [browserName[1], browserName[2]]
    : [navigator.appName, navigator.appVersion, '-?']
  if ((tem = ua.match(/version\/(\d+)/i)) !== null)
    browserName.splice(1, 1, tem[1])
  return browserName[0]
}

const allowedUserForAIRAContract = ['superadmin', 'airamanager', 'bouser']
const allowedUserForSummarisationContract = [
  'superadmin',
  'airamanager',
  'bouser',
  'superadmin',
  'bomanager',
  'qcbouser',
  'client',
  'simpleadmin',
  'summarymanager',
  'summaryagent',
]
const allowedUserForSummarizationOrg = ['summarymanager', 'summaryagent']
/* istanbul ignore next */
// This return role of selected contract. Due to logged in user's
const getRoleFromAssignedContractJSON = (selectedContract, contractJSON) => {
  let selectedContractBasedRole = {}
  let superAdminOrOrgAdmin = ''
  if (contractJSON) {
    selectedContractBasedRole = JSON.parse(contractJSON).find((item) => {
      //For SuperAdmin and OrgAdmin role, the JSON string will have one object with contractId-0(represents - AllOrg/AllContracts) with its roleName
      if (item?.Id?.toString() === '0') {
        superAdminOrOrgAdmin = item?.RoleName
      }
      return item?.Id === selectedContract?.contractId
    })
  }
  // priority given to specific contract's role then superAdmin/OrgAdmin
  return selectedContractBasedRole?.RoleName
    ? [selectedContractBasedRole?.RoleName]
    : superAdminOrOrgAdmin
    ? [superAdminOrOrgAdmin]
    : []
}
/* istanbul ignore next */
const checkUserRole = (contractId, contractJSON, roleName) => {
  let selectedContractBasedRole = {}
  if (contractJSON) {
    selectedContractBasedRole = JSON.parse(contractJSON).find((item) => {
      return item?.Id === contractId
    })
  }
  if (
    selectedContractBasedRole?.RoleName?.toString()
      ?.toLowerCase()
      ?.replaceAll(' ', '') === roleName?.toLowerCase()?.replaceAll(' ', '')
  )
    return true
  else return false
}

/* istanbul ignore next */
// function to return permission status with userRoleData(Array) and AllowedRoleData(Array)
const checkPermittedRoleOrNot = (
  selectedContract,
  currentRoleJsonArray,
  validRoleArray
) => {
  let selectedContractBasedRole = {}

  selectedContractBasedRole = JSON?.parse(currentRoleJsonArray)?.find(
    (item) => {
      //For SuperAdmin and OrgAdmin role, the JSON string will have one object with contractId-0(represents - AllOrg/AllContracts) with its roleName
      return (
        item?.Id?.toString() === '0' ||
        item?.Id === selectedContract?.contractId
      )
    }
  )
  if (
    validRoleArray?.includes(
      selectedContractBasedRole?.RoleName?.toString()
        ?.toLowerCase()
        ?.replaceAll(' ', '')
    )
  )
    return true
  else return false
}
const defaultDaysDataSource = Array.from({ length: 28 }, (_, i) => ({
  id: (i + 1).toString(),
  reason: (i + 1).toString(),
}))
const tptetaNoticeType = [
  { id: 1, name: 1 },
  { id: 2, name: 2 },
  { id: 3, name: 3 },
  { id: 4, name: 4 },
  { id: 5, name: 5 },
  { id: 6, name: 6 },
  { id: 7, name: 7 },
  { id: 8, name: 8 },
  { id: 9, name: 9 },
]

/* istanbul ignore next */
const commonDateTimeFormat = (PickerType) => {
  // const localeFormatStr = localStorage.getItem('localeDate')
  if (PickerType === 'Date') {
    const dateFormat = 'dd/MM/yyyy'
    // localeFormat?.localeDateFormat[localeFormatStr] ?? 'dd/MM/yyyy'
    return dateFormat
  }

  if (PickerType === 'Time24h') {
    const dateTimeFormat = 'HH:mm:ss'
    // localeFormat?.localeDateTimeFormat[localeFormatStr] ??
    // 'HH:mm'
    return dateTimeFormat
  }
  if (PickerType === 'Time24hwithoutsec') {
    return 'HH:mm'
  }

  if (PickerType === 'Time12h') {
    const dateTimeFormat = 'hh:mm:ss A'
    // localeFormat?.localeDateTimeFormat[localeFormatStr] ??
    // 'HH:mm'
    return dateTimeFormat
  }
  if (PickerType === 'DateAndTime') {
    const dateTimeFormat = 'dd/MM/yyyy HH:mm:ss'
    // localeFormat?.localeDateTimeFormat[localeFormatStr] ??
    // 'dd/MM/yyyy HH:mm:ss'
    return dateTimeFormat
  }
  if (PickerType === 'DateAndTimeForMoment') {
    const dateTimeFormat = 'YYYY-MM-DD HH:mm:ss'
    // localeFormat?.localeDateTimeFormat[localeFormatStr] ??
    // 'dd/MM/yyyy HH:mm:ss'
    return dateTimeFormat
  }
  if (PickerType === 'utcDate') {
    const dateTimeFormat = 'yyyy-MM-dd'
    // localeFormat?.localeDateTimeFormat[localeFormatStr] ??
    // 'dd/MM/yyyy HH:mm:ss'
    return dateTimeFormat
  }
  if (PickerType === 'monthText') {
    return 'MMMM'
  }
  if (PickerType === 'year') {
    return 'yyyy'
  }
  if (PickerType === 'downloadingFileNameFormat_Date') return 'ddMMyyyy' //'YYYYMMDD'
  if (PickerType === 'downloadingFileNameFormat_Time') return 'HHmmss'
  if (PickerType === 'downloadingFileNameFormat_Date_Time')
    return 'ddMMyyyy_HHmmss'
  // Default fallback to prevent undefined format strings
  return 'dd/MM/yyyy'
}

/* istanbul ignore next */
const commonDateTimeFormatMoment = (PickerType) => {
  if (PickerType === 'Date') {
    const dateFormat = 'DD/MM/YYYY'
    return dateFormat
  }
  if (PickerType === 'DateAndTime') {
    const dateTimeFormat = 'DD/MM/YYYY HH:mm:ss'
    return dateTimeFormat
  }
  if (PickerType === 'utcDate') {
    const dateTimeFormat = 'YYYY-MM-DD'
    return dateTimeFormat
  }
  if (PickerType === 'downloadingFileNameFormat_Date') return 'DDMMYYYY' //'YYYYMMDD'
  if (PickerType === 'downloadingFileNameFormat_Time') return 'HHmmss'
  if (PickerType === 'downloadingFileNameFormat_Date_Time')
    return 'DDMMYYYY_HHmmss'
}

/* istanbul ignore next */
// Separated dateFormat for baseComponent due to luxon library uses small case dateFormat and react-multi-date-picker uses upperCase as date-format
const commonDateTimeFormatForReactMultiDatePicker = (pickerType) => {
  if (pickerType === 'Date') {
    const dateFormat = 'DD/MM/YYYY'
    return dateFormat
  }

  if (pickerType === 'Time24h') {
    const dateTimeFormat = 'HH:mm:ss'
    return dateTimeFormat
  }
  if (pickerType === 'Time24hwithoutsec') {
    return 'HH:mm'
  }

  if (pickerType === 'Time12h') {
    const dateTimeFormat = 'hh:mm:ss A'
    return dateTimeFormat
  }
  if (pickerType === 'DateAndTime') {
    const dateTimeFormat = 'DD/MM/YYYY HH:mm:ss'
    return dateTimeFormat
  }
  if (pickerType === 'downloadingFileNameFormat_Date') return 'DDMMYYYY' //'YYYYMMDD'
  if (pickerType === 'downloadingFileNameFormat_Time') return 'HHmmss'
}
/* istanbul ignore next */
const commonDateTimePlaceHolder = (PickerPlaceHolder) => {
  // const localeFormatStr = localStorage.getItem('localeDate')
  if (PickerPlaceHolder === 'Date') {
    const datePlaceHolder = 'dd/mm/yyyy'
    // localeFormat?.localeDateFormat[localeFormatStr] ?? 'dd/MM/yyyy'
    return datePlaceHolder
  }

  if (PickerPlaceHolder === 'Time24h') {
    const TimePlaceHolder = 'hh:mm:ss'
    // localeFormat?.localeDateTimeFormat[localeFormatStr] ??
    // 'HH:mm'
    return TimePlaceHolder
  }

  if (PickerPlaceHolder === 'Time24hwithoutsec') {
    return 'hh:mm'
  }

  if (PickerPlaceHolder === 'Time12h') {
    const TimePlaceHolder = 'hh:mm:ss A'
    // localeFormat?.localeDateTimeFormat[localeFormatStr] ??
    // 'HH:mm'
    return TimePlaceHolder
  }
  if (PickerPlaceHolder === 'DateAndTime') {
    const dateTimePlaceHolder = 'dd/mm/yyyyThh:mm:ss'
    // localeFormat?.localeDateTimeFormat[localeFormatStr] ??
    // 'dd/MM/yyyy HH:mm:ss'
    return dateTimePlaceHolder
  }
}
/* istanbul ignore next */
const commonPostBodyDateTimeFormat = (pickerType) => {
  if (pickerType?.toString()?.toLowerCase() === 'date') return 'YYYY-MM-DD'
  if (pickerType?.toString()?.toLowerCase() === 'dateandtime')
    return 'YYYY-MM-DD HH:mm:ss'
}

/* const dateAndTimeConversion = (params, converttype = null) => {
  if (converttype) {
    return dayjs(params).format('DD/MM/YYYY')
  } else {
    if (params?.data?.createdOn) {
      if (params?.data?.createdOn?.length > 10) {
        return (
          dayjs(params?.data?.createdOn).format('DD/MM/YYYY') +
          ' ' +
          dayjs(params?.data?.createdOn).format('HH:mm')
        )
      } else if (params?.data?.createdOn?.length === 10) {
        return dayjs(params?.data?.createdOn).format('DD/MM/YYYY')
      }
    }
  }
} */
/* const dateAndTimeConversion = (params, converttype = null) => {
  if (converttype) {
    return dayjs(params).format('DD/MM/YYYY')
  } else {
    const field = params?.colDef?.field
    const data = params?.data?.hasOwnProperty(field)
      ? params?.data[field]
      : null
    if (data) {
      if (data?.length > 10) {
        return (
          dayjs(data).format('DD/MM/YYYY') + ' ' + dayjs(data).format('HH:mm')
        )
      } else if (data?.length === 10) {
        return dayjs(data).format('DD/MM/YYYY')
      }
    }
  }
} */
/* istanbul ignore next */
const dateAndTimeConversion = (params, converttype = null) => {
  if (converttype) {
    // here the params(date from ag-grid filter) date will be in this format 'Mon Aug 21 2023 00:00:00 GMT+0530'. Therefore converting it to date object to isoString which will be accepted in luxon library.
    const dateString = new Date(params)
    if (converttype === 'utcDate') {
      return DateTime?.fromISO(dateString?.toISOString())?.toFormat(
        commonDateTimeFormat('utcDate')
      )
    } else {
      const formatType = converttype === true ? 'Date' : converttype || 'Date'
      return DateTime?.fromISO(dateString?.toISOString())?.toFormat(
        commonDateTimeFormat(formatType)
      )
    }
  } else {
    const field = params?.colDef?.field
    const data = params?.data?.hasOwnProperty(field)
      ? params?.data[field]
      : null
    if (data) {
      if (data?.length > 10) {
        return (
          DateTime?.fromISO(data)?.toFormat(commonDateTimeFormat('Date')) +
          ' ' +
          DateTime?.fromISO(data)?.toFormat(
            commonDateTimeFormat('Time24hwithoutsec')
          )
        )
      } else if (data?.length === 10) {
        return DateTime?.fromISO(data)?.toFormat(commonDateTimeFormat('Date'))
      }
    }
  }
}
/* istanbul ignore next */
const convertSystemDateTimeToDate = (params) => {
  let dataValue = ''
  if (
    params?.data?.hasOwnProperty(params?.colDef?.field) &&
    params?.data[params?.colDef?.field]
  ) {
    dataValue = DateTime.fromISO(params?.data[params?.colDef?.field]).toFormat(
      commonDateTimeFormat('Date')
    )
  }
  return dataValue
}
/* istanbul ignore next */
const convertDateTimePickerDateTimeToCustomFormat = (value, customFormat) => {
  let dataValue = value
  if (dataValue) {
    if (customFormat === commonDateTimeFormat('Date'))
      dataValue = dataValue?.substring(0, 10)
    else if (customFormat === commonDateTimeFormat('Time24h'))
      dataValue = dataValue?.substring(0, 19)
  }
  return dataValue
}

const iconFontSize = '20px'
/* istanbul ignore next */
const convertUtcDateTimeToFormattedDateAgGrid = (params) => {
  let dataValue = ''
  if (
    params?.data?.hasOwnProperty(params?.colDef?.field) &&
    params?.data[params?.colDef?.field]
  ) {
    dataValue = DateTime.fromISO(params?.data[params?.colDef?.field]).toFormat(
      commonDateTimeFormat('Date')
    )
  }
  return dataValue
}
/* istanbul ignore next */
const convertUtcDateTimeToFormattedDateTimeAgGrid = (params) => {
  let dataValue = ''
  if (
    params?.data?.hasOwnProperty(params?.colDef?.field) &&
    params?.data[params?.colDef?.field]
  ) {
    dataValue = DateTime.fromISO(params?.data[params?.colDef?.field]).toFormat(
      commonDateTimeFormat('DateAndTime')
    )
  }
  return dataValue
}
/* istanbul ignore next */
const convertUtcDateTimeToFormattedDateTime = (dateTimeValue, formatType) => {
  let dataValue = ''
  if (dateTimeValue) {
    dataValue = DateTime.fromISO(
      getDateTimeValueInUtcFormat(dateTimeValue)
    ).toFormat(formatType)
  }
  return dataValue
}
/* istanbul ignore next */
const getDateTimeValueInUtcFormat = (dateTimeValue) => {
  return dateTimeValue?.length === 10
    ? dateTimeValue
    : `${dateTimeValue?.toString()?.substring(0, 19)?.replace(' ', 'T')}Z`
}
/* istanbul ignore next */
const returnCurrentDateTimeBasedOnTimezone = (ianaTimezone) => {
  return DateTime.now()
    .setZone(ianaTimezone)
    .toFormat(commonDateTimeFormat('DateAndTime'))
}

/* istanbul ignore next */
const returnCurrentDateTimeBasedOnTimezoneMoment = (ianaTimezone) => {
  return DateTime?.now()
    ?.setZone(ianaTimezone)
    ?.toFormat(commonDateTimeFormatMoment('Date'))
}
/* istanbul ignore next */
const returnCurrentDateTimeSpecificFormatBasedOnTimezone = (
  ianaTimezone,
  requiredFormat
) => {
  return DateTime.now().setZone(ianaTimezone).toFormat(requiredFormat)
}
/* istanbul ignore next */
const returnCurrentDateBasedOnTimezoneWithoutFormatting = (ianaTimezone) => {
  return DateTime.now().setZone(ianaTimezone)?.toISO()
}
/* istanbul ignore next */
const returnCurrentDateTimeInUTC = () => {
  return DateTime.utc().toISO()
}
/* istanbul ignore next */
const convertUtcDateTimeToTimezoneDateTime = (ianaTimezone, dateTimeValue) => {
  return DateTime.fromISO(getDateTimeValueInUtcFormat(dateTimeValue), {
    zone: 'utc',
  })
    .setZone(ianaTimezone)
    .toFormat(commonDateTimeFormat('DateAndTime'))
}
/* istanbul ignore next */
const convertUtcDateTimeToTimezoneDate = (ianaTimezone, dateTimeValue) => {
  return DateTime.fromISO(getDateTimeValueInUtcFormat(dateTimeValue), {
    zone: 'utc',
  })
    .setZone(ianaTimezone)
    .toFormat(commonDateTimeFormat('Date'))
}

/* istanbul ignore next */
const convertUtcDateTimeToTimezoneDateMoment = (
  ianaTimezone,
  dateTimeValue
) => {
  return DateTime?.fromISO(getDateTimeValueInUtcFormat(dateTimeValue), {
    zone: 'utc',
  })
    ?.setZone(ianaTimezone)
    ?.toFormat(commonDateTimeFormatMoment('Date'))
}

/* istanbul ignore next */
const convertUtcDateTimeToTimezoneTime = (ianaTimezone, dateTimeValue) => {
  return DateTime.fromISO(getDateTimeValueInUtcFormat(dateTimeValue), {
    zone: 'utc',
  })
    .setZone(ianaTimezone)
    .toFormat(commonDateTimeFormat('Time24h'))
}
/* istanbul ignore next */
const convertUtcDateTimeToTimezoneCustomFormat = (
  ianaTimezone,
  dateTimeValue,
  formatType
) => {
  return DateTime.fromISO(getDateTimeValueInUtcFormat(dateTimeValue), {
    zone: 'utc',
  })
    .setZone(ianaTimezone)
    .toFormat(formatType)
}
/* istanbul ignore next */
const convertTimezoneDateTimeToUtcDateTime = (ianaTimezone, dateTimeValue) => {
  return DateTime.fromISO(dateTimeValue?.replace(' ', 'T'), {
    zone: ianaTimezone,
  })
    .setZone('utc')
    .toISO()
}
const convertDateTimeWithAMPM = (isoString) => {
  const date = new Date(isoString)
  const pad = (n) => n?.toString()?.padStart(2, '0')

  const day = pad(date?.getDate())
  const month = pad(date?.getMonth() + 1)
  const year = date?.getFullYear()

  let hours = date?.getHours()
  const minutes = pad(date?.getMinutes())
  const ampm = hours >= 12 ? 'PM' : 'AM'

  hours = hours % 12 || 12
  hours = pad(hours)

  return `${day}/${month}/${year} ${hours}:${minutes}${ampm}`
}

/* istanbul ignore next */
const dateComparator = (valueA, valueB) => {
  const parseDate = (dateStr) => {
    if (!dateStr) return 0
    const datePart = dateStr.includes(' ') ? dateStr.split(' ')[0] : dateStr
    const parts = datePart.split('/')
    if (parts.length !== 3) return 0
    return new Date(parts[2], parts[1] - 1, parts[0]).getTime()
  }
  return parseDate(valueA) - parseDate(valueB)
}
/* istanbul ignore next */
const gridFilterOptions = {
  commonFilterParams: {
    filterOptions: [
      {
        displayKey: 'Equals',
        displayName: 'Equals',
        /* istanbul ignore next */
        predicate: ([fv1], cellValue) => cellValue === fv1,
        numberOfInputs: 1,
      },
    ],
    suppressAndOrCondition: true,
  },
  dateFilterParams: {
    filterOptions: [
      {
        displayKey: 'Equals',
        displayName: 'Equals',
        /* istanbul ignore next */
        predicate: ([fv1], cellValue) => {
          const cellDateOnly = cellValue?.includes(' ')
            ? cellValue?.split(' ')[0]
            : cellValue
          return cellDateOnly === dateAndTimeConversion(fv1, true)
        },
        numberOfInputs: 1,
      },
    ],
    suppressAndOrCondition: true,
  },
  dateComparator,
}
const permissionMessage = {
  Message: 'No access permission or no data found',
}
const dataSaveWarnTitle = 'Cancel Confirmation'
const dataCloseConfirmation = 'Close Confirmation'
const dataSaveWarnMessage = 'Your work is unsaved. Are you sure want to cancel?'
const cancellationReasonWarnMessage =
  'Unsaved data will be lost. Do you want to continue?'
const dataEditTitle = 'Save Confirmation'
const dataEditMessage = 'Are you sure want to make these changes?' // Are you sure to save the changes to this record (based on the story 41933 message title changed, in previous story not mentioned the edit confirmation message in story 40640 )
const deleteConfirmationTitle = 'Delete Confirmation'
const deleteConfirmation = 'Are you sure to delete this entry?'
const messageTitle = 'Message'
const deleteMessage =
  'Please delete the locations associated with this zone to perform this action.'
const rejectTitle = 'Please enter reason'
const infectedVirusMessage =
  'Virus Detected. Preview or download of the original file will be unavailable for safety reasons. Please contact your support team.'
const scanFailed = 'Virus scan failed. Please contact your support team.'
const scanOnProgress =
  'Security Alert: Scanning is incomplete. Preview or download of the original file will be unavailable for safety reasons. Please contact your support team.'
const generateNewRequest = 'Are you sure wish to generate new warrant batch?'
const generateNewRequestHeading = 'Generate New Request'
const batchCreatedSuccessMessage = 'Batch created and downloaded successfully!'
const batchCreatedErrorMessage = 'Error downloading the file :'
const debtRestrictionBannerTitle =
  'Some cases were excluded from this debt batch'
const debtRestrictionInvalidKeeperAddress = (count) =>
  `${count} case${
    count === 1 ? '' : 's'
  } skipped because the Keeper Address contains special characters.`
const debtRestrictionMissingChargeCertDate = (count) =>
  `${count} case${
    count === 1 ? '' : 's'
  } skipped because the Charge Certificate Service Date is missing.`
const generateNewRequestDebt =
  'Are you sure wish to generate new debt registration batch?'
const processMessage = 'Are you sure wish to process this Batch?'
const processCancel = 'Are you sure want to cancel the TEC Process?'
const discountApplied = 'Discount already applied'
const discountCannotApply = 'Discount can not be applied for this status'
const representationReview =
  'Are you sure want to complete the client review and mark this representation as '
const representationReviewMoreInfo =
  'Are you sure want to request more information for this representation'
const generateNewRequestBailiff =
  'Are you sure wish to generate a new enforcement agency export batch?'
const tokenExpiredMessage = 'Authorization Failed: Token Expired'
const constMessageObject = {
  userLogoNotAvailable: 'User Details Not Found',
  noDataFound: 'No Data Found',
}
const isAssociatedWithTask =
  'For the selected case there are other linked workflow items available, do you want to assign those item(s) as well?'
const reAssignUserMessage =
  'Are you sure wish to reassign the selected task(s)?'
const assignUSerMessage = 'Are you sure wish to assign the selected task(s)?'
const completeSuccessMessage = 'Task completed successfully!'
const completedTaskMessage =
  'Are you sure wish to complete the selected task(s)?'
const deleteTaskConfirmMessage = 'Are you sure to delete the workflow task?'
const workFlowAssignMessage = 'Task assigned successfully!'
const workFlowUnassignMessage = 'Task unassigned successfully!'
const workFlowReAssignMessage = 'Task reassigned successfully!'
const workFlowCompleteConfirmationMessage =
  'Would you like to mark all open workflow tasks associated with this case as completed?'
const workFlowCompleteSuccessMessage =
  'Associated workflow tasks marked as completed'
const inputSaveMessage = 'Your inputs have been saved'
const noteSavedSuccessMessage = 'Note saved successfully'
const cancellationWarningMessage =
  'Cancellation reason is already associated with a system process.'
const cancellationReasonDelete =
  'Cancellation reason were deleted successfully.'
const exportDataError = 'Error on Getting Export Data'
const errorOnGettingData = 'Error on getting Data'
const keeperConfirmation =
  'Please note that this case has no Keeper address set so will be excluded from any future print and export batches until one is set manually or new KADOE address added.'
const keeperUpdateConfirmation = 'Do you wish to amend the keeper address?'
const RegOffenderAddress = 'Do you wish to amend the Reg Offender Address?'

// Toast message for missing keeper address required fields
const keeperAddressRequiredToast =
  'Address does not have all required fields so cannot be set as Keeper'
const foreignAddressHintMessage =
  'Adding a foreign address will update this case to progress under the Non-UK (Foreign VRM) process.'
const foreignAddressConfirmText = 'Are you sure you want to add this address?'
const actionButton = {
  popupMessage: {
    challengeConfirm: 'Are you sure want to record the entry in NPS as ',
    challengeConfirmReverseCancellation:
      'Are you sure you wish to reverse the cancellation of this case and change the status to ',
    actionConfirm: 'Are you sure want to record the entry in NPS as ',
    challengeStageValidation: 'Unable to proceed. Please check current stage',
    stageNoChange: 'Stage will not Change',
    statusNoChange: 'Status will not Change',
  },
}
const exceedRows =
  '*You have to export search results as it contains more than 5000 rows'

/* istanbul ignore next */
const isValidEmailAddress = (value) => {
  /* istanbul ignore next */
  // const validMailRegEx = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/
  const validMailRegEx = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,3}$/
  /* istanbul ignore next */
  if (!validMailRegEx.test(value)) {
    return false
  }
  return true
}

/* istanbul ignore next */
const isValidCEOBadgeNumber = (ceoBadgeNumber) => {
  let ceoBadgeRegex = new RegExp(`^[a-zA-Z]{2}\\d{5}$`)
  if (!ceoBadgeRegex.test(ceoBadgeNumber)) {
    return false
  }
  return true
}

/* istanbul ignore next */
const isAlphaNumeric = (value, alphaNumericMust = true) => {
  if (!alphaNumericMust) {
    const alphaNumericWithSpace = /^[\w\-\s]+$/
    if (!value.match(alphaNumericWithSpace) || !isNaN(value)) {
      return false
    }
  } else {
    const alphaNumeric = /^(?=.*?\d)(?=.*?[a-zA-Z])[a-zA-Z\d]+$/
    if (!value.match(alphaNumeric)) {
      return false
    }
  }
  return true
}
const isAlphaOrNumeric = (value) => {
  const alphaOrNumericRegex = /^(?=.*[a-zA-Z\d])[a-zA-Z\d]+$/
  if (value?.match(alphaOrNumericRegex)) return true
  return false
}

/* istanbul ignore next */
const isValidPhoneNumber = (value) => {
  const validPhoneNumberRegEx = /[!_,`~|@#$%^<[{&*=;:}\]>'"?/a-zA-Z\s]/
  if (validPhoneNumberRegEx.test(value)) {
    return false
  }
  return true
}
/* istanbul ignore next */
const csvDownLoadClickHandler = (csvJsonData, fileName) => {
  // Creating a Blob for having a csv file format
  // and passing the data with type
  /* istanbul ignore next */
  const blob = new Blob([csvJsonData], { type: 'text/csv' })
  // Creating an object for downloading url
  /* istanbul ignore next */
  const url = window.URL.createObjectURL(blob)
  // const dataValue =
  //   dayjs(selectedData?.generatedDateTime).format('DD/MM/YYYY') +
  //   ' ' +
  //   dayjs(selectedData?.generatedDateTime).format('HH:mm:ss')
  // Creating an anchor(a) tag of HTML
  /* istanbul ignore next */
  const a = document.createElement('a')
  // Passing the blob downloading url
  /* istanbul ignore next */
  a.setAttribute('href', url)
  // Setting the anchor tag attribute for downloading
  // and passing the download file name
  /* istanbul ignore next */
  a.setAttribute('download', fileName)
  // Performing a download with click
  /* istanbul ignore next */
  a.click()
}
/* istanbul ignore next */
const setDecimalValue = (inputnumber, decimaldigit) => {
  return Number(inputnumber)?.toFixed(decimaldigit)
}
const userRoleCheck = [
  'OrganisationAdmin',
  'SuperAdmin',
  'ContractAdmin',
  'BOManager',
  'QCBOUser',
  'BOUser',
  'SeniorBOUser',
  'NoticeProcessing',
  'Client',
  'ReadOnly',
  'ClientReadOnly',
  'CEOManager',
  'PoundOperator',
  'PoundManager',
  'SimpleAdmin',
  'AIRAManager',
  'SimpleBOUser',
  'CCTVReviewer',
  'PermitUser',
  'SummaryManager',
  'SummaryAgent',
  'TfLEnforcementViewer',
]
const clampAndRemovalAccessRoles = [
  'PoundOperator',
  'PoundManager',
  'SimpleAdmin',
  'SuperAdmin',
  'BOManager',
]
const userRoleCheckAdmin = ['SuperAdmin']

const defaultHHDAutoLogOut = 120
const minHHDAutoLogOut = 60
const maxHHDAutoLogOut = 1440

const defaultVQ4RetryDays = 14
const minVQ4RetryDays = 4
const maxVQ4RetryDays = 21

const fileDownloaderByURL = (urlName, fileName) => {
  var link = document.createElement('a')
  link.download = fileName
  link.href = urlName
  document.body.appendChild(link)
  link.click()
  setTimeout(function () {
    document.body.removeChild(link)
  }, 50)
}
/* istanbul ignore next */
// used to download the blob file by creating new url with specific fileName instead of directly download from blob
const createObjectUrlAndDownload = async (blobUrl, fileName) => {
  let downloadStatus = 'error'
  let errorData = null
  await axios({
    url: blobUrl,
    method: 'GET',
    responseType: 'blob',
  })
    .then((response) => {
      /* istanbul ignore next */
      let createdBlobUrl = window.URL.createObjectURL(response?.data)
      /* istanbul ignore next */
      fileDownloaderByURL(createdBlobUrl, fileName)
      /* istanbul ignore next */
      downloadStatus = 'success'

      // Cleanup the temporary URL
      /* istanbul ignore next */
      URL.revokeObjectURL(createdBlobUrl)
    })
    .catch((error) => {
      errorData = error?.message
      downloadStatus = 'error'
    })
  return { downloadStatus: downloadStatus, errorData: errorData }
}
const isValidURL = (value) => {
  // Commenting this due to code quality analysis issue
  // const validRegEx = new RegExp(
  //   '^([a-zA-Z]+:\\/\\/)?' + // protocol
  //     '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
  //     '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR IP (v4) address
  //     '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
  //     '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
  //     '(\\#[-a-z\\d_]*)?$', // fragment locator
  //   'i'
  // )
  // // const validRegEx = new RegExp(
  // //   '(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?'
  // // )
  // if (!validRegEx.test(value)) {
  //   return false
  // }
  // return true

  try {
    new URL(value)
    return true
  } catch {
    return false
  }
}

const stageTextMap = {
  appeal: { displayText: 'Appeal', key: 'Appeal' },
  chargeCertificate: {
    displayText: 'Charge Certificate',
    key: 'ChargeCertificate',
  },
  confirmationNote: {
    displayText: 'Confirmation Note',
    key: 'ConfirmationNote',
  },
  highCourt: { displayText: 'High Court', key: 'HighCourt' },
  informal: { displayText: 'Informal', key: 'Informal' },
  informalNoa: { displayText: 'NOA Informal', key: 'NOAInformal' },
  informalNor: { displayText: 'NOR Informal', key: 'NORInformal' },
  intimereceived: { displayText: 'In Time', key: 'InTime' },
  letter_14Day: { displayText: '14 Day Letter', key: '14DayLetter' },
  letter_28Day: { displayText: '28 Day Letter', key: '28DayLetter' },
  nto: { displayText: 'NTO', key: 'NTO' },
  ofr: { displayText: 'OFR', key: 'OFR' },
  ofrExpired: { displayText: 'OFR Expired', key: 'OfrExpired' },
  ofrRepresentation: {
    displayText: 'OFR Representation',
    key: 'OFRRepresentation',
  },
  outimereceived: { displayText: 'Out of Time', key: 'OutofTime' },
  postedPcn: { displayText: 'Posted PCN', key: 'PostedPCN' },
  representation: { displayText: 'Representation', key: 'Representation' },
  representation_cc: {
    displayText: 'CC Representation',
    key: 'CCRepresentation',
  },
  representationNoa: {
    displayText: 'NOA Representation',
    key: 'NOARepresentation',
  },
  representationNor: {
    displayText: 'NOR Representation',
    key: 'NORRepresentation',
  },
  revokeOrder: { displayText: 'Revoke Order', key: 'RevokeOrder' },
  tribunal: { displayText: 'Tribunal', key: 'Tribunal' },
  tribunalAdjudicator: {
    displayText: 'Tribunal Adjudicator',
    key: 'TribunalAdjudicator',
  },
  tribunalReview: { displayText: 'Tribunal Review', key: 'TribunalReview' },
  warrant: { displayText: 'Warrant', key: 'Warrant' },
  warrantExported: { displayText: 'Warrant Exported', key: 'WarrantExported' },
  warrantReceived: { displayText: 'Warrant Received', key: 'WarrantReceived' },
  warrantRequested: {
    displayText: 'Warrant Requested',
    key: 'WarrantRequested',
  },
  noaWarningNotice: {
    displayText: 'NOA Warning Notice',
    key: 'NOAWarningNotice',
  },
  ofrStage: { displayText: 'OFR', key: 'ofr' },
  liveStage: { displayText: 'Live', key: 'Live' },
  debtStage: { displayText: 'Debt', key: 'Debt' },
  debtRegistration: {
    displayText: 'Debt Registration',
    key: 'DebtRegistration',
  },
  paidCase: {
    newStage: {
      displayText: 'No Change',
      key: 'NoChange',
    },
  },
  atCourt: {
    displayText: 'At Court',
    key: 'atCourt',
  },
  finalWarning: {
    displayText: 'Final Warning',
    key: 'finalWarning',
  },
  finalRemainder: {
    displayText: 'Final Remainder',
    key: 'FinalRemainder',
  },
  courtEligible: {
    displayText: 'Court Eligible',
    key: 'courtEligible',
  },
  tolRepresentationSuccessful: {
    displayText: 'TOL Representation',
    key: 'TOLRepresentation',
  },
  reverseCancellation: {
    newStage: {
      displayText: 'No Change',
      key: 'NoChange',
    },
    newStatus: {
      displayText: 'On hold',
      key: 'Onhold',
    },
  },
  discountRepReject21Days: {
    displayText: 'NOR Representation',
    key: 'NORRepresentation',
  },
  nip: {
    displayText: 'NIP',
    key: 'NIP',
  },
  nipFinalWarning: {
    displayText: 'NIP Final Warning',
    key: 'NIPFinalWarning',
  },
  warningPcn: {
    displayText: 'Warning PCN',
    key: 'WarningPCN',
  },
  noticeStage: {
    displayText: 'Notice',
    key: 'Notice',
  },
  atBailiffStage: {
    displayText: 'At Bailiff',
    key: 'AtBailiff',
  },
  notice: {
    displayText: 'Notice',
    key: 'Notice',
  },
  abandonedVehicle: {
    displayText: 'Abandoned Vehicle',
    key: 'AbandonedVehicle',
  },
  standardCharge: {
    displayText: 'Standard Charge',
    key: 'StandardCharge',
  },
}

const statusTextMap = {
  exported: { displayText: 'Exported', key: 'Exported' },
  accepted: { displayText: 'Accepted', key: 'Accepted' },
  actioned: { displayText: 'Actioned', key: 'Actioned' },
  reverted: { displayText: 'Reverted', key: 'Reverted' },
  prepared: { displayText: 'Prepared', key: 'Prepared' },
  contested: { displayText: 'Contested', key: 'Contested' },
  active: { displayText: 'Active', key: 'Active' },
  removed: { displayText: 'Removed', key: 'Removed' },
  cancelled: { displayText: 'Cancelled', key: 'Cancelled' },
  onHold: { displayText: 'On hold', key: 'Onhold' },
  submitted: { displayText: 'Submitted', key: 'Submitted' },
  lost: { displayText: 'Lost', key: 'Lost' },
  rejected: { displayText: 'Rejected', key: 'Rejected' },
  successful: { displayText: 'Successful', key: 'Successful' },
  unsuccessful: { displayText: 'Unsuccessful', key: 'Unsuccessful' },
  paid: { displayText: 'Paid', key: 'Paid' },
  infoRequested: { displayText: 'Info Requested', key: 'InfoRequested' },
  clientReviewed: { displayText: 'Client Reviewed', key: 'ClientReviewed' },
  discountStatus: { displayText: 'Discount', key: 'discount' },
  paidCase: {
    newStatus: {
      displayText: 'Paid',
      key: 'Paid',
    },
  },
  requested: { displayText: 'Requested', key: 'Requested' },
  reverseCancellation: {
    newStatus: {
      displayText: 'On Hold',
      key: 'OnHold',
    },
  },
  manualFullCharge: {
    displayText: 'Manual Full Charge',
    key: 'ManualFullCharge',
  },
  // The discount21Days value here does not match the corresponding value in the database.
  discountRepReject21Days: {
    displayText: 'Discount 21',
    key: 'Discount21',
  },
  ManualDiscount: {
    displayText: 'Manual Discount',
    key: 'ManualDiscount',
  },
  discountReOffered: {
    displayText: 'Discount Re-Offered',
    key: 'DiscountReOffered',
  },
  // The Discount21Days value here is the correct one
  Discount21Days: {
    displayText: 'Discount 21 Days',
    key: 'Discount21Days',
  },
  warningSent: { displayText: 'Warning Sent', key: 'WarningSent' },
  issue: { displayText: 'Issue', key: 'Issue' },
  tol: { displayText: 'TOL', key: 'TOL' },
  paymentPlan: { displayText: 'Payment Plan', key: 'PaymentPlan' },
}
const onHoldOffHoldPCN = {
  offHoldPCNMessage:
    'Are you sure wish to take off hold and revert to previous status?',
  indefinite: 'Indefinite',
  timeLimited: 'Time Limited',
  holdPcn: 'Hold PCN',
  expiryDateTimeErrorMessage: 'Please select a valid expiry date and time',
}
const sourceTextMap = {
  ocrError: { displayText: 'OCRError', key: 'ocrerror' },
  emailError: { displayText: 'EmailError', key: 'emailerror' },
}
const challengeAction = {
  reCloseFollowingPaidCorrespondenceTitle:
    'Re-Close following Paid Correspondence',
}
const applicationInactiveTimeLapse = 10800000 //3 hours
const refreshTokenMinutesEarlier = 10 //5 mins
const toastAutoHideDuration = 30000

const messageDisplayItems = [
  {
    id: 1,
    name: 'All',
  },
  {
    id: 2,
    name: 'Current',
  },
  {
    id: 3,
    name: 'Future',
  },
  {
    id: 4,
    name: 'Expired',
  },
]

const bailiffMessages = {
  errorSplitMessage:
    'Split of job among the Enforcement Agency does not cover 100%. Please recheck',
  errorShouldNotExceed: 'Enforcement Agency Job Split should total to 100%',
  deleteWarningMessage: 'Are you sure want to remove this entry?',
  errorMaxRecord:
    'To add maximum of 10 Partner ID and Enforcement Agency entries per contract.',
  errorUnique: 'Enforcement Agency must be unique.',
  saveSuccessMessage: 'Enforcement Agency record has been added',
  updateSuccessMessage: 'Enforcement Agency record has been updated',
  deleteMessage: 'Enforcement Agency Record has been deleted.',
  dataEditMessage: 'Are you sure wish to update this record?',

  saveEmptyValidation: 'Please complete data input before saving this entry',
  updateEmptyValidation:
    'Please complete data input before updating this entry',
}

const reallocationAttachment = {
  title: 'Reallocate Attachment',
  popupType: {
    reallocation: 'reallocate',
    reallocateCancel: 'reallocateCancel',
  },
  reallocationAttachmentConfirmation:
    'Are you sure wish to reassign the attachment(s)?',
  reacllocationAttachmentSuccessMessage:
    'Attachments reallocated successfully!',
  reallocationAttachmentCancel: 'Are you sure to cancel this request?',
}
// const toastSuccessStatus = 'success'

const repReviewActionStatus = {
  accept: 'Representation marked as accepted',
  reject: 'Representation marked as rejected',
  moreInfo: 'More info requested for',
  failedStatus: 'Representation review not updated',

  acceptKey: 'accepted',
  rejectKey: 'rejected',
  moreInfoKey: 'moreinfo',
  acceptDisplayName: 'Accepted',
  rejectDisplayName: 'Rejected',
  moreInfoDisplayName: 'MoreInfo',
}
const templateData = {
  initialText: 'Design',
  headerText: 'Header',
  footerText: 'Footer',
  hhdNoticeTemplate: {
    tabName: 'hhdNoticeTemplate',
    designTypeText: 'PCN',
    tabTitle: 'HHD Notice',
    templateType: ['pcn', 'warningpcn'],
    templateTypeForCR: ['pcn', 'warningpcn', 'c&rletter', 'vr11'],
    fpnNoticeTemplateType: ['fpnnotice'],
    fpnDesignTemplate: ['statdoc', 'e-mail', 'letter'],
    activationWarningText:
      'a contract cannot have more than 1 active pcn template',
    possibleTemplateTypeMessage: 'PCN / Warning PCN',
    successInsertMessage: 'HHD Notice Template added successfully',
    successUpdateMessage: 'HHD Notice Template updated successfully',
  },
  pcnTemplateActivationWarning:
    'Are you sure wish to __activation__ the __templateType__ template?',
  templateNameLength: 100,
  fpnNotificationTemplate: [
    'alertemail',
    'casesyncfailure',
    'correspondenceapprove',
    'ctportalchallenge',
    'malwarescanvirusinfected',
    'newusercreation',
    'pcnprefixalert',
    'printfailure',
    'printnoresponse',
  ],
}
const contractData = {
  message: {
    invalidEmail: 'Please enter valid email address.',
  },
}
/* istanbul ignore next */
const integrationServices = {
  fieldName: {
    isActiveDaysEnabled: 'isActiveDaysEnabled',
    isKadoeEnabled: 'isKadoeEnabled',
    isPrintEnabled: 'isPrintEnabled',
    isScanEnabled: 'isScanEnabled',
    isCctvEnabled: 'isCctvEnabled',
    isBailiffEnabled: 'isBailiffEnabled',
    isVRMNationAlertEnabled: 'isVRMNationAlertEnabled',
    isTarantoEnabled: 'isTarantoEnabled',
    isMetrosEnabled: 'isMetrosEnabled',
    isDTESEnabled: 'isDTESEnabled',
  },
  title: {
    isActiveDaysEnabled: 'Active days',
    isKadoeEnabled: 'KADOE',
    isPrintEnabled: 'Print',
    isScanEnabled: 'Scan',
    isCctvEnabled: 'CCTV',
    isBailiffEnabled: 'Enforcement Agency',
    isVRMNationAlertEnabled: 'VRM National Alert',
    isTarantoEnabled: 'Taranto',
    isMetrosEnabled: 'METROS',
    isDTESEnabled: 'DTES',
  },
  description: {
    isActiveDaysEnabled:
      'Disabling this active days will disable the entire progression of cases.',
    isKadoeEnabled:
      'Disabling this service will stop VQ4 generation and VQ5 import.',
    isPrintEnabled:
      'Disabling this service will not generate any scheduled print batches.',
    isScanEnabled:
      'Disabling this service will not import files from Scan and Parseq.',
    isCctvEnabled:
      'Disabling this service will not import case files form 3rd party CCTV.',
    isBailiffEnabled:
      'Disabling this service will not generate Enforcement Agency XML file and will not import EA message file.',
    isVRMNationAlertEnabled:
      'Disabling this toggle will not provide any case details to Optimise as per MNPS standards.',
    isTarantoEnabled:
      'Enabling this toggle allows case and enforcement data to be exchanged with Taranto for processing. Disabling this toggle will prevent any data exchange with Taranto as per MNPS standards.',
    isMetrosEnabled:
      'Enabling this toggle allows the system to use GIS and enforcement zone data from METROS for location and restriction mapping. Disabling this toggle will stop all METROS data ingestion and related GIS functionality.',
    isDTESEnabled:
      'Enabling this toggle allows enforcement data to be shared with DTES for duplicate checks and processing. Disabling this toggle will prevent any data transfer to DTES as per enforcement integration rules.',
  },
  confirmMessage: {
    isActiveDaysEnabled:
      'Disabling active days will disable all other services',
    /* istanbul ignore next */
    commonService: (toggleValue, serviceName) =>
      `Are you sure want to ${
        toggleValue ? 'enable' : 'disable'
      } the ${serviceName} ?`,
  },
  successMessage: 'Integration Entry updated successfully',
}

const brandingMenu = {
  configuration: {
    field: 'configuration',
    recType: 'configuration',
    title: 'Configuration',
    stage: 0,
  }, //if API following different field name - need to add that too in this object - apiField : 'Configuration'
  attachments: {
    field: 'attachments',
    recType: 'attachments',
    title: 'Attachments',
    stage: 1,
  },
  headerSetting: {
    field: 'headerSetting',
    recType: 'header',
    title: 'Header Settings',
    stage: 1,
  },
  footerSetting: {
    field: 'footerSetting',
    recType: 'footer',
    title: 'Footer Settings',
    stage: 2,
  },
  headerCode: {
    field: 'headerSetting',
    recType: 'header',
    title: 'Header Code',
    stage: 2,
  },
  footerCode: {
    field: 'footerSetting',
    recType: 'footer',
    title: 'Footer Code',
    stage: 3,
  },
  launchSummaryBasic: {
    field: 'launchSummaryBasic',
    recType: 'launchSummaryBasic',
    title: 'Launch Summary',
    stage: 3,
  },
  launchSummaryAdvanced: {
    field: 'launchSummaryAdvance',
    recType: 'launchSummaryAdvance',
    title: 'Launch Summary',
    stage: 4,
  },
}

const persistantEvaderLabel = {
  labelName: 'Persistent Evader',
}

const branding = {
  brandingTypeTitle: {
    basic: { id: 'basic', displayText: 'Basic' },
    advanced: { id: 'advanced', displayText: 'Advanced' },
  },
  brandingSetupMenuDataSource: {
    basic: [
      { ...brandingMenu?.configuration },
      { ...brandingMenu?.headerSetting },
      { ...brandingMenu?.footerSetting },
      { ...brandingMenu?.launchSummaryBasic },
    ],
    advanced: [
      { ...brandingMenu?.configuration },
      { ...brandingMenu?.attachments },
      { ...brandingMenu?.headerCode },
      { ...brandingMenu?.footerCode },
      { ...brandingMenu?.launchSummaryAdvanced },
    ],
  },
  backgroundOptions: [
    {
      name: 'Image',
      value: 'image',
    },
    {
      name: 'Colour',
      value: 'colour',
    },
  ],
  buttonMenuDataSource: {
    button: {
      id: 'newBranding',
      fieldValue: 'New Branding',
      fieldLabel: 'New Branding',
    },
    menu: { id: 'optionsMenu', dataTestId: 'brandingOptions' },
    menuItems: [{ label: 'Basic' }, { label: 'Advanced' }],
  },
  success: {
    title: 'Success',
    message: 'Branding published successfully',
  },
  error: {
    fileTooBig: 'Error! File size too big, maximum allowed is 1mb',
    maxFilesReached:
      'Max files allowed is 10, please review before clicking submit',
    invalidFormat:
      'Error! File type is not permitted, please attach a document of the following type(s) only .png, .jpg, .svg',
  },
  attachmentCopied: 'Copied to clipboard',
  limit: { attachmentCount: 10, attachmentSize: 1, airaAttachmentSize: 10 }, // for aira attachment size UI change
  makeDefault: 'Select a default URL',
  validDomainURL: 'Please enter valid URL',
  duplicateDomainURL: 'Duplicate domain URL is not allowed',
  placeholder: 'Enter URL',
  tabTitle: 'Maximum 100 characters only allowed',
  specialCharacters: 'Special characters are not allowed',
  favIcon: {
    fileSizeError:
      'File size exceeds the 20KB limit. Please upload a smaller file',
    dimensionError: 'Favicon dimensions must be 48x48 pixels',
    formatError: 'Only .ico format is allowed',
    fileAllowance: 'Maximum 1 file is allowed',
  },
}

const localPrintingMessage = {
  noFilesToPrint: 'No file to print',
  noFilesToDownload: 'No file to download',
  printSuccess: 'print batch sent to printer successfully',
  downloadSuccess: 'print batch downloaded successfully',
  statusPartConfirmed: 'Part Confirmed',
  statusUpdateError: 'Status update failed.',
  localPrintingStatus: {
    new: 'New',
    sent: 'Sent',
    confirm: 'Confirmed',
  },
}

const invalidGPS = 'Invalid GPS Values'
const toastStatus = {
  error: 'error',
  success: 'success',
  warning: 'warning',
  alert: 'alert',
}
const confirmWindowMessage =
  'Please save your data before switching to another case.'
const allowedFileLimit = 'Max allowed is 10 files, please review'
const newGenerateRequestMessage = 'New Request Generated'
const newGenerateRequestMessageFalse = 'New Request Not Generated'
const domainSettings = 'Domain Settings'
const noRecordsFound = 'No Records Found'
const configurationStep1 = 'Step 1 - Configuration'
const txtbutton = 'Button'
const brandingContractHeading = 'Contract Settings'

/**
 * Function to get blob detail
 * @param {*} blobName - blobName with extension
 * @param {*} requiredDetail - name or extension
 * @returns {string} - name / extension based on requiredDetail
 */
/* istanbul ignore next */
const getBlobDetail = (blobName, requiredDetail) => {
  const extensionIndex = blobName?.toString()?.lastIndexOf('.')
  if (requiredDetail === 'name')
    return blobName?.toString()?.slice(0, extensionIndex)
  else if (requiredDetail === 'extension')
    return blobName?.toString()?.slice(extensionIndex)
  return ''
}
// const escapeRegExp = (str) => {
//   return str.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, '\\$1')
// }
// const replaceAll = (str, find, replace) => {
//   return str.replace(new RegExp(escapeRegExp(find), 'g'), replace)
// }
// const convertSvgTextToCssString = (text) => {
//   var encoded = text?.replace(/\s+/g, ' ')
//   encoded = replaceAll(encoded, '%', '%25')
//   encoded = replaceAll(encoded, '> <', '><') // normalise spaces elements
//   encoded = replaceAll(encoded, '; }', ';}') // normalise spaces css
//   encoded = replaceAll(encoded, '<', '%3c')
//   encoded = replaceAll(encoded, '>', '%3e')
//   encoded = replaceAll(encoded, '"', "'")
//   encoded = replaceAll(encoded, '#', '%23') // needed for ie and firefox
//   encoded = replaceAll(encoded, '{', '%7b')
//   encoded = replaceAll(encoded, '}', '%7d')
//   encoded = replaceAll(encoded, '|', '%7c')
//   encoded = replaceAll(encoded, '^', '%5e')
//   encoded = replaceAll(encoded, '`', '%60')
//   encoded = replaceAll(encoded, '@', '%40')
//   var uri = 'url("data:image/svg+xml;charset=UTF-8,' + encoded + '")'
//   return uri
// }

/**
 * Converts SVG text to a CSS string.
 * @param {string} text - The SVG text to be converted.
 * @returns {string} - The converted CSS string.
 */
const convertSvgTextToCssString = (text) => {
  // Normalize whitespace and escape special characters
  let encoded = text
    ?.replace(/\s+/g, ' ')
    .replace(/%/g, '%25')
    .replace(/> <|; }|</g, (match) => {
      switch (match) {
        case '> <':
          return '><'
        case '; }':
          return ';}'
        case '<':
          return '%3c'
        default:
          return match
      }
    })
    .replace(/>|"|#|{|}|[|^|`|@]/g, (match) => {
      const replacements = {
        '>': '%3e',
        '"': "'",
        '#': '%23',
        '{': '%7b',
        '}': '%7d',
        '|': '%7c',
        '^': '%5e',
        '`': '%60',
        '@': '%40',
      }
      return replacements[match] || match
    })

  return `url("data:image/svg+xml;charset=UTF-8,${encoded}")`
}

/**
 *
 * @param {*} url - get svg text from url
 * @returns {string} - returns the svg text
 */

/* istanbul ignore next */
const getTextOfSvgImageUrl = async (url) => {
  const svgText = await axios({
    url: url,
    method: 'GET',
    responseType: 'text',
  })?.then((response) => {
    return response?.data
  })
  return svgText
}

/**
 * Loads css replaced string of svg tag text
 * @param {*} url - get svg text from url and convert to css string
 * @returns {string} - returns the css string
 */

/* istanbul ignore next */
const loadCssStringOfSvgImage = async (url) => {
  const svgText = await getTextOfSvgImageUrl(url)
  const cssString = convertSvgTextToCssString(svgText)
  return cssString
}

/**
 * sets background image to the applied dom with the given image url
 * @param {*} appliedSection
 * @param {*} bgImage
 */
/* istanbul ignore next */
const setBackgroundImage = async (appliedSection = null, bgImage = null) => {
  if (bgImage && appliedSection) {
    let backgroundImageUrl = `url(${bgImage})`
    const mediaExtension = getBlobDetail(bgImage, 'extension')
    if (mediaExtension.includes('svg')) {
      backgroundImageUrl = await loadCssStringOfSvgImage(bgImage)
    }
    document.getElementsByClassName(appliedSection)[0].style.backgroundImage =
      backgroundImageUrl
  } else {
    document.getElementsByClassName(appliedSection)[0].style.backgroundImage =
      null
  }
}

/* istanbul ignore next */
const setBackground = (appliedSection = null, bgData = null) => {
  /* istanbul ignore next */
  if (bgData) {
    document.getElementsByClassName(appliedSection)[0].style.backgroundColor =
      bgData
  } else {
    /* istanbul ignore next */
    document.getElementsByClassName(appliedSection)[0].style.backgroundColor =
      null
  }
}
/* istanbul ignore next */
const setBgImageProperty = (appliedSection) => {
  if (appliedSection) {
    document.getElementsByClassName(appliedSection)[0].style.backgroundRepeat =
      'no-repeat'
    // document.getElementsByClassName(appliedSection)[0].style.backgroundSize =
    //   'cover'
  }
}
/* istanbul ignore next */
const applyPreviewData = (appliedSection, appliedData) => {
  if (document.getElementsByClassName(appliedSection)?.length > 0) {
    if (!appliedData?.isBackgroundImage) {
      setBackgroundImage(appliedSection, null)
      setBackground(appliedSection, appliedData?.backgroundColor)
    } else if (appliedData?.isBackgroundImage) {
      setBackgroundImage(appliedSection, appliedData?.backgroundFileLocation)
      setBackground(appliedSection, null)
      setBgImageProperty(appliedSection)
    } else {
      setBackgroundImage(appliedSection, null)
      setBackground(appliedSection, null)
    }
  }
}

const tribunalActionEnabledStages = [
  'NTO',
  'PostedPCN',
  'Tribunal',
  'TribunalReview',
  'HighCourt',
  'TribunalAdjudicator',
  'OFR',
  'OfrExpired',
  'ConfirmationNote',
  'RevokeOrder',
  '14DayLetter',
  '28DayLetter',
  'InTime',
  'OutofTime',
  'WarrantRequested',
  'WarrantReceived',
  'WarrantExported',
  'ChargeCertificate',
  // 'WarrantEligible',
]
const locationNameCheck = 'Location name already exist'
const uRLCheck = 'URL already exist'
const domainURLCheck = 'Duplicate domain URL is not allowed'
const SuccessMsgs = {
  locationAdd: 'Location has been added successfully.',
  locationEdit: 'Updated Successfully',
}
const mediaExtensions = {
  image: {
    tiff: ['tif', 'tiff'],
    imageTagSupports: ['jpg', 'jpeg', 'jfif', 'png', 'bmp', 'svg'],
    heic: ['heic', 'heif'],
    videoTagSupports: ['mp4', 'ogg', 'webmp'],
    iframeTagSupports: ['xlsx', 'xls', 'doc', 'docx', 'ppt', 'pptx', 'txt'],
    video: 'video',
  },
}
const fileSequenceNoHelpText =
  'This field value is non-editable, please contact support team to change it.'

const searchFieldDataSource = [
  {
    apiFieldName: 'companyName',
    displayName: 'Company Name',
    controlType: 'textBox',
    sentToAPI: false,
    id: 'companyName',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.privateLand,
      contractTypes?.ecnName,
    ],
  },
  {
    apiFieldName: 'postCode',
    displayName: 'Post Code',
    controlType: 'textBox',
    sentToAPI: false,
    id: 'postCode',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.privateLand,
      contractTypes?.ecnName,
    ],
  },
  {
    apiFieldName: 'surname',
    displayName: 'Surname',
    controlType: 'textBox',
    sentToAPI: false,
    id: 'surname',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.privateLand,
      contractTypes?.ecnName,
    ],
  },
  {
    apiFieldName: 'sourceType',
    displayName: 'Source Type',
    controlType: 'list',
    sentToAPI: true,
    id: 'caseSourceTypeId',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.privateLand,
      contractTypes?.ecnName,
    ],
  },
  {
    apiFieldName: 'serviceMethod',
    displayName: 'Service Method',
    controlType: 'list',
    sentToAPI: true,
    id: 'issuedTypeId',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.privateLand,
      contractTypes?.ecnName,
    ],
  },
  {
    apiFieldName: 'stage',
    displayName: 'Stage',
    controlType: 'list',
    sentToAPI: true,
    id: 'stageId',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.privateLand,
      contractTypes?.ecnName,
    ],
  },
  {
    apiFieldName: 'status',
    displayName: 'Status',
    controlType: 'list',
    sentToAPI: true,
    id: 'statusId',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.privateLand,
      contractTypes?.ecnName,
    ],
  },
  {
    apiFieldName: 'location',
    displayName: 'Location',
    controlType: 'list',
    sentToAPI: true,
    id: 'locationId',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.privateLand,
      contractTypes?.ecnName,
    ],
  },
  {
    apiFieldName: 'createdOn',
    displayName: 'Created On',
    controlType: 'datePicker',
    sentToAPI: false,
    id: 'createdOn',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.privateLand,
      contractTypes?.ecnName,
    ],
    subControl: [
      {
        apiFieldName: 'createdOnFrom',
        displayName: 'Created On From',
        controlType: 'datePicker',
        sentToAPI: false,
        id: 'createdOnFrom',
      },
      {
        apiFieldName: 'createdOnTo',
        displayName: 'Created On To',
        controlType: 'datePicker',
        sentToAPI: false,
        id: 'createdOnTo',
      },
    ],
  },
  {
    apiFieldName: 'contraventionDate',
    displayName: 'Contravention Date',
    controlType: 'datePicker',
    sentToAPI: false,
    id: 'contraventionDate',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.privateLand,
      contractTypes?.ecnName,
    ],
    subControl: [
      {
        apiFieldName: 'contraventionDateFrom',
        displayName: 'Contravention Date From',
        controlType: 'datePicker',
        sentToAPI: false,
        id: 'contraventionDateFrom',
      },
      {
        apiFieldName: 'contraventionDateTo',
        displayName: 'contravention Date To',
        controlType: 'datePicker',
        sentToAPI: false,
        id: 'contraventionDateTo',
      },
    ],
  },
  {
    apiFieldName: 'contraventionDescription',
    displayName: 'Contravention Description',
    controlType: 'list',
    sentToAPI: true,
    id: 'contraventionId',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.privateLand,
      contractTypes?.ecnName,
    ],
  },
  {
    apiFieldName: 'vehicleColour',
    displayName: 'Vehicle Colour',
    controlType: 'list',
    sentToAPI: true,
    id: 'vehicleColorId',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.privateLand,
      contractTypes?.ecnName,
    ],
  },
  {
    apiFieldName: 'vehicleMake',
    displayName: 'Vehicle Make',
    controlType: 'list',
    sentToAPI: true,
    id: 'vehicleMakeId',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.privateLand,
      contractTypes?.ecnName,
    ],
  },
  {
    apiFieldName: 'vrmNo',
    displayName: 'VRM No',
    controlType: 'textBox',
    sentToAPI: false,
    id: 'vrmNo',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.privateLand,
      contractTypes?.ecnName,
    ],
  },
  {
    apiFieldName: 'warningFlag',
    displayName: 'Warning Flag',
    controlType: 'list',
    sentToAPI: false,
    id: 'warningFlag',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.privateLand,
      contractTypes?.ecnName,
    ],
  },
  {
    apiFieldName: 'CEOId',
    displayName: 'CEO Id',
    controlType: 'textBox',
    sentToAPI: false,
    id: 'CEOId',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.privateLand,
      contractTypes?.ecnName,
    ],
  },
  {
    apiFieldName: 'amountPaid',
    displayName: 'Amount Paid',
    controlType: 'textBox',
    sentToAPI: false,
    id: 'amountPaid',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  // {
  //   apiFieldName: 'balance',
  //   displayName: 'Balance',
  //   controlType: 'number',
  //   sentToAPI: false,
  //   id: 'balance',
  // },
]
/* istanbul ignore next */
const sampleFileZone = 'SampleFileZone'

const getLabel = (label, mandatory, disabled) => {
  return (
    <div
      className={` ${'inputLabel'} ${
        disabled ? 'contract_InputLabel_Disabled' : 'contract_InputLabel'
      }`}
    >
      <span>
        {label}
        {mandatory ? <span className="mandatorySymbol">{` *`}</span> : null}
      </span>
    </div>
  )
}
const brandingDeleteSuccess = 'Branding deleted successfully.'
/* istanbul ignore next */
const getFileSizeInKb = (fileSize) => {
  return Number(fileSize) / 1024 / 1024
}

/* istanbul ignore next */
const customTooltipThemeData = {
  branding: {
    tooltip: {
      marginRight: '85px !important',
    },
  },
}

const defaultButtonBGColor = '#1f3c88'
const defaultButtonTextColor = '#FFFFFF'

const acceptPartPayment =
  'Are you sure wish to write off the remaining balance?'
const updatedSuccessfully = 'Updated Successfully'
const markAsPaidContent = 'Are you sure wish to mark _ as paid?'

const roleList = {
  superAdmin: 'superadmin',
  orgAdmin: 'organisationadmin',
  ceo: 'ceo',
  inspector: 'inspector',
  boUser: 'bouser',
  seniorBoUser: 'seniorbouser',
  contractAdmin: 'contractadmin',
  simpleAdmin: 'simpleadmin',
  airaManager: 'airamanager',
  yunexOperator: 'yunexoperator',
  summaryAgent: 'summaryagent',
  summaryManager: 'summarymanager',
}
const debtReviewCancelCases = {
  cancelSuccessMessage: 'Debt review completed',
  permittedRole: [
    'noticeprocessing',
    'bomanager',
    'superadmin',
    'client',
    'simpleadmin',
  ],
}
const liveTrackingTimelineTabPermittedRoles = [
  'bomanager',
  'ceomanager',
  'superadmin',
  'contractadmin',
  'organisationadmin',
  'simpleadmin',
]
const configurationPagePermittedRoles = [
  'superadmin',
  'organisationadmin',
  'contractadmin',
  'simpleadmin',
  'bomanager',
]
const discountButtonValue = {
  discount: {
    title: 'Discount Pre NtO Challenge Reject',
    subTitle: null,
    description:
      'Informal challenge reject actioned and discount period reset for 14 days',
  },
  discountRepReject: {
    title: 'Discount Rep Reject',
    subTitle: null,
    description:
      'Representation reject actioned and discount period re-offered for 14 days. After which then 14 days later will progress to Charge Certificate',
  },
  discountCCRepReject: {
    title: 'Discount CC Rep Reject',
    subTitle: null,
    description:
      'Late Representation following the issue of a Charge Certificate reject actioned and discount period re-offered for 14 days. After which 14 days later will progress to eligible for Debt Registration',
  },
  discountOFRRepReject: {
    title: 'Discount OFR Rep Reject',
    subTitle: null,
    description:
      'Late Representation following the issue of an Order for Recovery reject actioned and discount period re-offered for 14 days. After which 14 days later will progress to eligible for Warrant Request',
  },
  reverttoIssuedDiscount: {
    title: 'Revert to Issued (Discount)',
    description: 'Revert to issued/discount',
    customPopupTitle:
      'Are you sure wish to revert the case to issued stage at the discount charge',
  },
  reverttoFullChargeAtCC: {
    title: 'Revert to Full charge at CC',
    description:
      'Following the issue of a Charge Certificate full charge period re-offered for 14 days. After which 14 days later will progress to eligible for progression.',
  },
  discountRepReject21Days: {
    title: 'Discount rep reject(21 days)',
    description:
      'Representation reject actioned and discount period re-offered for 21 days. After which then 21 days later will progress to Charge Certificate.',
    customPopupTitle:
      'Are you sure you want to record the entry in NPS as Discount Rep Reject (21 Days) ?',
  },
  revertToIssuedDiscountPL: {
    title: 'Revert to Issued(Discount)',
    description: 'Revert to Notice to Keeper sent and reset discount',
    customPopupTitle:
      'Are you sure you wish to revert the case to the primary stage(NTK) to allow progression again from that point at the discount amount',
    customEventDescription: 'Revert to Issued(Discount) button invoked',
  },
  revertToDiscountAtCC: {
    title: 'Revert to Discount at CC',
    description:
      'Re-offers the discounted amount at CC stage, recalculates the amount due, and records an audit entry. If the case remains unpaid after 14 days, the amount will revert to the CC charge.',
    customPopupTitle:
      'Are you sure you want to record the entry in NPS as Revert to discount at CC ?',
    customEventDescription: 'Revert to Discount at CC Button invoked',
  },
}

const isShowPreviouslyIssuedPcnValues = {
  enableOption:
    "Enabling this feature will show the CEOs with the details of previously issued PCN's for that vehicle for that day.",
  disableOption:
    "Disabling this feature will not show the CEOs with the details of previously issued PCN's for that vehicle for that day.",
}

const toggleHorizontalLine = 'HorizontalLine'

const contractSetupToggleOptions = [
  {
    optionLabel: 'Client Workflow',
    optionValue: 'clientWorkFlow',
    optionDataTestId: 'clientWorkFlow',
  },
  {
    optionLabel: 'International Debt Recovery',
    optionValue: 'internationalDebtRecovery',
    optionDataTestId: 'internationalDebtRecovery',
  },
  {
    optionLabel: 'Show FAQ in Citizen Portal',
    optionValue: 'isShowFAQCitizen',
    optionDataTestId: 'ShowFAQCitizenSwitch',
  },
  {
    optionLabel: 'Is Active',
    optionValue: 'isActive',
    optionDataTestId: 'ActiveSwitch',
  },
  {
    optionLabel: 'TRO/TMO Map',
    optionValue: 'troTmoMap',
    optionDataTestId: 'TroTmoMapSwitch',
  },
  {
    optionLabel: 'Show previously issued PCN in HHD',
    optionValue: 'isShowPreviouslyIssuedPCN',
    optionDataTestId: 'IsShowPreviouslyIssuedPCN',
  },
  {
    optionLabel: 'Correspondence PIN',
    optionValue: 'isCorrespondencePin',
    optionDataTestId: 'correspondencePinSwitch',
  },
  {
    optionLabel: 'HHD Illumin8',
    optionValue: 'isHHDIllumini8',
    optionDataTestId: 'HHDIllumin8',
  },
  {
    optionLabel: 'Officer Tracking',
    optionValue: 'isOfficerTracking',
    optionDataTestId: 'officerTracking',
  },
  {
    optionLabel: 'HALO Integration',
    optionValue: 'isHaloIntegration',
    optionDataTestId: 'haloIntegration',
  },
  {
    optionLabel: 'Hide Personal Info for CEO Manager',
    optionValue: 'isHidePersonalInfo',
    optionDataTestId: 'hidePersonalInfo',
  },
  {
    optionLabel: '14 Day Letter Review',
    optionValue: 'is14DayLetterReview',
    optionDataTestId: '14DayLetterReview',
  },
  {
    optionLabel: 'Challenge status email & SMS alert',
    optionValue: 'isSendChallengeStatus',
    optionDataTestId: 'sendChallengeStatus',
  },
  {
    optionLabel: 'Ignore Repeat Rep at CC',
    optionValue: 'isIgnoreRepeatRepAtCC',
    optionDataTestId: 'ignoreRepeatRepatCC',
  },
  {
    optionLabel: 'HorizontalLine',
    optionValue: 'HorizontalLine',
    optionDataTestId: 'HorizontalLine',
  },
  {
    optionLabel: 'Print Local',
    optionValue: 'isPrintLocal',
    optionDataTestId: 'printLocalId',
  },
  {
    optionLabel: 'Print Export',
    optionValue: 'isPrintExport',
    optionDataTestId: 'printExportId',
  },
  {
    optionLabel: 'Manual Case Creation',
    optionValue: 'isManualCaseEnabled',
    optionDataTestId: 'isManualCaseEnabled',
  },
  {
    optionLabel: 'BO/QC User Case Update',
    optionValue: 'isBOQCUserCaseUpdate',
    optionDataTestId: 'correspondencePinSwitch',
  },
  {
    optionLabel: 'Briefing',
    optionValue: 'isBriefingEnabled',
    optionDataTestId: 'isBriefingEnabled',
  },
  {
    optionLabel: 'Payments Monitoring',
    optionValue: 'isPaymentMonitoringEnabled',
    optionDataTestId: 'isPaymentMonitoringEnabled',
  },
  {
    optionLabel: 'Market Inspection',
    optionValue: 'isMarketInspectionEnabled',
    optionDataTestId: 'isMarketInspectionEnabled',
  },
  {
    optionLabel: 'Debt PE Controller',
    optionValue: 'isDebtPEControllerEnabled',
    optionDataTestId: 'isDebtPEControllerEnabled',
  },
  {
    optionLabel: 'KADOE Live Lookup',
    optionValue: 'isKADOELiveLookupEnabled',
    optionDataTestId: 'isKADOELiveLookupEnabled',
  },
  {
    optionLabel: 'AIRA',
    optionValue: 'isAIRAEnabled',
    optionDataTestId: 'isAIRAEnabled',
  },
  {
    optionLabel: 'Chatbot',
    optionValue: 'isChatBotEnabled',
    optionDataTestId: 'isChatBotEnabled',
  },
  {
    optionLabel: 'Customer Response Preference',
    optionValue: 'customerEmailResponseEnabled',
    optionDataTestId: 'customerEmailResponseEnabled',
  },
  {
    optionLabel: 'Simple Admin hide PII',
    optionValue: 'isSimpleAdminHidePII',
    optionDataTestId: 'isSimpleAdminHidePII',
  },
  {
    optionLabel: 'Payment Plan',
    optionValue: 'isPaymentPlanEnabled',
    optionDataTestId: 'isPaymentPlanEnabled',
  },
  {
    optionLabel: 'Hide Reg 10 on HHD',
    optionValue: 'isHideReg10OnHHD',
    optionDataTestId: 'hideReg10OnHHD',
  },
]

const clampAndRemovalSource = {
  optionLabel: 'Clamp & Removal',
  optionValue: 'isCandREnabled',
  optionDataTestId: 'isCandREnabled',
}

const contractDisablingToggles = {
  cAndREnabled: 'isCandREnabled',
  clientWorkFlow: 'clientWorkFlow',
  marketInspectionDisabled: 'isMarketInspectionEnabled',
  isKADOELiveLookupEnabled: 'isKADOELiveLookupEnabled',
  isDiscountRepReject21Days: 'isDiscountRepReject21Days',
  isPaymentPlanEnabled: 'isPaymentPlanEnabled',
}
const debtorReview = {
  optionLabel: 'Debtor Review',
  optionValue: 'isDebtorReviewEnabled',
  optionDataTestId: 'isDebtorReviewEnabled',
}
const discountRepReject21Days = {
  optionLabel: 'Discount Rep Reject(21 Days)',
  optionValue: 'isDiscountRepReject21Days',
  optionDataTestId: 'isDiscountRepReject21Days',
}
const printImages = {
  optionLabel: 'Print Images',
  optionValue: 'isPrintImageEnabled',
  optionDataTestId: 'isPrintImageEnabled',
}

const userPermissionMessage = {
  accountPermissionAction:
    'Please contact your supervisor to configure your account.',
  accountInactive: 'Account inactive',
  insufficientPermission: 'Insufficient Permissions',
  invalidUser: 'Invalid User',
  poundManagerLowerCase: 'pound manager',
  poundOperatorLowerCase: 'pound operator',
  onBoardCeoLowerCase: 'onboard ceo',
  mobileCeoLowerCase: 'mobile ceo',
}
const attachmentDropZoneStaticData = {
  attachmentDropZoneError:
    'Error! File type is not permitted, please attach a document of the following type(s) only .png, .pdf, .jpg, .bmp, .tif, .heic',
  onlyImageAttachmentDropZoneError:
    'Error! File type is not permitted, please attach a document of the following type(s) only .png, .jpg, .bmp, .tif, .heic',
  attachmentCSVError: 'CSV Format does not match',
  fileUploadValidatingContent: 'Please wait while we are validating CSV...',
  fileUploadSuccessContent: 'Please wait... Uploading in progress.',
  fileUploadSkipContentData:
    'Do you wish to skip the error data and upload the CSV?',
  fileUploadSaveAlertMessage: 'CSV Uploaded Successfully',
}
const attachmentInvalidErrors = {
  bulkUpdateError:
    'Error! File type is not permitted, please attach a document of the following type(s) only .png .pdf .jpg .bmp .tif .heic .doc',
  attachmentTabError:
    'Error! File type is not permitted, please attach a document of the following type(s) only .png, .pdf, .jpg, .bmp, .tif, .heic',
  fileFormatError: 'Error! File type is not permitted.',
  scanImportError:
    'Error! File type is not permitted, please attach a document of the following type(s) only .png .jpg  .tif .pdf',
  ceoTaskAttachmentError:
    'Error! File type is not permitted, please attach a document of the following type(s) only .tif .bmp .jpg .jpeg .png .heic',
}
const bulkUpdateMessages = {
  bulkUpdateCancelWarning: 'Are you sure wish to cancel bulk batch details ?',
  duplicateWarning:
    'duplicate case numbers have been identified, which would be skipped.',
  acceptedFileFormats: `(Accepted file type: .png .pdf .jpg .bmp .tif .heic) |
                  Max.Files:10 | Max.Size:10 mb per file`,
  maxAllowedFiles: 'Maximum allowed files are 10, please review',
  maxAllowedSizeForAttachments:
    'Error! File size too big, maximum allowed is 10mb',
  attachmentfilecount: 'Attachments file count : ',
  bulkReportSubmitted: 'Bulk update successfully submitted,',
  bulkReportCommitApproval: 'Awaiting for approval.',
  bulkReportCommitConfirmation:
    'Are you sure want to commit this Bulk Update ?',
  bulkReportCommitHelpText:
    'Validation set for stage and status as per the PCN service method',
}
const fileUploadStateValue = {
  maxAllowedCaseNumbers: 1000,
  caseNumberDuplicate: 'Case already exists. Try again with another number',
  caseNumberCountValidate: 'Case number exceeding the limit 1000',
  fieldValidationError: "CSV format doesn't match",
  csvFileValidationByAPI: 'Please wait while we are validating CSV...',
  skipCSVFileError: 'Do you wish to skip the error data and upload the CSV ?',
  csvFileUploadAPI: 'Uploading...',
}
const maxNotesLength = 60000
const maximumCharacterLimitReached = 'Maximum character limit is reached'
const placeHolderText = {
  enterNotes: 'Enter Notes',
}
/* istanbul ignore next */
const holdReasonStaticData = {
  contentMessageAdd: 'Hold Reason added successfully!',
  contentMessageEdit: 'Hold Reason updated successfully!',
  fromAddMode: 'add',
  fromEditMode: 'edit',
  headerTitle: 'Add Hold Reason',
  btnTextSave: 'Add',
  btnTextCancel: 'Cancel',
  headerEditTitle: 'Edit Hold Reason',
  btnUpdate: 'Update',
  orgInputAlert: 'Please select organisation',
  postMethode: 'POST',
  putMethode: 'PUT',
  holdReasonInputAlert: 'Should not exceed more than 200 character',
  deletedMessage: 'Hold Reason deleted successfully!',
  deletedConfirmation: 'Are you sure want to delete?',
}
/* istanbul ignore next */
const cancellationReasonFileUploadPage = {
  sampleFileCancelreason: 'cancelreasonsample',
  skippedDataCancelreason: 'cancelreason',
  crConfigurationCancellationReason: 'cancelreasonsample',
  crConfigurationSampleDownloadName: 'cancellationreasonsample',
  crConfigurationSkippedDownloadName: 'cancellationreason',
}

const officerTrackingMessage = {
  liveLocationURL: 'https://development-mobile-restapi.azurewebsites.net/hub',
  locationNameError: 'Error on getting GEO Location name',
  noCEOLocation: 'Selected CEO not in live state.',
}
/* istanbul ignore next */
const RemoveBase64FileType = (base64String) => {
  return base64String?.substring(
    base64String?.indexOf(',') + 1,
    base64String?.length
  )
}

/* getFileData from blobURL */
/* istanbul ignore next */
const getBase64FromBlobURL = async (params) => {
  const base64BlobData = await axios({
    url: params,
    method: 'GET',
    responseType: 'blob',
  })

  if (base64BlobData) {
    if (base64BlobData?.status === apiResponseCode?.successCode) {
      const reader = new FileReader()
      reader.readAsDataURL(base64BlobData?.data)
      return (reader.onloadend = () => {
        const base64data = reader?.result
        return decode(RemoveBase64FileType(base64data))
      })
    }
  }
}

/* istanbul ignore next */
const tooltipTheme = (customTooltipValue) =>
  createTheme({
    components: {
      MuiTooltip: {
        styleOverrides: {
          arrow: () => ({
            color: 'var(--tooltipBackgroundColor) !important',
          }),
          tooltip: {
            fontFamily: 'var(--default-FontName) !important',
            fontSize: 'var(--tooltipFontSize) !important',
            fontWeight: 'var(--default-FontWeight) !important',
            color: 'var(--tooltipFontColor) !important',
            backgroundColor: 'var(--tooltipBackgroundColor) !important',
            margin: '0px !important',
            marginBottom: '2px !important',
            borderRadius: 'var(--tooltipBorderRadius) !important',
            ...customTooltipValue,
          },
        },
      },
    },
  })
/* istanbul ignore next */
const locationFileUploadPage = {
  sampleFileLocation: 'locationsample',
  skippedDataLocation: 'Location',
}
const xmlFileDownLoadClickHandler = (csvJsonData, fileName) => {
  const blob = new Blob([csvJsonData], { type: 'application/xml' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.setAttribute('href', url)
  a.setAttribute('download', fileName)
  a.click()
}
const dvlaXMLFileDownloadSuccess = 'Batch downloaded successfully!'
const noValuesChanged = 'No values changed'

const emptyFunction = () => {}

const inputFieldNameListLoweredCase = {
  contractName: 'contractName',
  addFaultName: 'addfaultName',
  offlineCaseCount: 'offlineCaseCount',
  emailAddress: 'emailAddress',
  contractSenderEmail: 'contractSenderEmail',
  recipientMail: 'recipientMail',
  troTmoMapPath: 'trotmomappath',
  kadoeReference: 'kadoeReference',
  hhdAutoLogout: 'hhdAutoLogout',
  skipDays: 'skipDays',
  citizenPortalDomain: 'citizenPortalDomain',
  autoAssignTaskCount: 'autoAssignTaskCount',
  hhdTestPrintCount: 'hhdTestPrintCount',
  hhdDataBackupConfirmPin: 'hhdDataBackupConfirmPin',
  hhdDataBackupPin: 'hhdDataBackupPin',
  emailIntegrationSecretKey: 'emailIntegrationSecretKey',
  emailIntegrationClientId: 'emailIntegrationClientId',
  emailAddressIntegration: 'emailIntegrationAddress',
  vatRegistrationNumber: 'vatRegistrationNumber',
  privacyPolicyUrl: 'privacyPolicyUrl',
  vatPercentage: 'vatPercentage',
  dataSharingPolicyUrl: 'dataSharingPolicyUrl',
  cookiePolicyUrl: 'cookiePolicyUrl',
  customerCode: 'customerCode',
  fundCode: 'fundCode',
}
const contractSetupFieldErrors = {
  faultNameError: 'Please enter the Fault Name',
  offlineCaseCount: 'Minimum count should be 10 and maximum can be 500.',
  validTROMap: 'Enter valid TRO/TMO map URL',
  kadoeReferenceError: 'Please enter alpha numeric values',
  CTDomainError: 'Enter valid CT Domain',
  noValuesChanges: 'No values changed.',
  taskMaxAllowedCharacters: 'Maximum 50 characters only allowed ',
  duplicateValues: 'Duplicate category is not allowed',
  privacyPolicyUrl: 'Enter valid privacy policy URL',
  dataSharingPolicyUrl: 'Enter valid data sharing policy URL',
  cookiePolicyUrl: 'Enter valid cookie policy URL',
  // US-313727: Barcode (Customer Code / Fund Code) - numeric, 1-10 digits
  customerCodeRequired: 'Please enter Customer Code',
  customerCodeInvalid: 'Customer Code must be 1-10 digits',
  fundCodeRequired: 'Please enter Fund Code',
  fundCodeInvalid: 'Fund Code must be 1-10 digits',
}
const modes = {
  newMode: 'new',
  editMode: 'edit',
}

const caseSettingsPage = {
  optionDataSource: {
    yes: 'Yes',
    no: 'No',
  },
  iscancel: 'iscancel',
  isupdate: 'isupdate',
  true: 'true',
  false: 'false',
  stage: 'stage',
  reasonForCancellation: 'reasonForCancellation',
  fileCount: 10,
  notes: 'notes',
  notesHeadding: 'Notes',
  attachmentsHeading: 'Attachments',
  cancelHeading: 'Cancel',
  updateHeading: 'Update',
}
/* istanbul ignore next */
const getBase64StringDetail = (base64String, required) => {
  let requiredData = required?.toString()?.toLowerCase()
  if (requiredData === 'base64string')
    return base64String?.substring(
      base64String?.indexOf(',') + 1,
      base64String?.length
    )
  if (requiredData === 'filetype')
    return base64String?.substring(0, base64String?.indexOf(','))
}
const apiMethods = {
  post: 'POST',
  put: 'PUT',
  get: 'GET',
}

const purgePage = {
  purgeHeading: 'Purge',
  add: 'add',
  edit: 'edit',
  contract: 'contract',
  organisation: 'organisation',
  deleteConfirmationHeading: 'Delete Confirmation',
  confirmationMessage: 'Are you sure to delete this purge rule?',
  headerTitle: 'Add New Purge Rule',
  editHeaderTitle: 'Edit Purge Rule',
  pleaseSelectOrganisation: 'Please select organisation',
  yearsCount: 'yearscount',
  minYearsCount: 2,
  minYearsCountPCN: 1,
  maxYearsCount: 6,
  placeHolderYearsCount: 'Select years',
  maxYearsCountWarrningMessage: 'Maximum value is 6',
  minYearsCountWarrningMessage: 'Minimum value is 2',
  minYearsCountWarrningMessagePCN: 'Minimum value is 1',
  noValuesChanged: 'No values changed.',
  saveSuccessMessage: 'Purge rule added successfully!',
  updateSuccessMessage: 'Purge rule updated successfully!',
  deleteSuccessMessage: 'Purge rule deleted successfully!',
  buttonTextField: {
    addNewRule: 'Add New Rule',
    delete: 'Confirm',
    cancel: 'Cancel',
    save: 'Save',
    update: 'Update',
    confirmation: 'Confirm',
    edit: 'Edit',
  },
  purgeConditionsSection: {
    purgeConditions: 'Purge Conditions',
    setYearsCountSinceCase: 'Set years count since case',
    closed: ' closed',
    purgePersonalInformation: 'Purge personal information',
    purgeCaseAttachments: 'Purge case attachments',
    purgeCaseNotes: 'Purge case notes',
    purgeIncludesNonUKVRM: 'Purge includes Non-UK VRM',
  },
  orphanRecordsSection: {
    sectionTitle: 'Orphan Records',
    orphanRecordsPurgePeriod: 'orphanrecordspurgeperiod',
    fieldLabel: 'Orphan Records Purge Period (In days)',
    placeHolder: 'Enter days',
    minDays: 1,
    maxDays: 45,
    defaultDays: 3,
    helpText:
      'Set the number of days after which an orphan ANPR records needs to be removed. Allowed range is 1 to 45 days.',
    minDaysWarningMessage: 'Minimum value is 1',
    maxDaysWarningMessage: 'Maximum value is 45',
    requiredFieldMessage: 'This field is required',
    rangeErrorMessage: 'Allowed range is 1-45',
  },
  privateLandContractType: 'PrivateLand',
}

const revertedType = {
  ntoReverted: {
    displayText: 'NTO/Reverted',
    key: 'NTO/Reverted',
  },
  postedPCNReverted: {
    displayText: 'PostedPCN/Reverted',
    key: 'PostedPCN/Reverted',
  },
  ntoActioned: {
    displayText: 'NTO/Actioned',
    key: 'NTO/Actioned',
  },
  postedPCNActioned: {
    displayText: 'PostedPCN/Actioned',
    key: 'PostedPCN/Actioned',
  },
  revertIssued: {
    displayText: 'RevertToIssued',
    key: 'RevertToIssued',
  },
  revertToCourtEligible: {
    displayText: 'Revert to Court Eligible',
    key: 'RevertToCourtEligible',
  },
  reverttoAtCourt: {
    displayText: 'Revert to At Court',
    key: 'RevertToAtCourt',
  },
  revertToFinalWarningSent: {
    displayText: 'Revert to Final Warning Sent',
    key: 'RevertToFinalWarningSent',
  },
  revertToSelectForFinalWarning: {
    displayText: 'Revert to select for Final Warning',
    key: 'RevertToSelectForFinalWarning',
  },
  revertToSelectForNIP: {
    displayText: 'Revert to select for NIP',
    key: 'RevertToSelectForNIP',
  },
  revertToNIPSent: {
    displayText: 'Revert to NIP Sent',
    key: 'RevertToNIPSent',
  },
  revertToSelectForNIPFinalWarning: {
    displayText: 'Revert to select for NIP Final Warning',
    key: 'RevertToSelectForNIPFinalWarning',
  },
  revertToNIPFinalWarningSent: {
    displayText: 'Revert to NIP Final Warning Sent',
    key: 'RevertToNIPFinalWarningSent',
  },
  revertToSelectForChargeCert: {
    displayText: 'Revert to Select for Charge Cert',
    key: 'RevertToSelectForChargeCert',
  },
  revertToPostChargeCert: {
    displayText: 'Revert to Post Charge Cert',
    key: 'RevertToPostChargeCert',
  },
  plRevertToIssued: {
    displayText: 'PL Revert To Issued',
    key: 'PLRevertToIssued',
  },
  plRevertToSelectForFinalWarning: {
    displayText: 'PL Revert To Select For Final Warning',
    key: 'PLRevertToSelectForFinalWarning',
  },
  plRevertToFinalWarningSent: {
    displayText: 'PL Revert To Final Warning Sent',
    key: 'PLRevertToFinalWarningSent',
  },
  plGenerateNotice: {
    displayText: 'PL Generate Notice',
    key: 'PLGenerateNotice',
  },
}
/* istanbul ignore next */
const signOutTheUser = (
  msalInstance,
  config,
  authenticationResult,
  msalPolicyName
) => {
  localStorage?.setItem('logout-event', 'true')
  // can select which account application should sign out
  let homeAccountId = authenticationResult?.account?.homeAccountId

  const logoutRequest = {
    account: msalInstance?.getAccountByHomeId(homeAccountId),
    postLogoutRedirectUri: config?.VariableSettings?.BackOfficeUIURL,
  }
  // for AD user-flow(B2CPolicyNameCustom) alone the authorityEndpoint should be common
  if (
    msalPolicyName?.toString()?.toLowerCase() ===
    config?.VariableSettings?.B2CPolicyNameCustom?.toString()?.toLowerCase()
  ) {
    logoutRequest.authority = config?.VariableSettings?.B2CAuthorityCommon
  }

  msalInstance?.logoutRedirect(logoutRequest)
}

const messagePopupStaticData = {
  setLocation: 'Set Location',
  setUser: 'Set User',
  setVRM: 'Set VRM',
  setContravention: 'Set Contravention Code',
  messageSuccess: 'Message has been successfully sent',
  updateMessage: 'Message updated successfully',
  validDateTIme: 'Valid Till should be greater or equal to Valid From date',
  view: 'view',
  add: 'add',
  edit: 'edit',
  user: 'user',
  location: 'location',
  contraventionCode: 'contraventioncode',
  vrm: 'vrm',
}

const representationReviewPage = {
  heading: 'Representation Review',
  tabTitle: {
    representations: 'Representations',
    fourteenDayLetter: '14 Days Letter',
  },
}

const workFlowManagerPage = {
  ok: 'OK',
  cancel: 'Cancel',
  workflowManager: 'Workflow Manager',
  assignedCases: 'Assigned Cases',
  unAssignedCases: 'UnAssigned Cases',
  tabTitle: {
    unassignedTasks: 'Unassigned Tasks',
    assignedTasks: 'Assigned Tasks',
    autoAssignConfiguration: 'Auto Assign Configuration',
    overviewTasks: 'Overview',
    fourteenDayLetter: '14 Days Letter',
    infoRequested: 'Info Requested',
    chargeCertificateReview: 'CC Rep Review',
    qaConfiguration: 'QA Config',
    challengeOverview: 'Challenge Overview',
  },
  dialogType: {
    assignUser: 'assignUser',
    unAssignUser: 'unAssignUser',
    reAssignUser: 'reAssignUser',
    complete: 'complete',
    deleteTask: 'deleteTask',
  },
  buttonText: {
    confirm: 'Confirm',
    cancel: 'Cancel',
    yes: 'Yes',
    no: 'No',
  },
}

const scanImportPage = {
  pageTitle: 'Scan Import',
  upload: 'Upload',
  attachment: 'Attachment',
  uploadTitle:
    ' (Accepted file type (e.g., JPEG,PNG,TIFF) and documents formats (e.g., PDF) | Max. files: 1)',
  fileLimit: 1,
  fileSizeLimit: 10,
  allowedFileLimitMessage: 'Max files allowed is 1, please review',
  maxAllowedSizeForAttachments:
    'Error! File size too big, maximum allowed is 10mb',
  uploadSuccessMessage: 'File uploaded successfully',
}
const workflow = {
  deleteConfirmationMessage: 'Are you sure to delete the workflow task?',
  assignConfirmationMessage: 'Are you sure want to assign files to ',
  invalidPcn: 'Invalid case number',
}
const linkedCases = 'Linked Cases'
const contraventionCode = {
  observationPeriodSetting:
    'Observation period must be set to enable wheel positions recording',
  enablingWheelPositions: 'Are you sure want to enable record wheel position ?',
  disablingWheelPositions:
    'Are you sure want to disable record wheel position ?',
  errorWhileSaving: `Setting the observation time as 00:00:00 will automatically disable the wheel positions toggle. Are you sure want to disable the wheel position?`,
  noValuesChanged: 'No values changed.',
  createdSuccessFully: 'Created Successfully',
  updatedSuccessfully: 'Updated Successfully',
  errors: {
    charactersAllowed: 'Only 6 Characters are allowed',
    alphaNumericAllowed: 'AlphaNumeric only allowed',
    numbersOnlyAllowed: 'Number only allowed with decimal value',
    valueGreater: 'Value should be greater than 0',
    dvlaRequired: 'Please select a DVLA option.',
  },
  fieldErrors: {
    organisation: 'organisation',
    contract: 'contract',
    code: 'code',
    description: 'description',
    observationTime: 'observationTime',
    contraventionPath: 'contraventionPath',
    insideBandA: 'insideBandA',
    insideBandB: 'insideBandB',
    differentialLevel: 'differentialLevel',
    tptetaNoticeType: 'tptetaNoticeType',
    dvlaEnquiryCode: 'dvlaEnquiryCode',
    outside: 'outside',
    locationGroup: 'locationGroup',
    recordWheelPositions: 'recordWheelPositions',
    dvla: 'dvla',
  },
}

const addAttachmentPage = {
  title: 'Attach the Documents that may support your contents',
  fileTypeText:
    '*Accepted files types: .png, .pdf, .jpg, .bmp, .tif, .heic | Max Size: 10 mb per file | Max. files: 10.',
  onlyImageFileTypeText:
    '*Accepted files types: .png, .jpg, .bmp, .tif, .heic | Max Size: 10 mb per file | Max. files: 10.',
  noteText:
    'Following successful upload, the file will be virus scanned. Next the attachment page must be reloaded to preview the uploaded document.',
  createWorkflowTaskWarnMessage:
    'Your selected attachment is added and a workflow task has been created',
  createWorkflowTask: 'Create workflow task?',
}
const templateSetupPage = {
  mode: {
    edit: 'edit',
  },
  areYouSureToUpdateTheChanges: 'Are you sure to update the changes?',
  areYouSureToSaveTheDesignedTemplate:
    'Are you sure to save the designed template?',
}
/* istanbul ignore next */
const getCurrentDateTime = () => {
  var currentdate = new Date()
  var dateTimeFormat =
    (currentdate?.getDate() < 10
      ? '0' + currentdate?.getDate()?.toString()
      : currentdate?.getDate()?.toString()) +
    (currentdate?.getMonth() + 1 < 10
      ? '0' + (currentdate?.getMonth() + 1)?.toString()
      : (currentdate?.getMonth() + 1)?.toString()) +
    currentdate?.getFullYear()?.toString() +
    '_' +
    (currentdate?.getHours() < 10
      ? '0' + currentdate?.getHours()?.toString()
      : currentdate?.getHours()?.toString()) +
    (currentdate?.getMinutes() < 10
      ? '0' + currentdate?.getMinutes()?.toString()
      : currentdate?.getMinutes()?.toString()) +
    (currentdate?.getSeconds() < 10
      ? '0' + currentdate?.getSeconds()?.toString()
      : currentdate?.getSeconds()?.toString())

  return dateTimeFormat
}
/* istanbul ignore next */
export const generateZipFromCloud = async (
  selectedData,
  allURLs,
  showLoader,
  showMessage,
  timeZoneID
) => {
  let filename = `${selectedData?.noticeType
    ?.toString()
    ?.toLowerCase()}_${selectedData?.batchReference?.toString()}_${
    selectedData?.successQuantity
      ? selectedData?.successQuantity?.toString()
      : selectedData?.quantity?.toString()
  }_${convertUtcDateTimeToTimezoneCustomFormat(
    timeZoneID,
    returnCurrentDateTimeInUTC(),
    commonDateTimeFormat('downloadingFileNameFormat_Date_Time')
  )}`
  showLoader(true)
  const zip = new JSZip()
  const folder = zip.folder('batchFiles')
  for (let i = 0; i < allURLs?.length; i++) {
    const blobPromise = await fetch(allURLs[i]?.filePath)
    if (
      blobPromise?.status === apiResponseCode?.successCode ||
      blobPromise?.status === apiResponseCode?.zeroCode
    ) {
      const name = `${allURLs[i]?.filePath
        ?.toString()
        ?.substring(
          allURLs[i]?.filePath?.toString()?.lastIndexOf('/') + 1,
          allURLs[i]?.filePath?.toString()?.indexOf('?')
        )}`
      folder?.file(name, blobPromise?.blob())
    } else {
    }
  }

  zip
    .generateAsync({ type: 'blob' })
    .then((blob) => saveAs(blob, filename))
    .catch((e) => showMessage(toastStatus?.error, e))
  showLoader(false)
  showMessage(toastStatus?.success, localPrintingMessage?.downloadSuccess)
}

const caseActionsStaticData = {
  selectActionDataSource: [
    {
      name: 'Reversal',
    },
    {
      name: 'Challenge',
    },
    {
      name: 'Discount',
    },
  ],
  caseReversal: 'Reversal',
  caseChallenge: 'Challenge',
  caseDiscount: 'Discount',
  fromAction: ['Reversal', 'Challenge', 'Discount'],
  caseActionConfirmation: 'Do you also wish to send a letter?',
  headerTitle: 'Case Update',
  btnTextSave: 'Save & Send',
  btnTextSkip: 'Skip',
  sentToEmailToastMessage: 'Letter has been sent by email to the customer',
  sentToPrintFolderToastMessage: 'Letter has been sent to the print folder',
  isEmailAndPostToastMessage: 'Letter has been sent via Email and Post',
  isEmailAndPostApprovalMessage:
    'Letter has been sent for QA review prior to being sent via Email and Post.',
  qaReviewEmailToastMessage:
    'Letter has been sent for QA review prior to being sent by email',
  qaReviewPostToastMessage:
    'Letter has been sent for QA review prior to being sent by post',
  addressMissingEmailToastMessage: 'Selected address does not have an email',
  reversalActionsButtonValue: {
    ntoReverted: {
      title: 'Revert to Select for NTO',
      description: 'Generate a new NTO',
    },
    ntoActioned: {
      title: 'Revert to Post NTO',
      description: 'Revert to NTO issued',
    },
    postedPCNReverted: {
      title: 'Revert to Select for Posted PCN',
      description: 'Generate new Postal PCN',
    },
    postedPCNActioned: {
      title: 'Revert to Post Postal PCN',
      description: 'Revert to Postal PCN issued',
    },
    letter_14Day: {
      title: 'Prepare 14 day letter reverted',
      description: 'TEC/Appeal - Send 14 day letter',
    },
    noaWarningNotice: {
      title: 'Revert to Select for Warning Notice',
      description: 'Generate new Postal Warning Notice',
    },
    revertIssued: {
      title: 'Revert to Issued',
      description: 'Revert case to issued stage (Full Charge)',
      customPopupTitle:
        'Are you sure wish to revert the case to the primary stage to allow progression again from that point at the full charge amount',
    },
    revertToSelectFinalWarning: {
      title: 'Revert to select for Final Warning',
      description: 'Reverting the case for Final warning',
      customPopupTitle:
        'Are you sure wish to revert the case to send a new Final Warning Letter and to allow progression again from that point at the full charge amount',
    },
    revertToFinalWarningSent: {
      title: 'Revert to Final Warning Sent',
      description: 'Reverting the case to Final Warning Sent',
      customPopupTitle:
        'Are you sure wish to revert the case to the secondary stage (Final Warning Issued) to allow progression again from that point at the full charge amount',
    },
    revertToAtCourt: {
      title: 'Revert to At Court',
      description: 'Reverting the case to At Court',
      customPopupTitle:
        'Are you sure wish to revert the case to the final stage to allow progression again from that point at the full charge amount',
    },
    revertToCourtEligible: {
      title: 'Revert to Court Eligible',
      description: 'Reverting the case to court eligible',
      customPopupTitle:
        'Are you sure wish to revert the case to the third stage to allow progression again from that point at the full charge amount',
    },
    revertToSelectForNIP: {
      title: 'Revert to select for NIP',
      description: 'Generate new notice of intent to prosecute.',
      customPopupTitle:
        'Are you sure you wish to revert the case to send a new NIP Letter and to allow progression again from that point at the full charge amount?',
    },
    revertToNIPSent: {
      title: 'Revert to NIP Sent',
      description: 'Revert to notice of intent to prosecute issued.',
      customPopupTitle:
        'Are you sure you wish to revert the case to the 3rd stage (NIP Issued) to allow progression again from that point at the full charge amount?',
    },
    revertToSelectForNIPFinalWarning: {
      title: 'Revert to select for NIP Final Warning',
      description: 'Generate new notice of intent to prosecute final warning',
      customPopupTitle:
        'Are you sure you wish to revert the case to send a new NIP Final Warning Letter and to allow progression again from that point at the full charge amount?',
    },
    revertToNIPFinalWarningSent: {
      title: 'Revert to NIP Final Warning Sent',
      description:
        'Revert to notice of intent to prosecute final warning issued.',
      customPopupTitle:
        'Are you sure you wish to revert the case to the 4th stage (NIP Final Warning Sent) to allow progression again from that point at the full charge amount?',
    },
    revertToSelectForChargeCert: {
      title: 'Revert to Select for Charge Cert',
      description:
        'Revert Case to allow a new charge certificate to be generated and sent',
      customPopupTitle:
        'Are you sure you wish to revert the case to generate a new Charge Certificate and update the balance to appropriate level',
    },
    revertToPostChargeCert: {
      title: 'Revert to Post Charge Cert',
      description:
        'Revert case to the stage following the issue of a Charge Certificate.',
      customPopupTitle:
        'Are you sure you wish to revert the case to following the issue of a Charge Certificate and update the balance to appropriate level',
    },
    plRevertToIssued: {
      title: 'Revert to Issued',
      description: 'Revert case to NTK Sent (Full Charge)',
      customPopupTitle:
        'Are you sure you wish to revert the case to primary stage to allow progression again from the point at the full charge amount',
      customEventDescription: 'Revert to Issued button invoked',
    },
    plRevertToSelectForFinalRemainder: {
      title: 'Revert to select for Final Remainder',
      description:
        'Revert case to NTK Sent (Full Charge) and schedule for Printing of Final Remainder',
      customPopupTitle:
        'Are you sure you wish to revert the case to sent a new Final Remainder letter and to allow progression again from the point at the full charge amount',
      customEventDescription:
        'Revert to select for Final Warning button invoked',
    },
    plRevertToFinalRemainderSent: {
      title: 'Revert to Final Remainder Sent',
      description: 'Revert case to Final Remainder sent',
      customPopupTitle:
        'Are you sure you wish to revert the case to secondary stage (Final Remainder Issued) to allow progression again from the point at the full charge amount',
      customEventDescription: 'Revert to Final Remainder Sent button invoked',
    },
    plGenerateNotice: {
      title: 'Generate Notice',
      description:
        'Revert following TOL Rep to send new Notice at discount amount (based on TOL type flag)',
      customPopupTitle:
        'Are you sure you wish to revert the case to resend the Notice',
      customEventDescription: 'Generate Notice button invoked',
    },
  },
  challengeButtonValue: {
    ChallengeSuccess: {
      title: 'Challenge Successful',
    },
    ChallengeUnSuccess: {
      title: 'Challenge Unsuccessful',
    },
    RepresentationSuccess: {
      title: 'Representation Successful',
    },
    RepresentationUnSuccess: {
      title: 'Representation Unsuccessful',
    },
    RejectRep: {
      title: 'Reject Rep. at CC',
    },
    AcceptRep: {
      title: 'Accept Rep. at CC',
    },
    IgnoreCC: {
      title: 'CC Ignore Representation',
    },
    IgnoreChallenge: {
      title: 'Ignore Challenge',
    },
    ChallengeRequestInfo: {
      title: 'Challenge Request Info',
      description: 'Challenge info requested',
    },
    IgnoreRepresentation: {
      title: 'Ignore Representation',
    },
    TOLRepresentationSuccessful: {
      title: 'TOL Representation Successful',
    },
    IgnoreOFRRep: {
      title: 'Ignore OFR Rep',
    },
    AcceptRepAtOFR: {
      title: 'Accept Rep at OFR',
    },
    RejectRepAtOFR: {
      title: 'Reject Rep at OFR',
    },
    AcceptTOLRepAtOFR: {
      title: 'Accept TOL Rep at OFR',
    },
    IgnoreWarrantRep: {
      title: 'Ignore Warrant Rep',
    },
    customEventDescription: 'Representation info requested',
    reviewAccepted: {
      title: 'Review Accepted',
      description: 'The review has been accepted so cancelling the case',
    },
    reviewRejected: {
      title: 'Review Rejected',
      description: 'The review has been rejected',
    },

    courtSuccess: {
      title: 'Court Successful',
      description: 'Court has been successful, case needs to be proceeded',
    },
    courtUnSuccess: {
      title: 'Court Unsuccessful',
      description: 'Court has been unsuccessful, case needs to be cancelled',
    },
    unCancelCase: {
      title: 'Un-cancel Case',
      description: 'Put the case into a non cancelled state',
    },
    reCloseFollowingPaid: {
      title: 'Re-close following Paid Correspondence',
      description:
        'Update status to Paid following receipt and review of correspondence along with or following a payment being made',
    },
    TOLatCCRepSuccessful: {
      title: 'TOL at CC Rep. Successful',
    },
    CCStageRequestInfo: {
      title: 'CC Stage Request Info',
      description:
        "Request information to be supplied, after the set period if there's no response or payment a workflow task will be created automatically",
    },
    reviewAcceptedTOLKeeper: {
      title: 'Review Accepted - TOL Keeper',
      description: 'Transfer of Liability to new Keeper details provided',
      customTitle:
        'Are you sure you wish to mark the case as reviewed and accept the offenders request to transfer liability and have a Notice to Keeper(NTK) generated',
    },
    reviewAcceptedTOLDriver: {
      title: 'Review Accepted - TOL Driver',
      description: 'Transfer of Liability to new Driver details provided',
      customTitle:
        'Are you sure you wish to mark the case as reviewed and accept the offenders request to transfer liability and have a Notice to Driver(NTD) generated',
    },
    reviewAcceptedTOLHirer: {
      title: 'Review Accepted - TOL Hirer',
      description: 'Transfer of Liability to new Hirer details provided',
      customTitle:
        'Are you sure you wish to mark the case as reviewed and accept the offenders request to transfer liability and have a Notice to Hirer(NTH) generated',
    },
  },
}

const reClosefollowingPaidCorrespondenceChallenge = {
  paidCorrespondence: 'Paid Correspondence',
  updatedSuccessfully: 'Updated successfully',
}

const stageAndStatus = {
  stage: {
    id: 'stageId',
    label: 'Change Stage',
    placeHolder: 'Select stage',
  },
  status: {
    id: 'statusId',
    label: 'Change Status',
    placeHolder: 'Select status',
  },
  cancellationReason: {
    id: 'cancellationReason',
    label: 'Choose Cancellation Reason',
    placeHolder: 'Select Cancellation Reason',
  },
}

const ContraventionFileUploadPage = {
  uploadCSV: 'Upload CSV',
  uploadSummary: 'Upload Summary',
  totalData: 'Total Data',
  uploadedData: 'Uploaded Data',
  skippedData: 'Skipped Data',
  errorFiles: 'Error Files: ',
  downloadSample: 'Download Sample',
  skippedDataReport: 'Skipped data report',
  sampleFileContravention: 'contraventionsample',
}

const pcnNumberReservationPage = {
  pcnNumberReservationHeading: 'Case number reservation',
  add: 'add',
  headerTitle: 'Reserve Case number',
  rangeValue: 'rangeValue',
  placeHolderEnterRange: 'Enter',
  step: 10,
  minRange: 0,
  maxRange: 200,
  rangeShouldNotExceedMoreThan: 'Range should not exceed more than 200',
  saveSuccessMessage: 'Case Number Reservation inserted successfully!',
  reservedPCNNumbersMailedSuccessfully:
    'Reserved Case Numbers mailed successfully!',
  processComplete:
    'Case Number reservation request initiated. Once completed, case number(s) will be reserved successfully.',
  addFailureMessage:
    'Case reservation limit of 200 for this CEO ID has been reached. Further reservations are not allowed.',
  confirmationUsed1: 'Please note, already there are ',
  confirmationUsed2:
    ' unused reserved case numbers available for this CEO ID. Do you wish to continue?',
  confirmationUnused1: 'Do you wish to reserve ',
  confirmationUnused2: ' Case Numbers for the CEO ID ',
  buttonTextField: {
    addNewRule: 'Add New Rule',
    cancel: 'Cancel',
    save: 'Save',
    reserve: 'Reserve',
    send: 'Send',
  },
  sendEmail: {
    headerTitle: 'Send Email',
    attachment: 'Attachment',
  },
}
const manualCase = {
  stepperName: {
    caseDetails: 'caseDetails',
    vehicleDetails: 'vehicleDetails',
    contraventionDetails: 'contraventionDetails',
    additionalInfo: 'additionalInfo',
    createdPcn: 'createdPcn',
  },
  contraventionDetails: {
    contravention: {
      id: 'contravention',
      label: 'Contravention',
      placeholder: 'Select Contravention',
    },
    observationStartTime: {
      id: 'observationStartTime',
      label: 'Observation Start Time',
      placeholder: 'Enter observation start time',
    },
    observationDuration: {
      id: 'observationDuration',
      label: 'Observation Duration',
      placeholder: 'Enter observation duration',
    },
    serviceMethod: {
      id: 'serviceMethod',
      label: 'Service Method',
      placeholder: 'Select service method',
    },
    wheelPositionOne: {
      id: 'wheelPositionOne',
      label: 'Wheel Position 1',
      placeholder: 'Select Wheel Position 1',
    },
    wheelPositionTwo: {
      id: 'wheelPositionTwo',
      label: 'Wheel Position 2',
      placeholder: 'Select Wheel Position 2',
    },
    returnWheelPositionOne: {
      id: 'returnWheelPositionOne',
      label: 'Return Wheel Position 1',
      placeholder: 'Select Wheel Position 1',
    },
    returnWheelPositionTwo: {
      id: 'returnWheelPositionTwo',
      label: 'Return Wheel Position 2',
      placeholder: 'Select Wheel Position 2',
    },
    allWindowsChecked: {
      id: 'allWindowsChecked',
      label: 'All Windows Checked',
    },
    dbs: {
      id: 'dbs',
      label: 'DBS',
      relativeField: 'ndbs',
    },
    ns: {
      id: 'ns',
      label: 'NS',
      relativeField: 'nn',
    },
    lo: {
      id: 'lo',
      label: 'LO',
      relativeField: 'nlo',
    },
    ds: {
      id: 'ds',
      label: 'DS',
      relativeField: 'dns',
    },
    ch: {
      id: 'ch',
      label: 'CH',
      relativeField: 'nc',
    },
    allInteriorChecked: {
      id: 'allInteriorChecked',
      label: 'All Interiors Checked',
    },
    ndbs: {
      id: 'ndbs',
      label: 'NDBS',
      relativeField: 'dbs',
    },
    nn: {
      id: 'nn',
      label: 'NN',
      relativeField: 'ns',
    },
    nlo: {
      id: 'nlo',
      label: 'NLO',
      relativeField: 'lo',
    },
    dns: {
      id: 'dns',
      label: 'DNS',
      relativeField: 'ds',
    },
    nc: {
      id: 'nc',
      label: 'NC',
      relativeField: 'ch',
    },
    warning: {
      id: 'warning',
      label: 'Warning',
    },
  },
  case_vehicle_details: {
    ceoId: {
      id: 'ceoId',
      label: 'CEO ID',
      placeholder: 'Select CEO ID',
      valueField: 'id',
      textField: 'badgeNumber',
    },
    caseNumber: {
      id: 'caseNumber',
      label: 'Case Number',
      placeholder: 'Select Case Number',
      valueField: 'caseNumber',
      textField: 'caseNumber',
    },
    contraventionDateTime: {
      id: 'contraventionDateTime',
      label: 'Date & Time',
      placeholder: 'Select Date & Time',
    },
    country: {
      id: 'country',
      label: 'Country',
      placeholder: 'Select Country',
      valueField: 'id',
      textField: 'country',
    },
    location: {
      id: 'location',
      label: 'Location',
      placeholder: 'Select Location',
      valueField: 'id',
      textField: 'name',
    },
    gps: {
      id: 'gps',
      label: 'GPS',
      placeholder: 'Enter GPS',
    },
    reason: {
      id: 'reason',
      label: 'Reason - Manual Case',
      placeholder: 'Select Reason',
      valueField: 'id',
      textField: 'manualCaseReason',
    },
    vrm: {
      id: 'vrm',
      label: 'VRM',
      placeholder: 'Enter VRM',
    },
    make: {
      id: 'make',
      label: 'Make',
      placeholder: 'Select make',
      valueField: 'id',
      textField: 'make',
    },
    color: {
      id: 'color',
      label: 'Color',
      placeholder: 'Select color',
      valueField: 'id',
      textField: 'color',
    },
    vehicleType: {
      id: 'vehicleTypeId',
      label: 'Vehicle Type',
      placeholder: 'Select type',
      valueField: 'id',
      textField: 'type',
    },
  },
  additionalInfo: {
    attachments: {
      title: 'Attachments',
      acceptedAttachmentText:
        '(Accepted file type: .png .pdf .jpg .bmp .tif .heic) | Max Size:10 mb per file',
      maximumFileSizExceed:
        'Error! File size too big, maximum allowed is 10 mb',
    },
    notes: {
      id: 'notes',
      label: 'Notes',
      placeholder: 'Enter Notes',
      inputLimit: 60000,
      alert: 'Maximum character limit is reached',
    },
  },
  manualCaseButtons: {
    cancel: {
      label: 'Cancel',
      variant: 'secondary',
      id: 'btn_cancel',
      className: 'cancelButton',
    },
    next: {
      label: 'Next',
      variant: 'primary',
      id: 'btn_next',
      className: 'nextButton',
    },
    back: {
      label: 'Back',
      variant: 'primary',
      id: 'btn_back',
      className: 'backButton',
    },
    createPcn: {
      label: 'Create PCN',
      variant: 'primary',
      id: 'btn_createPcn',
      className: 'createPcn',
    },
  },
  reportFault: {
    attachmentTitle: 'Attachment',
    attachmentFileReference: `(Accepted file type: .png .jpg .bmp .tif .heic)`,
    reportFaultSuccessTitle: 'Fault Reported Successfully!',
  },
}
/* istanbul ignore next */
const getDisplayTypeOfAttachment = (fileExtension) => {
  let fileNameExtension = fileExtension?.toString()?.toLowerCase()
  let displayType = ''
  switch (fileNameExtension) {
    case 'jpg':
    case 'jpeg':
    case 'bmp':
    case 'heic':
    case 'heif':
    case 'tif':
    case 'tiff':
    case 'png':
    case 'jfif':
      displayType = 'img'
      break
    case 'pdf':
      displayType = 'pdf'
      break
    case 'docx':
    case 'doc':
      displayType = 'doc'
      break
  }
  return displayType
}

const tecActions = {
  tecActions1: {
    prepareFourteenDayLetter: {
      title: 'Prepare 14 Days Letter',
      description: 'Generate 14 day TEC/Tribunal Letter to the customer',
    },
    fourteenDayLetterContest: {
      title: 'Prepare 14 Days Letter Contested',
      description:
        'RK has contested the case following the issue if a 14 days letter',
    },
  },
  tecActions2: {
    confirmationNoteReceived: {
      title: 'Confirmation Note Received',
      description: 'TEC confirmation note has been received',
    },
    revokeOrderReceived: {
      title: 'Revoke Order Received',
      description: 'TEC revoke order has been received',
    },
    cancelFollowingRevokeOrder: {
      title: 'Cancel Following Revoke Order',
      description: 'TEC revoke order received, case cancelled',
    },
    decisionNoteReceived: {
      title: 'Decision Note Received',
      description: 'TEC decision note has been received',
    },
  },
  tecActions3: {
    inTimeReceived: {
      title: 'In Time Received',
      description: 'In time witness statement received',
    },
    outTimeReceived: {
      title: 'Out Of Time Received',
      description: 'Out of time witness statement received',
    },
    inTimeLost: {
      title: 'In Time Lost',
      description: 'Witness statement outcome',
    },
    outTimeRejected: {
      title: 'Out Of Time Rejected',
      description: 'Out of Time witness statement rejected',
    },
    ootRejectedPostWarrant: {
      title: 'OOT Rejected Post Warrant',
      description:
        'Out of Time witness statement rejected after warrant issued',
      customEventDescription:
        'Out of time rejected post warrant button invoked',
      customPopupTitle:
        'Are you sure want to record the entry in NPS as OOT Rejected Post Warrant',
    },
  },
}

const location = {
  message: {
    confirmationWarningEndDateBelowSixMonth:
      'Are you sure want to set the Warning period at less than 6 months?',
    locationLimitReached: 'Input limit exceeds',
  },
}
const additionalTab = {
  locationDescription: 'Location Description',
  bodyWornVideo: 'Body Worn Video',
  observation: 'Observation',
  selectionToggles: 'Selection Toggles',
}

const anprOperationalReport = {
  pageTitle: 'ANPR Operational Report',
  anprOperational: {
    title: 'ANPR Operational',
    dataTestId: 'anprOperational',
  },
}

const monitoringReports = {
  monitoringDashboard: {
    title: 'Monitoring Dashboard',
    dataTestId: 'monitoringDashboard',
  },
  casesNotProgressed: {
    title: 'Cases Not Progressed',
    dataTestId: 'casesNotProgressed',
  },
  pcnForecast: {
    title: 'PCN Forecast',
    dataTestId: 'pcnForecast',
  },
  casesOnHoldAndNextProgression: {
    title: 'Held case progression',
    dataTestId: 'casesOnHoldAndNextProgression',
  },
  caseActiveDays: {
    title: 'Case Active Days',
    dataTestId: 'caseActiveDays',
  },
  printBatches: {
    title: 'Print Batches',
    dataTestId: 'printBatches',
  },
  bailiffBatches: {
    title: 'EA Batches',
    dataTestId: 'bailiffBatches',
  },
  HHDSync: {
    title: 'HHD Sync',
    dataTestId: 'HHDSync',
  },
  dvlaReport: {
    title: 'DVLA',
    dataTestId: 'dvla',
  },
  caseProgression: {
    title: 'Case Progression',
    dataTestId: 'caseProgression',
  },
  debtAndWarrant: {
    title: 'Debt Warrant',
    dataTestId: 'debtAndWarrant',
  },
  pageTitle: 'Monitoring Reports',
}
const hhdSourceType = 'HHD'
const manualCaseSourceType = 'Manual'
/* istanbul ignore next */
const challengeActionDisable = (
  stageKey,
  statusKey,
  sourceType,
  actionName,
  balanceAmount = 0,
  activeDays = null
) => {
  const challengeActionButtons = caseActionsStaticData?.challengeButtonValue
  statusKey = statusKey?.toString()?.toLowerCase()
  stageKey = stageKey?.toString()?.toLowerCase()
  if (
    actionName === challengeActionButtons?.ChallengeSuccess?.title ||
    actionName === challengeActionButtons?.ChallengeUnSuccess?.title ||
    actionName === challengeActionButtons?.IgnoreChallenge?.title
  ) {
    if (
      (stageKey === stageTextMap?.liveStage?.key?.toString().toLowerCase() ||
        stageKey === stageTextMap?.informal?.key?.toString().toLowerCase()) &&
      statusKey !== statusTextMap?.cancelled?.key?.toString().toLowerCase() &&
      statusKey !== statusTextMap?.warningSent?.key?.toString().toLowerCase() &&
      statusKey !== statusTextMap?.paid?.key?.toString().toLowerCase() &&
      statusKey !==
        statusTextMap?.manualDiscount?.key?.toString().toLowerCase() &&
      (sourceType === hhdSourceType?.toString().toLowerCase() ||
        sourceType === manualCaseSourceType?.toString()?.toLowerCase())
    )
      return false
    return true
  } else if (
    actionName === challengeActionButtons?.RepresentationSuccess?.title ||
    actionName === challengeActionButtons?.RepresentationUnSuccess?.title ||
    actionName === challengeActionButtons?.IgnoreRepresentation?.title ||
    actionName === challengeActionButtons?.TOLRepresentationSuccessful?.title
  ) {
    if (
      (stageKey === stageTextMap?.representation?.key?.toLowerCase() &&
        (statusKey === statusTextMap?.clientReviewed?.key?.toLowerCase() ||
          statusKey === statusTextMap?.onHold?.key?.toLowerCase())) ||
      ((stageKey === stageTextMap?.nto?.key?.toLowerCase() ||
        stageKey === stageTextMap?.postedPcn?.key?.toLowerCase()) &&
        statusKey === statusTextMap?.actioned?.key?.toLowerCase()) ||
      (stageKey === stageTextMap?.representationNor?.key?.toLowerCase() &&
        statusKey === statusTextMap?.onHold?.key?.toLowerCase())
    )
      return false
    return true
  } else if (
    actionName === challengeActionButtons?.TOLatCCRepSuccessful?.title
  ) {
    if (
      (stageKey === stageTextMap?.chargeCertificate?.key?.toLowerCase() &&
        statusKey === statusTextMap?.onHold?.key?.toLowerCase()) ||
      (stageKey === stageTextMap?.representation_cc?.key?.toLowerCase() &&
        statusKey === statusTextMap?.onHold?.key?.toLowerCase()) ||
      (stageKey === stageTextMap?.debtStage?.key?.toLowerCase() &&
        statusKey === statusTextMap?.active?.key?.toLowerCase())
    )
      return false
    return true
  } else if (
    actionName === challengeActionButtons?.AcceptRep?.title ||
    actionName === challengeActionButtons?.RejectRep?.title ||
    actionName === challengeActionButtons?.IgnoreCC?.title
  ) {
    if (
      (stageKey === stageTextMap?.chargeCertificate?.key?.toLowerCase() &&
        (statusKey === statusTextMap?.active?.key?.toLowerCase() ||
          statusKey === statusTextMap?.onHold?.key?.toLowerCase())) ||
      (stageKey === stageTextMap?.debtStage?.key?.toLowerCase() &&
        (statusKey === statusTextMap?.active?.key?.toLowerCase() ||
          statusKey === statusTextMap?.onHold?.key?.toLowerCase()))
    )
      return false
    return true
  } else if (actionName === challengeActionButtons?.IgnoreOFRRep?.title) {
    if (
      stageKey === stageTextMap?.ofr?.key?.toLowerCase() &&
      (statusKey === statusTextMap?.active?.key?.toLowerCase() ||
        statusKey === statusTextMap?.onHold?.key?.toLowerCase())
    )
      return false
    return true
  } else if (actionName === challengeActionButtons?.AcceptRepAtOFR?.title) {
    if (
      (stageKey === stageTextMap?.ofr?.key?.toLowerCase() ||
        stageKey === stageTextMap?.ofrExpired?.key?.toLowerCase()) &&
      statusKey === statusTextMap?.onHold?.key?.toLowerCase()
    )
      return false
    return true
  } else if (actionName === challengeActionButtons?.RejectRepAtOFR?.title) {
    if (
      (stageKey === stageTextMap?.ofr?.key?.toLowerCase() ||
        stageKey === stageTextMap?.ofrExpired?.key?.toLowerCase() ||
        stageKey === stageTextMap?.ofrRepresentation?.key?.toLowerCase()) &&
      statusKey === statusTextMap?.onHold?.key?.toLowerCase()
    )
      return false
    return true
  } else if (actionName === challengeActionButtons?.AcceptTOLRepAtOFR?.title) {
    if (
      (stageKey === stageTextMap?.ofr?.key?.toLowerCase() ||
        stageKey === stageTextMap?.ofrExpired?.key?.toLowerCase() ||
        stageKey === stageTextMap?.intimereceived?.key?.toLowerCase()) &&
      statusKey === statusTextMap?.onHold?.key?.toLowerCase()
    )
      return false
    return true
  } else if (actionName === challengeActionButtons?.IgnoreWarrantRep?.title) {
    if (
      stageKey === stageTextMap?.warrantExported?.key?.toLowerCase() &&
      statusKey === statusTextMap?.active?.key?.toLowerCase()
    )
      return false
    return true
  } else if (
    actionName === challengeActionButtons?.courtSuccess?.title ||
    actionName === challengeActionButtons?.courtUnSuccess?.title
  ) {
    if (
      stageKey === stageTextMap?.atCourt?.key?.toString()?.toLowerCase() &&
      statusKey !== statusTextMap?.paid?.key?.toString()?.toLowerCase() &&
      statusKey !== statusTextMap?.cancelled?.key?.toString()?.toLowerCase() &&
      statusKey !== statusTextMap?.warningSent?.key?.toString()?.toLowerCase()
    )
      return false
    return true
  } else if (actionName === challengeActionButtons?.unCancelCase?.title) {
    if (
      statusKey === statusTextMap?.cancelled?.key?.toString()?.toLowerCase() &&
      statusKey !== statusTextMap?.warningSent?.key?.toString().toLowerCase()
    )
      if (
        statusKey === statusTextMap?.cancelled?.key?.toString()?.toLowerCase()
      )
        return false
    return true
  } else if (
    actionName === challengeActionButtons?.reCloseFollowingPaid?.title
  ) {
    if (
      statusKey !== statusTextMap?.paid?.key?.toString()?.toLowerCase() &&
      balanceAmount === 0
    )
      return false
    return true
  } else if (actionName === challengeActionButtons?.reviewRejected?.title) {
    if (
      statusKey === statusTextMap?.onHold?.key?.toString()?.toLowerCase() &&
      statusKey !== statusTextMap?.paid?.key?.toString()?.toLowerCase() &&
      statusKey !== statusTextMap?.cancelled?.key?.toString()?.toLowerCase() &&
      statusKey !==
        statusTextMap?.warningSent?.key?.toString()?.toLowerCase() &&
      statusKey !==
        statusTextMap?.warningSent?.key?.toString()?.toLowerCase() &&
      statusKey !== statusTextMap?.warningSent?.key?.toString().toLowerCase() &&
      stageKey !==
        stageTextMap?.courtEligible?.key?.toString()?.toLowerCase() &&
      stageKey !== stageTextMap?.atCourt?.key?.toString()?.toLowerCase()
    )
      return false
    return true
  } else if (actionName === challengeActionButtons?.CCStageRequestInfo?.title) {
    if (
      (stageKey === stageTextMap?.chargeCertificate?.key?.toLowerCase() ||
        stageKey === stageTextMap?.debtStage?.key?.toLowerCase()) &&
      statusKey !== statusTextMap?.paid?.key?.toLowerCase() &&
      statusKey !== statusTextMap?.cancelled?.key?.toLowerCase() &&
      statusKey !== statusTextMap?.warningSent?.key?.toString()?.toLowerCase()
    )
      return false
    return true
  } else if (
    actionName === challengeActionButtons?.ChallengeRequestInfo?.title
  ) {
    if (
      (statusKey === statusTextMap?.onHold?.key?.toString()?.toLowerCase() ||
        statusKey ===
          statusTextMap?.infoRequested?.key?.toString()?.toLowerCase()) &&
      stageKey === stageTextMap?.informal?.key?.toString()?.toLowerCase()
    )
      return false
    return true
  } else if (
    actionName === challengeActionButtons?.reviewAccepted?.title &&
    statusKey === statusTextMap?.onHold?.key?.toString()?.toLowerCase()
  )
    return false
  return true
}

const isDisabledBasedOnCurrentStageStatus = (
  buttonTitle,
  existingStageKey,
  existingStatusKey,
  discountRepReject21DaysEnabled
) => {
  existingStageKey = existingStageKey?.toString()?.toLowerCase()
  existingStatusKey = existingStatusKey?.toString()?.toLowerCase()
  const liveStage = stageTextMap?.liveStage?.key?.toString()?.toLowerCase()
  const informalStage = stageTextMap?.informal?.key?.toString()?.toLowerCase()
  const informalNORStage = stageTextMap?.informalNor?.key
    ?.toString()
    ?.toLowerCase()
  const paidStage = statusTextMap?.paid?.key?.toString()?.toLowerCase()
  const cancelledStage = statusTextMap?.cancelled?.key
    ?.toString()
    ?.toLowerCase()
  const ntoStage = stageTextMap?.nto?.key?.toString()?.toLowerCase()
  const postedPCNStage = stageTextMap?.postedPcn?.key?.toString()?.toLowerCase()
  const representationStage = stageTextMap?.representation?.key
    ?.toString()
    ?.toLowerCase()
  const representationNORStage = stageTextMap?.representationNor?.key
    ?.toString()
    ?.toLowerCase()
  const requestedStage = statusTextMap?.requested?.key
    ?.toString()
    ?.toLowerCase()
  const debtRegistrationStage = stageTextMap?.debtRegistration?.key
    ?.toString()
    ?.toLowerCase()
  const debtStage = stageTextMap?.debtStage?.key?.toString()?.toLowerCase()
  const ofrStage = stageTextMap?.ofrStage?.key?.toString()?.toLowerCase()
  const orfExpiredStage = stageTextMap?.ofrExpired?.key
    ?.toString()
    ?.toLowerCase()
  const atCourtStage = stageTextMap?.atCourt?.key?.toString()?.toLowerCase()
  const chargeCertificateStage = stageTextMap?.chargeCertificate?.key
    ?.toString()
    ?.toLowerCase()
  const ccRepresentationStage = stageTextMap?.representation_cc?.key
    ?.toString()
    ?.toLowerCase()
  const activeStatus = statusTextMap?.active?.key?.toString()?.toLowerCase()
  const onHoldStatus = statusTextMap?.onHold?.key?.toString()?.toLowerCase()
  const warningSentStatus = statusTextMap?.warningSent?.key
    ?.toString()
    ?.toLowerCase()
  const paymentPlanStatus = statusTextMap?.paymentPlan?.key
    ?.toString()
    ?.toLowerCase()

  if (buttonTitle === discountButtonValue?.discount?.title) {
    if (
      (existingStageKey === liveStage ||
        existingStageKey === informalStage ||
        existingStageKey === informalNORStage) &&
      existingStatusKey !== paidStage &&
      existingStatusKey !== cancelledStage &&
      existingStatusKey !== warningSentStatus
    )
      return false
    return true
  } else if (buttonTitle === discountButtonValue?.discountRepReject?.title) {
    if (
      (existingStageKey === ntoStage ||
        existingStageKey === postedPCNStage ||
        existingStageKey === representationStage ||
        existingStageKey === representationNORStage) &&
      existingStatusKey !== paidStage &&
      existingStatusKey !== cancelledStage &&
      existingStatusKey !== warningSentStatus &&
      existingStatusKey !== requestedStage
    )
      return false
    return true
  } else if (buttonTitle === discountButtonValue?.discountCCRepReject?.title) {
    if (
      (existingStageKey === chargeCertificateStage ||
        existingStageKey === debtStage) &&
      existingStatusKey !== paidStage &&
      existingStatusKey !== cancelledStage &&
      existingStatusKey !== warningSentStatus
    )
      return false
    return true
  } else if (buttonTitle === discountButtonValue?.discountOFRRepReject?.title) {
    if (
      (existingStageKey === debtRegistrationStage ||
        existingStageKey === ofrStage ||
        existingStageKey === orfExpiredStage) &&
      existingStatusKey !== paidStage &&
      existingStatusKey !== cancelledStage &&
      existingStatusKey !== warningSentStatus
    )
      return false
    return true
  } else if (
    buttonTitle == discountButtonValue?.reverttoIssuedDiscount?.title
  ) {
    if (
      existingStageKey !== atCourtStage &&
      existingStatusKey !== cancelledStage &&
      existingStatusKey !== warningSentStatus &&
      existingStatusKey !== paidStage &&
      existingStageKey !== liveStage
    )
      return false
    return true
  } else if (
    buttonTitle === discountButtonValue?.reverttoFullChargeAtCC?.title
  ) {
    if (
      (existingStageKey === chargeCertificateStage &&
        (existingStatusKey === activeStatus ||
          existingStatusKey === onHoldStatus)) ||
      (existingStageKey === ccRepresentationStage &&
        (existingStatusKey === activeStatus ||
          existingStatusKey === onHoldStatus))
    ) {
      return false
    }
    return true
  } else if (
    buttonTitle === discountButtonValue?.discountRepReject21Days?.title
  ) {
    if (
      discountRepReject21DaysEnabled &&
      (existingStageKey === ntoStage ||
        existingStageKey === postedPCNStage ||
        existingStageKey === representationStage ||
        existingStageKey === representationNORStage) &&
      existingStatusKey !== paidStage &&
      existingStatusKey !== cancelledStage &&
      existingStatusKey !== warningSentStatus &&
      existingStatusKey !== requestedStage
    ) {
      return false
    }
    return true
  } else if (
    buttonTitle === discountButtonValue?.revertToIssuedDiscountPL?.title
  ) {
    const atBailiffStageKey = stageTextMap?.atBailiffStage?.key
      ?.toString()
      ?.toLowerCase()
    if (
      existingStageKey !== atBailiffStageKey &&
      existingStageKey !== liveStage &&
      existingStatusKey !== paidStage &&
      existingStatusKey !== cancelledStage
    )
      return false
    return true
  } else if (buttonTitle === discountButtonValue?.revertToDiscountAtCC?.title) {
    if (
      existingStageKey === chargeCertificateStage &&
      existingStatusKey !== paidStage &&
      existingStatusKey !== cancelledStage &&
      existingStatusKey !== warningSentStatus &&
      existingStatusKey !== paymentPlanStatus
    ) {
      return false
    }
    return true
  } else if (
    buttonTitle === tecActions?.tecActions3?.ootRejectedPostWarrant?.title
  ) {
    const outimereceivedStage = stageTextMap?.outimereceived?.key
      ?.toString()
      ?.toLowerCase()
    const rejectedStatus = statusTextMap?.rejected?.key
      ?.toString()
      ?.toLowerCase()
    if (
      existingStageKey === outimereceivedStage &&
      (existingStatusKey === onHoldStatus ||
        existingStatusKey === activeStatus ||
        existingStatusKey === rejectedStatus) &&
      existingStatusKey !== paidStage &&
      existingStatusKey !== cancelledStage
    ) {
      return false
    } else if (
      buttonTitle === tecActions?.tecActions3?.ootRejectedPostWarrant?.title
    ) {
      const outimereceivedStage = stageTextMap?.outimereceived?.key
        ?.toString()
        ?.toLowerCase()
      const rejectedStatus = statusTextMap?.rejected?.key
        ?.toString()
        ?.toLowerCase()
      if (
        existingStageKey === outimereceivedStage &&
        (existingStatusKey === onHoldStatus ||
          existingStatusKey === activeStatus ||
          existingStatusKey === rejectedStatus) &&
        existingStatusKey !== paidStage &&
        existingStatusKey !== cancelledStage
      ) {
        return false
      }
      return true
    }
    return true
  }
}

const prefixSetupPage = {
  placeholder2: 'Enter 2 character prefix',
  placeholder3: 'Enter 3 character prefix',
  createdSuccessfully: 'Created Successfully',
  updatedSuccessfully: 'Updated Successfully',
  enterthetwocharacterprefixhere: 'Please enter two character prefix.',
  enterthethreecharacterprefixhere: 'Please enter three character prefix.',
  warningMessage:
    'Please note this template will create an attachment on the case but a letter will not be sent to the customer automatically.',
}
const twoPrefixPCN = {
  pcnLength: 10,
  prefixCount: 2, // when length is 10 and prefix is 2
  digitCount: 7, // when length is 10 and prefix is 2 then number length should be 6
  suffixCount: 1,
}
const threePrefixPCN = {
  pcnLength: 11,
  prefixCount: 3, // when length is 11 and prefix is 3
  digitCount: 7, // when length is 11 and prefix is 3 then number length should be 7
  suffixCount: 1,
}
// This is to validate the given caseNumber is valid format of PCN or not
const isValidPcnNumber = (caseNumber) => {
  let pcnNumber = caseNumber?.toString()?.trim()
  let pcnNumberRegExString = ``
  let regExOccurence = { ...twoPrefixPCN } // considered 2 prefix is default pcnFormat

  if (pcnNumber?.length === threePrefixPCN?.pcnLength) {
    regExOccurence = { ...threePrefixPCN }
  }
  pcnNumberRegExString = `^[a-zA-Z]{${regExOccurence?.prefixCount}}\\d{${regExOccurence?.digitCount}}[a-zA-Z0-9]{${regExOccurence?.suffixCount}}$`
  const pcnNumberRegEx = new RegExp(pcnNumberRegExString)
  return pcnNumberRegEx?.test(pcnNumber)
}
const taskPreview = {
  attachmentTitle: 'Images',
  acceptedFiles: `(Accepted file types: .tiff .bmp .jpg .jpeg .png .heic) | Max.Size: 10 mb per file.`,
  note: 'Notes',
}

const dvlaMismatchStaticData = {
  dvlaHeaderTitle: 'DVLA Response',
  npsHeaderTitle: 'NPS Data Mismatch',
  btnTextProgress: 'Progress',
  btnTextCancelCase: 'Cancel Case',
  Source: 'DVLA',
}
const fpnBasedChanges = {
  offender: 'Offender',
  keeper: 'Keeper',
  fpnRadioBoxWidth: 100,
  globalSearch: 'Quick Search',
  invalidGlobalSearch: 'Input should not be empty.',
  cancelCase: 'Cancel Case',
  cancelCaseConfirmation: 'Are you sure want to cancel the Case?',
  holdCase: 'Hold Case',
  offHoldCase: 'Off Hold Case',
  locationDescription: 'Location Description',
  pcnDescription: 'Description',
  pcnLocation: 'Location',
  pcnContravention: 'Code',
  pcnOperationalReport: 'Operational Internal',
  pcnActiveDays: ['servedinpost', 'servedinperson'], // wont be used as created new data source in ActivityDaysList.js ruleDataConfigurationBasedOnContractType
  fpnDescription: 'Contravention Description',
  fpnLocation: 'Offence Location',
  fpnContravention: 'Contravention Code',
  fpnAvailable: 'Available',
  fpnUnAvailable: 'Unavailable',
  fpnNoLinkedCase: 'No linked cases',
  fpnContraventionCode: 'Contravention Code',
  fpnOperationalReport: 'Case Status',
}
const preCaseChanges = {
  preCaseRadioBoxWidth: 200,
}

const mismatchProgressWarnMessage = 'Are you sure want to progress this case?'
const cancelCaseWarnMessage =
  'Cancelling this case will stop the case progression. Are you sure want to cancel?'

const changeBasedOnTheFPNContract = (
  oldChange,
  fpnWordChange,
  contractName
) => {
  return contractName === 'fpn' ? fpnWordChange : oldChange
}
const buttonDisabledValidation = (currentStage, currentStatus, actionName) => {
  const caseStatus = currentStatus?.toString()?.toLowerCase()
  const caseStage = currentStage?.toString()?.toLowerCase()
  const atCourtStage = stageTextMap?.atCourt?.key?.toString()?.toLowerCase()
  const liveStage = stageTextMap?.liveStage?.key?.toString()?.toLowerCase()
  const paidStatus = statusTextMap?.paid?.key?.toString()?.toLowerCase()
  const finalWarning = stageTextMap?.finalWarning?.key
    ?.toString()
    ?.toLowerCase()
  const courtEligiblestage = stageTextMap?.courtEligible?.key
    ?.toString()
    ?.toLowerCase()
  const onHoldStatus = statusTextMap?.onHold?.key?.toString()?.toLowerCase()
  const cancelledStatus = statusTextMap?.cancelled?.key
    ?.toString()
    ?.toLowerCase()
  const chargeCertEligibleStages = [
    stageTextMap?.chargeCertificate?.key,
    stageTextMap?.debtStage?.key,
    stageTextMap?.intimereceived?.key,
    stageTextMap?.outimereceived?.key,
    stageTextMap?.tribunal?.key,
  ]
    .map((stage) => stage?.toString()?.toLowerCase())
    .filter(Boolean)

  if (
    actionName ===
    caseActionsStaticData?.reversalActionsButtonValue?.revertIssued?.title
  ) {
    if (
      caseStage !== atCourtStage &&
      caseStage !== liveStage &&
      caseStatus !== paidStatus
    )
      return false
    return true
  } else if (
    actionName ===
    caseActionsStaticData?.reversalActionsButtonValue?.revertToFinalWarningSent
      ?.title
  ) {
    if (
      caseStage !== atCourtStage &&
      caseStatus !== paidStatus &&
      caseStage !== liveStage &&
      caseStage !== finalWarning
    )
      return false
    return true
  } else if (
    actionName ===
    caseActionsStaticData?.reversalActionsButtonValue
      ?.revertToSelectFinalWarning?.title
  ) {
    if (
      caseStage !== atCourtStage &&
      caseStatus !== paidStatus &&
      caseStage !== liveStage
    )
      return false
    return true
  } else if (
    actionName ===
    caseActionsStaticData?.reversalActionsButtonValue?.revertToAtCourt?.title
  ) {
    if (caseStage === courtEligiblestage && caseStatus !== paidStatus)
      return false
    return true
  } else if (
    actionName ===
    caseActionsStaticData?.reversalActionsButtonValue?.revertToCourtEligible
      ?.title
  ) {
    if (caseStage === atCourtStage && caseStatus !== paidStatus) return false
    return true
  } else if (
    actionName ===
    caseActionsStaticData?.reversalActionsButtonValue?.revertToSelectForNIP
      ?.title
  ) {
    if (caseStage !== atCourtStage && caseStatus !== paidStatus) {
      return false
    }
    return true
  } else if (
    actionName ===
    caseActionsStaticData?.reversalActionsButtonValue?.revertToNIPSent?.title
  ) {
    if (caseStage !== atCourtStage && caseStatus !== paidStatus) {
      return false
    }
    return true
  } else if (
    actionName ===
    caseActionsStaticData?.reversalActionsButtonValue
      ?.revertToSelectForNIPFinalWarning?.title
  ) {
    if (caseStage !== atCourtStage && caseStatus !== paidStatus) {
      return false
    }
    return true
  } else if (
    actionName ===
    caseActionsStaticData?.reversalActionsButtonValue
      ?.revertToNIPFinalWarningSent?.title
  ) {
    if (caseStage !== atCourtStage && caseStatus !== paidStatus) {
      return false
    }
    return true
  } else if (
    actionName ===
      caseActionsStaticData?.reversalActionsButtonValue
        ?.revertToSelectForChargeCert?.title ||
    actionName ===
      caseActionsStaticData?.reversalActionsButtonValue?.revertToPostChargeCert
        ?.title
  ) {
    if (
      chargeCertEligibleStages.includes(caseStage) &&
      caseStatus !== cancelledStatus &&
      caseStatus !== paidStatus
    ) {
      return false
    }
    return true
  }
}

const cancellationReasonPopup = {
  crCancellationReasonSummaryHeading: 'Summary',
  crCancellationReasonAddSuccessMessage:
    'Cancellation reason added successfully!',
  cancelPopupConfirmation: 'Are you sure want to cancel?',
}
const cancellationReasonList = {
  cancellationReasonTitle: 'Cancellation Reason',
  uploadCsv: {
    uploadCsv: 'Upload CSV',
  },
  addCancellationButton: {
    fieldValue: 'AddCancellationReason',
    fieldLabel: 'Add Cancellation Reason',
    dataTestId: 'add_cancellationReason',
  },
  exportButton: {
    export: 'Export',
    className: 'exportCancellationReasonButton',
    dataTestId: 'export_cancellationReason',
    startIconClassName: 'exportCancellationReason',
  },
  reasonLimitReach: 'Should not exceed more than 5000 character',
  deleteConfirmationMessage:
    'Once this data is deleted then same can’t be retrieved. Are you sure to delete?',
}

const associatedPCNList = {
  exportButton: {
    export: { id: 'exportCR', text: 'Download' },
    className: 'exportCancellationReasonButton',
    dataTestId: 'export_associatedPcn',
    startIconClassName: 'exportCancellationReason',
  },
}

const poundLocationList = {
  poundLoationTitle: 'Pound Location',
  addPoundLocationButton: {
    fieldValue: 'addPoundLocation',
    fieldLabel: 'Add Pound Location',
    dataTestId: 'add_PoundLocation',
  },
  poundLocationNameLimitReach: 'Should not exceed more than 500 character',
  poundLocationIDLimitReach: 'Should not exceed more than 5 character',
  deleteConfirmationMessage:
    'Once this data is deleted then same can’t be retrieved. Are you sure to delete?',
  cancelPopupConfirmation: 'Are you sure want to cancel?',
  selectOrganisation: 'Please select organisation',
  belowExistingBayCount:
    'Maximum bay count should be greater than the current highest used value',
  summaryPopup: {
    confirmationButton: 'Confirm',
    editButton: 'Edit',
    modifyButton: 'Modify',
  },
  bayCountLimitError: 'Bay count should be lesser than 500',
  crPoundLocationAddSuccessMessage: 'Pound Location added successfully!',
  locationIDlength: 5,
  locationNameLenght: 500,
  bayCountMinimalErrorOnEdit:
    'The new value cannot be less than the current value. Please input a higher value',
}
const prioritySettingsList = {
  prioritySettingsTitle: 'Priority Settings',
}
const isThisDropdownFieldValid = (fieldData) =>
  fieldData !== null && fieldData !== 0 && fieldData !== undefined

const Base64ToPdfDownLoad = async (pdfContentBase64, fileName) => {
  function base64ToBlob(base64, mimeType) {
    const binaryString = atob(base64)
    const len = binaryString?.length
    const binaryArray = new Uint8Array(len)
    for (let i = 0; i < len; i++) {
      binaryArray[i] = binaryString?.charCodeAt(i)
    }
    return new Blob([binaryArray], { type: mimeType })
  }
  // Create Blob from base64 and download it
  const blob = base64ToBlob(pdfContentBase64, 'application/pdf')
  const url = URL.createObjectURL(blob)
  // Create a link and trigger the download
  const link = document.createElement('a')
  link.href = url
  link.download = `${fileName}.pdf`
  document.body.appendChild(link)
  link.click()
  // Clean up
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
const deleteConfirmationValues = {
  deleteConfirmationText: 'Are you sure want to delete?',
  deleteButtonText: 'Yes',
  cancelButtonText: 'No',
}
/* istanbul ignore next */

const encryptPasscode = (text) => {
  const encrptKey = process.env.PasscodeKey
  // Generate a random IV (Initialization Vector)
  const iv = CryptoJS.lib.WordArray.random(16)
  // Convert the key to a WordArray
  const keyBytes = CryptoJS.enc.Utf8.parse(encrptKey)
  // Encrypt using AES-256 CBC algorithm
  const encrypted = CryptoJS.AES.encrypt(text, keyBytes, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  })
  // Concatenate the IV and the ciphertext (encrypted text)  //
  const encryptedText = iv
    .concat(encrypted.ciphertext)
    .toString(CryptoJS.enc.Base64)

  return encryptedText
}
/* istanbul ignore next */
const decryptPasscode = (encryptedText) => {
  const encrptKey = process.env.PasscodeKey
  const keyBytes = CryptoJS.enc.Utf8.parse(encrptKey)
  const dataBytes = CryptoJS.enc.Base64.parse(encryptedText)
  const ivs = CryptoJS.lib.WordArray.create(dataBytes.words.slice(0, 4))
  const ciphertext = CryptoJS.lib.WordArray.create(dataBytes.words.slice(4))

  const decryptedBytes = CryptoJS.AES.decrypt({ ciphertext }, keyBytes, {
    iv: ivs,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  })
  /* istanbul ignore next */
  const decryptedText = CryptoJS?.enc?.Utf8?.stringify(decryptedBytes)
  return decryptedText
}
/* istanbul ignore next */
function generateRandomNumbers() {
  return Math.floor(100000 + Math.random() * 900000)
}

// Remove after verify with HHD
// const jobAssignmentType = {
//   Assign: 1,
//   Reassign: 2,
//   Unassign: 3,
//   cancelled: 4,
// }

const pushNotificationjobAssignmentType = {
  assign: 1,
  reAssign: 2,
  cancel: 3,
}

const signalRNotificationMethods = { sendJobMessage: 'SendJobMessageToOBCEO' }

const hotspotSource = {
  optionLabel: 'Hotspot',
  optionValue: 'isHotspotEnabled',
  optionDataTestId: 'isHotspotEnabled',
}
const airaNames = { airaName: 'AIRA', nonNPS: 'nonnps' }

const watchCaseTabIndexOnCaseOverview = 2

const encryptData = (data) => {
  return data !== null && data !== undefined ? btoa(data?.toString()) : null
}

const decryptData = (data) => {
  return data !== null && data !== undefined ? atob(data?.toString()) : null
}

const reportFlagKeys = [
  { reportName: 'Case Status', flagKey: 'isCaseStatusReportEnabled' },
  {
    reportName: 'Outbound Correspondence',
    flagKey: 'isOutboundCorrespondenceReportEnabled',
  },
  {
    reportName: 'Inbound Correspondence',
    flagKey: 'isInboundCorrespondenceReportEnabled',
  },
  {
    reportName: 'Enforcement Team',
    flagKey: 'isEnforcementTeamReportEnabled',
  },
  { reportName: 'Financial', flagKey: 'isFinancialReportEnabled' },
  { reportName: 'Audit & Tracking', flagKey: 'isAuditTrackingReportEnabled' },
  { reportName: 'Monitoring', flagKey: 'isMonitoringReportEnabled' },
  {
    reportName: 'Performance Monitoring',
    flagKey: 'isPerformanceMonitoringReportEnabled',
  },
  { reportName: 'Approval Queue', flagKey: 'isApprovalQueueReportEnabled' },
  { reportName: 'Cancellation', flagKey: 'isCancellationReportEnabled' },
  { reportName: 'CCTV Overview', flagKey: 'isCctvOverviewReportEnabled' },
  { reportName: 'Manual Discounts', flagKey: 'isManualDiscountsReportEnabled' },
  { reportName: 'HHD Overview', flagKey: 'isHhdOverviewReportEnabled' },
  { reportName: 'Hold Detail', flagKey: 'isHoldDetailReportEnabled' },
  { reportName: 'Case Type', flagKey: 'isCaseTypeReportEnabled' },
  { reportName: 'All Cases', flagKey: 'isAllCasesReportEnabled' },
  { reportName: 'Corres. Print', flagKey: 'isCorresPrintReportEnabled' },
  { reportName: 'Letter Produced', flagKey: 'isLetterProducedReportEnabled' },
  { reportName: 'Letters Sent', flagKey: 'isLettersSentReportEnabled' },
  { reportName: 'Print Overview', flagKey: 'isPrintOverviewReportEnabled' },
  { reportName: 'Open Workflow', flagKey: 'isOpenWorkflowReportEnabled' },
  { reportName: 'Parseq Scans', flagKey: 'isParseqScansReportEnabled' },
  {
    reportName: 'Challenge and Rep Outcomes',
    flagKey: 'isChallengeAndRepOutcomesReportEnabled',
  },
  {
    reportName: 'CEO Activity Detail',
    flagKey: 'isCeoActivityDetailReportEnabled',
  },
  { reportName: 'CEO Heatmap', flagKey: 'isCeoHeatmapReportEnabled' },
  {
    reportName: 'Patrolled CEO Hours',
    flagKey: 'isPatrolledCeoHoursReportEnabled',
  },
  { reportName: 'CEO Tracking', flagKey: 'isCeoTrackingReportEnabled' },
  {
    reportName: 'On Street Activities',
    flagKey: 'isOnStreetActivitiesReportEnabled',
  },
  { reportName: 'On Street Log', flagKey: 'isOnStreetLogReportEnabled' },
  {
    reportName: 'Officer Monitoring',
    flagKey: 'isOfficerMonitoringReportEnabled',
  },
  { reportName: 'Hotspot', flagKey: 'isHotspotReportEnabled' },
  { reportName: 'Payment', flagKey: 'isPaymentReportEnabled' },
  {
    reportName: 'Refund & Charge Back',
    flagKey: 'isRefundAndChargeBackReportEnabled',
  },
  {
    reportName: 'Revenue By Location',
    flagKey: 'isRevenueByLocationReportEnabled',
  },
  { reportName: 'Revenue Report', flagKey: 'isRevenueReportReportEnabled' },
  { reportName: 'Transfer', flagKey: 'isTransferReportEnabled' },
  { reportName: "PCN's and Revenue", flagKey: 'isPcnsAndRevenueReportEnabled' },
  { reportName: 'DFT', flagKey: 'isDftReportEnabled' },
  { reportName: 'Overall Summary', flagKey: 'isOverallSummaryReportEnabled' },
  { reportName: 'PE Report', flagKey: 'isPeReportReportEnabled' },
  { reportName: 'Prefix Audit', flagKey: 'isPrefixAuditReportEnabled' },
  { reportName: 'User Activity', flagKey: 'isUserActivityReportEnabled' },
  { reportName: 'PCNs by Type', flagKey: 'isPcnByTypeReportEnabled' },
  { reportName: 'Contract Stats', flagKey: 'isContractStatsReportEnabled' },
  { reportName: 'PE At EA', flagKey: 'isPeAtEaReportEnabled' },
  { reportName: 'Burndown', flagKey: 'isBurndownReportEnabled' },
  { reportName: 'EA Batches', flagKey: 'isEaBatchesReportEnabled' },
  { reportName: 'Case Active Days', flagKey: 'isCaseActiveDaysReportEnabled' },
  {
    reportName: 'Cases Not Progressed',
    flagKey: 'isCasesNotProgressedReportEnabled',
  },
  {
    reportName: 'Held Case Progression',
    flagKey: 'isHeldCaseProgressionReportEnabled',
  },
  {
    reportName: 'Monitoring Dashboard',
    flagKey: 'isMonitoringDashboardReportEnabled',
  },
  { reportName: 'PCN Forecast', flagKey: 'isPcnForecastReportEnabled' },
  { reportName: 'Print Batches', flagKey: 'isPrintBatchesReportEnabled' },
  { reportName: 'HHD Sync', flagKey: 'isHHDSyncReportEnabled' },
  { reportName: 'Back Officers', flagKey: 'isBackOfficersReportEnabled' },
  {
    reportName: 'Enforcement Officers',
    flagKey: 'isEnforcementOfficersReportEnabled',
  },
  {
    reportName: 'Invoicing Data',
    flagKey: 'isInvoicingDataReportEnabled',
  },
  { reportName: 'Time Pay', flagKey: 'isTimePayEnabled' },
  {
    reportName: 'Monthly Review Report',
    flagKey: 'isMonthlyReviewReportEnabled',
  },
  { reportName: 'Permission Checks', flagKey: 'isPermissionCheckEnabled' },
  { reportName: 'Accounting', flagKey: 'isAccountingReportEnabled' },
  { reportName: 'All CCTV Cases', flagKey: 'isAllCctvCasesReportEnabled' },
  { reportName: 'DVLA', flagKey: 'isDVLAEnabled' },
  {
    reportName: 'School Report',
    flagKey: 'isSchoolReportEnabled',
  },
  {
    reportName: 'School Enforcement Report',
    flagKey: 'isSchoolEnforcementReportEnabled',
  },
  {
    reportName: 'Inbound Emails',
    flagKey: 'isInboundEmailsEnabled',
  },
  {
    reportName: 'FPN Overview',
    flagKey: 'isFpnOverviewReportEnabled',
  },
  {
    reportName: 'Case Keeper Details',
    flagKey: 'isCaseKeeperDetailsReportEnabled',
  },
  {
    reportName: 'LG KPI Figures',
    flagKey: 'isLGKPIFiguresReportEnabled',
  },
  {
    reportName: 'On Street Activity Report Extended',
    flagKey: 'isOnStreetActivityReportExtendedEnabled',
  },
  {
    reportName: 'Observations',
    flagKey: 'isObservationsReportEnabled',
  },
  {
    reportName: 'Case Progression',
    flagKey: 'isCaseProgressionReportEnabled',
  },
  { reportName: 'Faults Report', flagKey: 'isFaultsReportEnabled' },
  { reportName: 'Officer Breaks', flagKey: 'isOfficerBreaksReportEnabled' },
  {
    reportName: 'Abandoned Vehicles',
    flagKey: 'isAbandonedVehiclesReportEnabled',
  },
  {
    reportName: 'Debt Warrant',
    flagKey: 'isDebtAndWarrantReportEnabled',
  },
]
const fpnReportFilterOptions = [
  {
    reportMenuId: 1,
    reportMenu: 'Case Status',
    subMenu: [
      {
        subReportId: 1,
        reportName: 'Approval Queue',
      },
      {
        subReportId: 4,
        reportName: 'Cancellation',
      },
      {
        subReportId: 18,
        reportName: 'Hold Detail',
      },
      {
        subReportId: 58,
        reportName: 'FPN Overview',
      },
      // {
      //   subReportId: 59,
      //   reportName: 'Case Keeper Details',
      // },
    ],
  },
]
// generaytes alpha-numeric password with symbols of length 8 to 20 (random)
const generateRandomPassword = () => {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const lower = 'abcdefghijklmnopqrstuvwxyz'
  const digits = '0123456789'
  const symbols = '!@#$%^&*()_+[]{}|;:,.<>?'
  const allChars = upper + lower + digits + symbols

  // Random length between 8 and 20
  const length = Math?.floor(Math?.random() * (20 - 8 + 1)) + 8
  const getRandom = (set) => set[Math?.floor(Math?.random() * set.length)]

  let password = [
    getRandom(upper),
    getRandom(lower),
    getRandom(digits),
    getRandom(symbols),
  ]
  for (let i = password.length; i < length; i++) {
    password?.push(getRandom(allChars))
  }
  // Shuffle the password characters
  password = password?.sort(() => 0.5 - Math.random())
  return password?.join('')
}

// generaytes alpha-numeric password with symbols of length 8 to 20 (random)

const getHashedData = (data) => {
  return CryptoJS.SHA1(data).toString()
}

const menuItemDatasourceBase = {
  home: {
    displayText: 'Home',
    redirectURL: '/',
    imageURLactive: homeBlue,
    imageURLinactive: homeGrey,
    roles: [...menuAccessRoles?.home],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  configuration: {
    displayText: 'Configuration',
    imageURLactive: configurationBlue,
    imageURLinactive: configurationGrey,
    roles: [...menuAccessRoles?.configuration],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    groupItems: [
      'users',
      'templates',
      'zone',
      'location',
      'cpzList',
      'vehicleType',
      'contraventionCodes',
      'faq',
      'cancellationReasons',
      'rejectReasons',
      'crRepGrounds',
      'holdReasons',
      'geoFencingSetup',
      'reportConfiguration',
      'nonWorkingDays',
    ],
  },
  printAdmin: {
    displayText: 'Print Admin',
    redirectURL: '/printadmin',
    imageURLactive: printAdminBlue,
    imageURLinactive: printAdminGrey,
    roles: [...(menuAccessRoles?.printAdmin || [])],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  caseLock: {
    displayText: 'Case Lock',
    redirectURL: '/caselock',
    imageURLactive: caseLockBlue,
    imageURLinactive: caseLockGrey,
    roles: [...(menuAccessRoles?.caseLock || [])],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  clientSetup: {
    displayText: 'Client Setup',
    imageURLactive: clientSetupBlue,
    imageURLinactive: clientSetupGrey,
    roles: [...menuAccessRoles?.clientSetup],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    groupItems: [
      'organisation',
      'contract',
      'branding',
      'prefix',
      'bailiffs',
      'purge',
      'integration',
    ],
  },
  organisation: {
    displayText: 'Organisation',
    redirectURL: '/organisationlist',
    roles: [...menuAccessRoles?.clientSetup],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  contract: {
    displayText: 'Contract',
    redirectURL: '/contractListPage',
    roles: [...menuAccessRoles?.clientSetup],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  branding: {
    displayText: 'Branding',
    redirectURL: '/brandinglistpage',
    roles: [...menuAccessRoles?.clientSetup],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  prefix: {
    displayText: 'Prefix',
    redirectURL: '/prefix',
    roles: [...menuAccessRoles?.clientSetup],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  bailiffs: {
    displayText: 'Enforcement Agency',
    redirectURL: '/bailiff',
    roles: [...menuAccessRoles?.clientSetup],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  purge: {
    displayText: 'Purge',
    redirectURL: '/purge',
    roles: [...menuAccessRoles?.clientSetup],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  integration: {
    displayText: 'Integration',
    redirectURL: '/integration',
    roles: [...menuAccessRoles?.clientSetup],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  crConfiguration: {
    displayText: `CR Configuration`,
    imageURLactive: CR_ConfigBlue,
    imageURLinactive: CR_ConfigGrey,
    roles: [...menuAccessRoles?.crConfiguration],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    groupItems: [
      'poundLocation',
      'crCancellationReasons',
      'prioritySettings',
      'truck',
      'charge',
      'releaseReason',
    ],
  },
  poundLocation: {
    displayText: 'Pound Location',
    redirectURL: '/poundlocation',
    roles: [...menuAccessRoles?.crConfiguration],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  crCancellationReasons: {
    displayText: 'Cancellation Reasons',
    redirectURL: '/cancellationreasonlist',
    roles: [...menuAccessRoles?.crConfiguration],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  prioritySettings: {
    displayText: 'Priority Settings',
    redirectURL: '/prioritysettingslist',
    roles: [...menuAccessRoles?.crConfiguration],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  truck: {
    displayText: 'Truck',
    redirectURL: '/trucklist',
    roles: [...menuAccessRoles?.crConfiguration],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  charge: {
    displayText: 'Charge',
    redirectURL: '/chargelist',
    roles: [...menuAccessRoles?.crConfiguration],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  releaseReason: {
    displayText: 'Release Reasons',
    redirectURL: '/releasereasonlist',
    roles: [...menuAccessRoles?.crConfiguration],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  airaConfiguration: {
    displayText: `AIRA Configuration`,
    imageURLactive: AIRABlueIcon,
    imageURLinactive: AIRAGreyIcon,
    roles: [...menuAccessRoles?.airaConfiguration],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    groupItems: [
      'reasonconfiguration',
      'questionMasterList',
      'pcnTypeCreation',
      'teamCreation',
      'reports',
    ],
  },
  reasonconfiguration: {
    displayText: 'Reason Config',
    redirectURL: '/reasonconfiguration',
    roles: [...menuAccessRoles?.airaConfiguration],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    projectType: 'AIRA',
  },
  questionMasterList: {
    displayText: 'Question Master List',
    redirectURL: '/questionmasterlist',
    roles: [...menuAccessRoles?.airaConfiguration],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    projectType: 'AIRA',
  },
  pcnTypeCreation: {
    displayText: 'PCN Type Creation',
    redirectURL: '/pcntypecreation',
    roles: [...menuAccessRoles?.airaConfiguration],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    projectType: 'AIRA',
  },
  teamCreation: {
    displayText: 'Team Creation',
    redirectURL: '/teamcreation',
    roles: [...menuAccessRoles?.airaConfiguration],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    projectType: 'AIRA',
  },
  reports: {
    displayText: `Reports`,
    menuItemId: 'reports',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    projectType: 'AIRA',
    roles: [...menuAccessRoles?.airaConfiguration],
    groupItems: ['usage', 'userJourney', 'masterDataCount'],
  },
  usage: {
    displayText: 'Usage',
    redirectURL: '/usagereport',
    roles: [...menuAccessRoles?.airaConfiguration],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    projectType: 'AIRA',
  },
  userJourney: {
    displayText: 'User Journey',
    redirectURL: '/userjourney',
    roles: [...menuAccessRoles?.airaConfiguration],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    projectType: 'AIRA',
  },
  masterDataCount: {
    displayText: 'Master Data Count',
    redirectURL: '/masterdatacount',
    roles: [...menuAccessRoles?.airaConfiguration],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    projectType: 'AIRA',
  },
  contractName: {
    displayText: `__SelectedContractName__`,
    redirectURL: '/caseoverview',
    imageURLactive: wokinghamBlue,
    imageURLinactive: wokinghamGrey,
    roles: [...menuAccessRoles?.selectedContract],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  organizationName: {
    displayText: `__SelectedOrgName__`,
    redirectURL: '/orgreport',
    imageURLactive: wokinghamBlue,
    imageURLinactive: wokinghamGrey,
    roles: [...menuAccessRoles?.selectedContract],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  search: {
    displayText: `Search`,
    redirectURL: '/search',
    imageURLactive: searchBlue,
    imageURLinactive: searchGrey,
    roles: [...menuAccessRoles?.globalSearch],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  paymentPlan: {
    displayText: `Payment Plan`,
    redirectURL: '/paymentplan',
    imageURLactive: paymentPlanBlue,
    imageURLinactive: paymentPlanGrey,
    roles: [...menuAccessRoles?.paymentPlan],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isPaymentPlanEnabled',
  },
  reporting: {
    displayText: `Reporting`,
    imageURLactive: reportsBlue,
    imageURLinactive: reportsGrey,
    roles: [...menuAccessRoles?.reporting, 'TfLEnforcementViewer'],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isEveryReportsEnabled',
    isHavingSubmenu: true,
    groupItems: [
      'favourites',
      'caseStatus',
      'outboundCorrespondence',
      'inboundCorrespondence',
      'enforcementTeam',
      'financial',
      'auditAndTracking',
      'monitoring',
      'performanceMonitoring',
    ],
  },
  // revealBI: {
  //   displayText: `Reveal BI`,
  //   imageURLactive: reportsBlue,
  //   imageURLinactive: reportsGrey,
  //   roles: [...menuAccessRoles?.reporting],
  //   contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
  //   isHavingSubmenu: true,
  //   groupItems: [
  //     'revealCeoTracking',
  //     'revealCeoHeatmap',
  //     'revealUserActivity',
  //     'revealStreetLog',
  //     'revealCreateDashboard',
  //   ],
  // },
  // revealCeoTracking: {
  //   displayText: 'CEO Tracking',
  //   redirectURL: '/revealdashboard?dashboardId=ceotracking',
  //   roles: [...menuAccessRoles?.reporting],
  //   contractType: [contractTypes?.pcnName],
  //   groupItems: [],
  // },
  // revealCeoHeatmap: {
  //   displayText: 'CEO Heatmap',
  //   redirectURL: '/revealdashboard?dashboardId=ceoheatmap',
  //   roles: [...menuAccessRoles?.reporting],
  //   contractType: [contractTypes?.pcnName],
  //   groupItems: [],
  // },
  // revealUserActivity: {
  //   displayText: 'User Activity',
  //   redirectURL: '/revealdashboard?dashboardId=useractivity',
  //   roles: [...menuAccessRoles?.reporting],
  //   contractType: [contractTypes?.pcnName],
  //   groupItems: [],
  // },
  // revealStreetLog: {
  //   displayText: 'Street Log',
  //   redirectURL: '/revealdashboard?dashboardId=onstreetlog',
  //   roles: [...menuAccessRoles?.reporting],
  //   contractType: [contractTypes?.pcnName],
  //   groupItems: [],
  // },
  // revealCreateDashboard: {
  //   displayText: 'Create Dashboard',
  //   redirectURL: '/revealdashboard?new=true',
  //   roles: [...menuAccessRoles?.reporting],
  //   contractType: [contractTypes?.pcnName, contractTypes?.fpnName],
  //   groupItems: [],
  // },
  favourites: {
    displayText: 'Favourites',
    redirectURL: '/favourites',
    roles: [...menuAccessRoles?.reporting, 'TfLEnforcementViewer'],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    groupItems: [],
  },
  caseStatus: {
    displayText: 'Case Status',
    roles: [...menuAccessRoles?.reporting, 'TfLEnforcementViewer'],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isCaseStatusReportEnabled',
    groupItems: [
      'approvalQueueReports',
      'financialManualDiscounts',
      'holdReportDetail',
      'cctvCaseOverview',
      'cancelationReportDetail',
      'hhdCaseOverview',
      'caseType',
      'allCases',
      'allCCTVCases',
      'fpnOverview',
      'caseKeeperDetails',
      'lgKpiFigures',
    ], //todo item
  },
  approvalQueueReports: {
    displayText: 'Approval Queue',
    favouriteReportName: 'Approval Queue',
    redirectURL: `/caseStatus?reportName=${encryptData(
      'approvalQueueReports'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isApprovalQueueReportEnabled',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    groupItems: [],
  },
  financialManualDiscounts: {
    displayText: 'Manual Discounts',
    favouriteReportName: 'Manual Discounts',
    redirectURL: `/caseStatus?reportName=${encryptData(
      'financialManualDiscounts'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isManualDiscountsReportEnabled',
    groupItems: [],
  },
  holdReportDetail: {
    displayText: 'Hold Detail',
    favouriteReportName: 'Hold Detail',
    redirectURL: `/caseStatus?reportName=${encryptData('holdReportDetail')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isHoldDetailReportEnabled',
    groupItems: [],
  },
  cctvCaseOverview: {
    displayText: 'CCTV Overview',
    favouriteReportName: 'CCTV Overview',
    redirectURL: `/caseStatus?reportName=${encryptData('cctvCaseOverview')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isCctvOverviewReportEnabled',
    groupItems: [],
  },
  cancelationReportDetail: {
    displayText: 'Cancellation',
    favouriteReportName: 'Cancellation',
    redirectURL: `/caseStatus?reportName=${encryptData(
      'cancelationReportDetail'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isCancellationReportEnabled',
    groupItems: [],
  },
  hhdCaseOverview: {
    displayText: 'HHD Overview',
    favouriteReportName: 'HHD Overview',
    redirectURL: `/caseStatus?reportName=${encryptData('hhdCaseOverview')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isHhdOverviewReportEnabled',
    groupItems: [],
  },
  caseType: {
    displayText: 'Case Type',
    favouriteReportName: 'Case Type',
    redirectURL: `/caseStatus?reportName=${encryptData('caseType')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isCaseTypeReportEnabled',
    groupItems: [],
  },
  allCases: {
    displayText: 'All Cases',
    favouriteReportName: 'All Cases',
    redirectURL: `/caseStatus?reportName=${encryptData('allCases')}`,
    roles: [...menuAccessRoles?.reporting, 'TfLEnforcementViewer'],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isAllCasesReportEnabled',
    groupItems: [],
  },
  allCCTVCases: {
    displayText: 'All CCTV Cases',
    favouriteReportName: 'All CCTV Cases',
    redirectURL: `/caseStatus?reportName=${encryptData('allCCTVCases')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isAllCctvCasesReportEnabled',
    groupItems: [],
  },
  fpnOverview: {
    displayText: 'FPN Overview',
    favouriteReportName: 'FPN Overview',
    redirectURL: `/caseStatus?reportName=${encryptData('fpnOverview')}`,
    roles: [...menuAccessRoles?.reporting],
    displayBasedOnFlagKey: 'isFpnOverviewReportEnabled',
    contractType: [contractTypes?.fpnName],
    groupItems: [],
  },
  caseKeeperDetails: {
    displayText: 'Case Keeper Details',
    favouriteReportName: 'Case Keeper Details',
    redirectURL: `/caseStatus?reportName=${encryptData('caseKeeperDetails')}`,
    roles: [...menuAccessRoles?.reporting],
    displayBasedOnFlagKey: 'isCaseKeeperDetailsReportEnabled',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    groupItems: [],
  },
  lgKpiFigures: {
    displayText: 'LG KPI Figures',
    favouriteReportName: 'LG KPI Figures',
    redirectURL: `/caseStatus?reportName=${encryptData('lgKpiFigures')}`,
    roles: [...menuAccessRoles?.reporting],
    displayBasedOnFlagKey: 'isLGKPIFiguresReportEnabled',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    groupItems: [],
  },
  outboundCorrespondence: {
    displayText: 'Correspondence Out',
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isOutboundCorrespondenceReportEnabled',
    groupItems: [
      'printDetailAndOverview',
      'letterProducedDetailAndOverview',
      'correspondencePrinting',
      'lettersSent',
    ],
  },
  printDetailAndOverview: {
    displayText: 'Print Overview',
    favouriteReportName: 'Print Overview',
    redirectURL: `/outboundcorrespondence?reportName=${encryptData(
      'printDetailAndOverview'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isPrintOverviewReportEnabled',
    groupItems: [],
  },
  letterProducedDetailAndOverview: {
    displayText: 'Letter Produced',
    favouriteReportName: 'Letter Produced',
    redirectURL: `/outboundcorrespondence?reportName=${encryptData(
      'letterProducedDetailAndOverview'
    )}`,
    displayBasedOnFlagKey: 'isLetterProducedReportEnabled',
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    groupItems: [],
  },
  correspondencePrinting: {
    displayText: 'Corres. Print',
    favouriteReportName: 'Corres. Print',
    redirectURL: `/outboundcorrespondence?reportName=${encryptData(
      'correspondencePrinting'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isCorresPrintReportEnabled',
    groupItems: [],
  },
  lettersSent: {
    displayText: 'Letters sent',
    favouriteReportName: 'Letters Sent',
    redirectURL: `/outboundcorrespondence?reportName=${encryptData(
      'lettersSent'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isLettersSentReportEnabled',
    groupItems: [],
  },
  inboundCorrespondence: {
    displayText: 'Correspondence In',
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isInboundCorrespondenceReportEnabled',
    groupItems: [
      'openWorkflowOverview',
      'parseqScans',
      'challengeAndRepOutcomes',
      'inboundEmails',
    ],
  },
  openWorkflowOverview: {
    displayText: 'Open Workflow',
    favouriteReportName: 'Open Workflow',
    redirectURL: `/inboundcorrespondence?reportName=${encryptData(
      'openWorkflowOverview'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isOpenWorkflowReportEnabled',
    groupItems: [],
  },
  parseqScans: {
    displayText: 'Parseq Scans',
    favouriteReportName: 'Parseq Scans',
    redirectURL: `/inboundcorrespondence?reportName=${encryptData(
      'parseqScans'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isParseqScansReportEnabled',
    groupItems: [],
  },
  challengeAndRepOutcomes: {
    displayText: 'Challenge and Rep Outcomes',
    favouriteReportName: 'Challenge and Rep Outcomes',
    redirectURL: `/inboundcorrespondence?reportName=${encryptData(
      'challengeAndRepOutcomes'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isChallengeAndRepOutcomesReportEnabled',
    groupItems: [],
  },
  inboundEmails: {
    displayText: 'Inbound Emails',
    favouriteReportName: 'Inbound Emails',
    redirectURL: `/inboundcorrespondence?reportName=${encryptData(
      'inboundEmails'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    displayBasedOnFlagKey: 'isInboundEmailsEnabled',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    groupItems: [],
  },
  enforcementTeam: {
    displayText: 'Enforcement Team',
    roles: [...menuAccessRoles?.reporting, 'TfLEnforcementViewer'],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isEnforcementTeamReportEnabled',
    groupItems: [
      'onStreetLog',
      'onStreetActivitiesSummary',
      'ceoTracking',
      'ceoActivityDetail',
      'ceoHoursPatrolledByLocation',
      'ceoHeatmap',
      'officerAccuracyMonitoring',
      'hotspotReports',
      'permissionChecks',
      'schoolReport',
      'schoolEnforcementReport',
      'onStreetActivityReportExtended',
      'observations',
      'officerBreaks',
      'faultsReport',
      'abandonedVehiclesReport',
    ], //todo
  },
  onStreetLog: {
    displayText: 'On Street Log',
    favouriteReportName: 'On Street Log',
    redirectURL: `/enforcementteam?reportName=${encryptData('onStreetLog')}`,
    roles: [...menuAccessRoles?.reporting, 'TfLEnforcementViewer'],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isOnStreetLogReportEnabled',
    groupItems: [],
  },
  onStreetActivitiesSummary: {
    displayText: 'On Street Activities',
    favouriteReportName: 'On Street Activities',
    redirectURL: `/enforcementteam?reportName=${encryptData(
      'onStreetActivitiesSummary'
    )}`,
    roles: [...menuAccessRoles?.reporting, 'TfLEnforcementViewer'],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isOnStreetActivitiesReportEnabled',
    groupItems: [],
  },
  ceoTracking: {
    displayText: 'CEO Tracking',
    favouriteReportName: 'CEO Tracking',
    redirectURL: `/enforcementteam?reportName=${encryptData('ceoTracking')}`,
    roles: [...menuAccessRoles?.reporting, 'TfLEnforcementViewer'],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isCeoTrackingReportEnabled',
    groupItems: [],
  },
  ceoActivityDetail: {
    displayText: 'CEO Activity Detail',
    favouriteReportName: 'CEO Activity Detail',
    redirectURL: `/enforcementteam?reportName=${encryptData(
      'ceoActivityDetail'
    )}`,
    roles: [...menuAccessRoles?.reporting, 'TfLEnforcementViewer'],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isCeoActivityDetailReportEnabled',
    groupItems: [],
  },
  ceoHoursPatrolledByLocation: {
    displayText: 'Patrolled CEO Hours',
    favouriteReportName: 'Patrolled CEO Hours',
    redirectURL: `/enforcementteam?reportName=${encryptData(
      'ceoHoursPatrolledByLocation'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isPatrolledCeoHoursReportEnabled',
    groupItems: [],
  },
  ceoHeatmap: {
    displayText: 'CEO Heatmap',
    favouriteReportName: 'CEO Heatmap',
    redirectURL: `/enforcementteam?reportName=${encryptData('ceoHeatmap')}`,
    roles: [...menuAccessRoles?.reporting, 'TfLEnforcementViewer'],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isCeoHeatmapReportEnabled',
    groupItems: [],
  },
  officerAccuracyMonitoring: {
    displayText: 'Officer Monitoring',
    favouriteReportName: 'Officer Monitoring',
    redirectURL: `/enforcementteam?reportName=${encryptData(
      'officerAccuracyMonitoring'
    )}`,
    roles: [...menuAccessRoles?.reporting, 'TfLEnforcementViewer'],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isOfficerMonitoringReportEnabled',
    groupItems: [],
  },
  hotspotReports: {
    displayText: 'Hotspot',
    favouriteReportName: 'Hotspot',
    redirectURL: `/enforcementteam?reportName=${encryptData('hotspotReports')}`,
    roles: [...menuAccessRoles?.reporting, 'TfLEnforcementViewer'],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isHotspotReportEnabled',
    groupItems: [],
  },
  permissionChecks: {
    displayText: 'Permission Checks',
    favouriteReportName: 'Permission Checks',
    redirectURL: `/enforcementteam?reportName=${encryptData(
      'permissionChecks'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isPermissionCheckEnabled',
    groupItems: [],
  },
  schoolReport: {
    displayText: 'School Report',
    favouriteReportName: 'School Report',
    redirectURL: `/enforcementteam?reportName=${encryptData('schoolReport')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isSchoolReportEnabled',
  },
  permissionChecks: {
    displayText: 'Permission Checks',
    favouriteReportName: 'Permission Checks',
    redirectURL: `/enforcementteam?reportName=${encryptData(
      'permissionChecks'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isPermissionCheckEnabled',
    groupItems: [],
  },
  onStreetActivityReportExtended: {
    displayText: 'On Street Activity Report Extended',
    favouriteReportName: 'On Street Activity Report Extended',
    redirectURL: `/enforcementteam?reportName=${encryptData(
      'onStreetActivityReportExtended'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isOnStreetActivityReportExtendedEnabled',
    groupItems: [],
  },
  observations: {
    displayText: 'Observations',
    favouriteReportName: 'Observations',
    redirectURL: `/enforcementteam?reportName=${encryptData('observations')}`,
    roles: [...menuAccessRoles?.reporting, 'TfLEnforcementViewer'],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isObservationsReportEnabled',
    groupItems: [],
  },
  officerBreaks: {
    displayText: 'Officer Breaks',
    favouriteReportName: 'Officer Breaks',
    redirectURL: `/enforcementteam?reportName=${encryptData('officerBreaks')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isOfficerBreaksReportEnabled',
    groupItems: [],
  },
  faultsReport: {
    displayText: 'Faults Report',
    favouriteReportName: 'Faults Report',
    redirectURL: `/enforcementteam?reportName=${encryptData('faultsReport')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isFaultsReportEnabled',
    groupItems: [],
  },
  abandonedVehiclesReport: {
    displayText: 'Abandoned Vehicles',
    favouriteReportName: 'Abandoned Vehicles',
    redirectURL: `/enforcementteam?reportName=${encryptData(
      'abandonedVehicles'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [contractTypes?.pcnName, contractTypes?.ecnName],
    displayBasedOnFlagKey: 'isAbandonedVehiclesReportEnabled',
    groupItems: [],
  },
  schoolEnforcementReport: {
    displayText: 'School Enforcement Report',
    favouriteReportName: 'School Enforcement Report',
    redirectURL: `/enforcementteam?reportName=${encryptData(
      'schoolEnforcementReport'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isSchoolEnforcementReportEnabled',
    groupItems: [],
  },
  observations: {
    displayText: 'Observations',
    favouriteReportName: 'Observations',
    redirectURL: `/enforcementteam?reportName=${encryptData('observations')}`,
    roles: [...menuAccessRoles?.reporting, 'TfLEnforcementViewer'],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isObservationsReportEnabled',
    groupItems: [],
  },
  financial: {
    displayText: 'Financial',
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isFinancialReportEnabled',
    groupItems: [
      'payment',
      'refundChargeBack',
      'transfer',
      'revenueReport',
      'revenueByLocation',
      'pcnAndRevenue',
      'timePay',
      'accounting',
      'monthlyReviewReport',
    ],
  },
  payment: {
    displayText: 'Payment',
    favouriteReportName: 'Payment',
    redirectURL: `/financial?reportName=${encryptData('payment')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isPaymentReportEnabled',
    groupItems: [],
  },
  refundChargeBack: {
    displayText: 'Refund & Charge Back',
    favouriteReportName: 'Refund & Charge Back',
    redirectURL: `/financial?reportName=${encryptData('refundChargeBack')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isRefundAndChargeBackReportEnabled',
    groupItems: [],
  },
  transfer: {
    displayText: 'Transfer',
    favouriteReportName: 'Transfer',
    redirectURL: `/financial?reportName=${encryptData('transfer')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isTransferReportEnabled',
    groupItems: [],
  },
  revenueReport: {
    displayText: 'Revenue Report',
    favouriteReportName: 'Revenue Report',
    redirectURL: `/financial?reportName=${encryptData('revenueReport')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isRevenueReportReportEnabled',
    groupItems: [],
  },
  revenueByLocation: {
    displayText: 'Revenue By Location',
    favouriteReportName: 'Revenue By Location',
    redirectURL: `/financial?reportName=${encryptData('revenueByLocation')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isRevenueByLocationReportEnabled',
    groupItems: [],
  },
  pcnAndRevenue: {
    displayText: `PCN's and Revenue`,
    favouriteReportName: `PCN's and Revenue`,
    redirectURL: `/financial?reportName=${encryptData('pcnAndRevenue')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isPcnsAndRevenueReportEnabled',
    groupItems: [],
  },
  timePay: {
    displayText: 'Time Pay',
    favouriteReportName: 'Time Pay',
    redirectURL: `/financial?reportName=${encryptData('timePay')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.ecnName,
      contractTypes?.scotlandPcnName,
    ],
    displayBasedOnFlagKey: 'isTimePayEnabled',
    groupItems: [],
  },
  monthlyReviewReport: {
    displayText: 'Monthly Review Report',
    favouriteReportName: 'Monthly Review Report',
    redirectURL: `/financial?reportName=${encryptData('monthlyReviewReport')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isMonthlyReviewReportEnabled',
    groupItems: [],
  },
  accounting: {
    displayText: 'Accounting',
    favouriteReportName: 'Accounting',
    redirectURL: `/financial?reportName=${encryptData('accounting')}`,
    roles: [...menuAccessRoles?.reporting],
    displayBasedOnFlagKey: 'isAccountingReportEnabled',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    groupItems: [],
  },
  auditAndTracking: {
    displayText: 'Audit & Tracking',
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isAuditTrackingReportEnabled',
    groupItems: [
      'overallSummary',
      'dft',
      'persistentEvaders',
      'userActivity',
      'prefixAudit',
      'contractStats',
      'pcnIssuedByType',
      'persistentEvadersByEa',
      'burndown',
      'invoicingData',
    ],
  },
  overallSummary: {
    displayText: 'Overall Summary',
    favouriteReportName: 'Overall Summary',
    redirectURL: `/auditandtracking?reportName=${encryptData(
      'overallSummary'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isOverallSummaryReportEnabled',
    groupItems: [],
  },
  dft: {
    displayText: 'DFT',
    favouriteReportName: 'DFT',
    redirectURL: `/auditandtracking?reportName=${encryptData('dft')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isDftReportEnabled',
    groupItems: [],
  },
  persistentEvaders: {
    displayText: 'PE report',
    favouriteReportName: 'PE Report',
    redirectURL: `/auditandtracking?reportName=${encryptData(
      'persistentEvaders'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isPeReportReportEnabled',
    groupItems: [],
  },
  userActivity: {
    displayText: 'User Activity',
    favouriteReportName: 'User Activity',
    redirectURL: `/auditandtracking?reportName=${encryptData('userActivity')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isUserActivityReportEnabled',
    groupItems: [],
  },
  prefixAudit: {
    displayText: 'Prefix Audit',
    favouriteReportName: 'Prefix Audit',
    redirectURL: `/auditandtracking?reportName=${encryptData('prefixAudit')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isPrefixAuditReportEnabled',
    groupItems: [],
  },
  contractStats: {
    displayText: 'Contract Stats',
    favouriteReportName: 'Contract Stats',
    redirectURL: `/auditandtracking?reportName=${encryptData('contractStats')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isContractStatsReportEnabled',
    groupItems: [],
  },
  pcnIssuedByType: {
    displayText: 'PCNs by Type',
    favouriteReportName: 'PCNs by Type',
    redirectURL: `/auditandtracking?reportName=${encryptData(
      'pcnIssuedByType'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isPcnByTypeReportEnabled',
    groupItems: [],
  },
  persistentEvadersByEa: {
    displayText: 'PE at EA',
    favouriteReportName: 'PE At EA',
    redirectURL: `/auditandtracking?reportName=${encryptData(
      'persistentEvadersByEa'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isPeAtEaReportEnabled',
    groupItems: [],
  },
  burndown: {
    displayText: 'Burndown',
    favouriteReportName: 'Burndown',
    redirectURL: `/auditandtracking?reportName=${encryptData('burndown')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isBurndownReportEnabled',
    groupItems: [],
  },
  invoicingData: {
    displayText: 'Invoicing Data',
    favouriteReportName: 'Invoicing Data',
    redirectURL: `/auditandtracking?reportName=${encryptData('invoicingData')}`,
    roles: [...menuAccessRoles?.reporting],
    displayBasedOnFlagKey: 'isInvoicingDataReportEnabled',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    groupItems: [],
  },

  monitoring: {
    displayText: 'Monitoring',
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isMonitoringReportEnabled',
    groupItems: [
      'monitoringDashboard',
      'caseNotProgressed',
      'pcnForecast',
      'casesOnholdAndNextProgression',
      'caseActiveDays',
      'printBatchesReports',
      'enforcementAgencyBatches',
      'hhdSync',
      'dvlaReport',
      'caseProgression',
      'debtAndWarrant',
    ],
  },
  monitoringDashboard: {
    displayText: 'Monitoring Dashboard',
    favouriteReportName: 'Monitoring Dashboard',
    redirectURL: `/monitoring?reportName=${encryptData('monitoringDashboard')}`,
    displayBasedOnFlagKey: 'isMonitoringDashboardReportEnabled',
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    groupItems: [],
  },

  caseNotProgressed: {
    displayText: 'Cases Not Progressed',
    favouriteReportName: 'Cases Not Progressed',
    redirectURL: `/monitoring?reportName=${encryptData('caseNotProgressed')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isCasesNotProgressedReportEnabled',
  },
  pcnForecast: {
    displayText: 'PCN Forecast',
    favouriteReportName: 'PCN Forecast',
    redirectURL: `/monitoring?reportName=${encryptData('pcnForecast')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isPcnForecastReportEnabled',
  },
  casesOnholdAndNextProgression: {
    displayText: 'Held case progression',
    favouriteReportName: 'Held Case Progression',
    redirectURL: `/monitoring?reportName=${encryptData(
      'casesOnholdAndNextProgression'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isHeldCaseProgressionReportEnabled',
  },
  caseActiveDays: {
    displayText: 'Case Active Days',
    favouriteReportName: 'Case Active Days',
    redirectURL: `/monitoring?reportName=${encryptData('caseActiveDays')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isCaseActiveDaysReportEnabled',
  },
  printBatchesReports: {
    displayText: 'Print Batches',
    favouriteReportName: 'Print Batches',
    redirectURL: `/monitoring?reportName=${encryptData('printBatchesReports')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isPrintBatchesReportEnabled',
  },
  enforcementAgencyBatches: {
    displayText: 'EA Batches',
    favouriteReportName: 'EA Batches',
    redirectURL: `/monitoring?reportName=${encryptData(
      'enforcementAgencyBatches'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isEaBatchesReportEnabled',
  },
  hhdSync: {
    displayText: 'HHD Sync',
    favouriteReportName: 'HHD Sync',
    redirectURL: `/monitoring?reportName=${encryptData('hhdSync')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isHHDSyncReportEnabled',
  },
  dvlaReport: {
    displayText: 'DVLA',
    favouriteReportName: 'DVLA',
    redirectURL: `/monitoring?reportName=${encryptData('dvlaReport')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isDVLAEnabled',
  },
  performanceMonitoring: {
    displayText: 'Performance Monitoring',
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isPerformanceMonitoringReportEnabled',
    groupItems: ['backOfficers', 'enforcementOfficers'],
  },

  caseProgression: {
    displayText: 'Case Progression',
    favouriteReportName: 'Case Progression',
    redirectURL: `/monitoring?reportName=${encryptData('caseProgression')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isCaseProgressionReportEnabled',
  },
  debtAndWarrant: {
    displayText: 'Debt & Warrant',
    favouriteReportName: 'Debt Warrant',
    redirectURL: `/monitoring?reportName=${encryptData('debtAndWarrant')}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [contractTypes?.pcnName],
    displayBasedOnFlagKey: 'isDebtAndWarrantReportEnabled',
  },
  backOfficers: {
    displayText: 'Back Officers',
    favouriteReportName: 'Back Officers',
    redirectURL: `/performancemonitoring?reportName=${encryptData(
      'backOfficers'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isBackOfficersReportEnabled',
    groupItems: [],
  },
  enforcementOfficers: {
    displayText: 'Enforcement Officers',
    favouriteReportName: 'Enforcement Officers',
    redirectURL: `/performancemonitoring?reportName=${encryptData(
      'enforcementOfficers'
    )}`,
    roles: [...menuAccessRoles?.reporting],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isEnforcementOfficersReportEnabled',
    groupItems: [],
  },
  myWorkflow: {
    displayText: `My Workflow`,
    redirectURL: '/myworkflow',
    imageURLactive: myWorkflowBlue,
    imageURLinactive: myWorkflowGrey,
    roles: [...menuAccessRoles?.myWorkFlow],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.summarisation,
      contractTypes?.summarization,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  repReview: {
    displayText: `Rep.Review`,
    redirectURL: '/repReview',
    imageURLactive: myWorkflowBlue,
    imageURLinactive: myWorkflowGrey,
    roles: [...menuAccessRoles?.representationReview],
    displayBasedOnFlagKey: 'clientWorkFlow',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  workflowManager: {
    displayText: `Workflow Manager`,
    redirectURL: '/workflowmanager',
    imageURLactive: workflowManagerBlue,
    imageURLinactive: workflowManagerGrey,
    roles: [...menuAccessRoles?.workflowManager],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  approvalQueue: {
    displayText: `Approval Queue`,
    redirectURL: '/approvalQueueList',
    imageURLactive: approvalQueueBlue,
    imageURLinactive: approvalQueueGrey,
    roles: [...menuAccessRoles?.approvalQueue],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isCorrespondenceApprovalLimitEnabled',
  },
  abandonedVehicles: {
    displayText: 'Abandoned Vehicles',
    redirectURL: '/abandonedvehicles',
    imageURLactive: noticeProcessingBlue,
    imageURLinactive: noticeProcessingGrey,
    roles: [...menuAccessRoles?.abandonedVehicles],
    displayBasedOnFlagKey: 'isAbandonedVehicleKadoeLookupEnabled',
    contractType: [contractTypes?.pcnName],
  },
  // Abandoned vehicle contracts always expose this menu, so no feature flag or role check is applied here.
  abandonedVehiclesForAbandonedVehicleContract: {
    displayText: 'Abandoned Vehicles',
    redirectURL: '/nonintegratedabandonedvehicles',
    imageURLactive: noticeProcessingBlue,
    imageURLinactive: noticeProcessingGrey,
    contractType: [contractTypes?.abandonedVehicle],
  },
  noticeProcessing: {
    displayText: `Notice Processing`,
    imageURLactive: noticeProcessingBlue,
    imageURLinactive: noticeProcessingGrey,
    roles: [
      ...menuAccessRoles?.noticeProcessing,
      ...menuAccessRoles.debtReview,
    ],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    groupItems: [
      'dvla',
      'printBatches',
      'scanImport',
      'partPayment',
      'bulkUpdate',
      'debt',
      'warrant',
      'foreignDebt',
      'debtorReview',
    ],
  },
  dvla: {
    displayText: 'DVLA',
    redirectURL: '/dvla',
    roles: [...menuAccessRoles?.noticeProcessing],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  printBatches: {
    displayText: 'Print Batches',
    redirectURL: '/printbatchlist',
    roles: [...menuAccessRoles?.noticeProcessing],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  scanImport: {
    displayText: 'Scan Import',
    redirectURL: '/scanimport',
    roles: [...menuAccessRoles?.noticeProcessing],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  partPayment: {
    displayText: 'Payments',
    redirectURL: '/partpayment',
    displayBasedOnFlagKey: 'isPaymentMonitoringEnabled',
    roles: [...menuAccessRoles?.noticeProcessing],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  bulkUpdate: {
    displayText: 'Bulk Update',
    redirectURL: '/bulkupdatelist',
    roles: [...menuAccessRoles?.noticeProcessing],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  debt: {
    displayText: 'Debt Reg',
    roles: [...menuAccessRoles?.noticeProcessing],
    contractType: [contractTypes?.pcnName, contractTypes?.ecnName],
    groupItems: ['preDebt', 'debtReview', 'debtReg'],
  },
  warrant: {
    displayText: 'Warrant',
    roles: [...menuAccessRoles?.noticeProcessing],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    groupItems: [
      'warrantReview',
      'warrantRequest',
      'bailiffOfContract',
      'bailiffMessage',
    ],
  },
  preDebt: {
    displayText: 'Pre Debt',
    redirectURL: '/predebt',
    roles: [...menuAccessRoles?.noticeProcessing],
    contractType: [contractTypes?.pcnName, contractTypes?.ecnName],
  },
  debtReview: {
    displayText: 'Debt Review',
    redirectURL: '/debtreview',
    roles: [...subMenuAccessRoles?.debtReview],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  debtReg: {
    displayText: 'Debt Registration',
    redirectURL: '/debt',
    roles: [...menuAccessRoles?.noticeProcessing],
    contractType: [contractTypes?.pcnName, contractTypes?.ecnName],
  },
  foreignDebt: {
    displayText: 'Foreign Debt',
    redirectURL: '/foreigndebt',
    displayBasedOnFlagKey: 'isInternationalDebtRecovery',
    roles: [...menuAccessRoles?.noticeProcessing],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  warrantRequest: {
    displayText: 'Warrant Request',
    redirectURL: '/warrant',
    roles: [...menuAccessRoles?.noticeProcessing],
    contractType: [contractTypes?.pcnName, contractTypes?.ecnName],
  },
  warrantReview: {
    displayText: 'Warrant Review',
    redirectURL: '/warrantreview',
    roles: [...menuAccessRoles?.noticeProcessing],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.ecnName,
    ],
  },
  bailiffOfContract: {
    displayText: 'Enforcement Agency',
    redirectURL: '/bailiffbatch',
    roles: [...menuAccessRoles?.noticeProcessing],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  bailiffMessage: {
    displayText: 'EA Message',
    redirectURL: '/bailiffmessage',
    roles: [...menuAccessRoles?.noticeProcessing],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  debtorReview: {
    displayText: 'Debtor Review',
    redirectURL: '/debtorreview',
    displayBasedOnFlagKey: 'isDebtorReviewEnabled',
    roles: [...menuAccessRoles?.noticeProcessing],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },

  messaging: {
    displayText: `Messaging`,
    imageURLactive: hhdMessageBlue,
    imageURLinactive: hhdMessageGrey,
    roles: [...menuAccessRoles?.hhdMessage, 'TfLEnforcementViewer'],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    groupItems: ['backOffice', 'onStreet', 'tasks', 'briefing', 'faults'],
  },
  backOffice: {
    displayText: 'Backoffice',
    redirectURL: '/backoffice',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    roles: [...menuAccessRoles?.hhdMessage],
  },
  onStreet: {
    displayText: 'On Street',
    redirectURL: '/onstreet',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    roles: [...menuAccessRoles?.hhdMessage],
  },
  tasks: {
    displayText: 'Tasks',
    redirectURL: '/tasks',
    displayBasedOnFlagKey: 'isCreateTask',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    roles: [...menuAccessRoles?.hhdMessage],
  },
  briefing: {
    displayText: 'Briefing',
    redirectURL: '/briefinglist',
    displayBasedOnFlagKey: 'isBriefingEnabled',
    roles: [...subMenuAccessRoles?.briefing],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  faults: {
    displayText: 'Faults',
    redirectURL: '/faults',
    contractType: [contractTypes?.pcnName],
    roles: [...menuAccessRoles?.hhdMessage, 'TfLEnforcementViewer'],
  },
  tracking: {
    displayText: 'Tracking',
    imageURLactive: clientSetupBlue,
    imageURLinactive: clientSetupGrey,
    roles: [
      ...menuAccessRoles?.tracking,
      ...subMenuAccessRoles?.hotspot,
      ...subMenuAccessRoles?.trackingAccess,
    ],
    displayBasedOnFlagKey: 'isTrackingMenuEnabled',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    groupItems: ['officerTracking', 'geoFenceLog', 'hotspot', 'trackingAccess'],
  },
  officerTracking: {
    displayText: 'Officer Tracking',
    redirectURL: '/officertracking',
    displayBasedOnFlagKey: 'isTrackingMenuEnabled',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    roles: [...menuAccessRoles?.tracking],
  },
  geoFenceLog: {
    displayText: 'Geo Fence Log',
    redirectURL: '/geofencelog',
    displayBasedOnFlagKey: 'isTrackingMenuEnabled',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    roles: [...subMenuAccessRoles?.geoFenceLog],
  },
  hotspot: {
    displayText: 'Hotspot',
    redirectURL: '/hotspot',
    displayBasedOnFlagKey: 'isHotSpotEnabled',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    roles: [...subMenuAccessRoles?.hotspot],
  },
  trackingAccess: {
    displayText: 'Tracking Access',
    redirectURL: '/trackingaccess',
    displayBasedOnFlagKey: 'isTrackingSubMenuEnabled',
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    roles: [...subMenuAccessRoles?.trackingAccess],
  },
  manualCase: {
    displayText: `Manual Case`,
    imageURLactive: manualCaseBlue,
    imageURLinactive: manualCaseGrey,
    roles: [...menuAccessRoles?.manualCase],
    displayBasedOnFlagKey: 'isManualCaseEnabled', // added only for parent as it is same for all child
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    groupItems: ['manualCaseCreation', 'pcnNumberReservation'],
  },
  manualCaseCreation: {
    displayText: 'Manual case creation',
    menuItemId: 'manualCaseCreation',
    redirectURL: '/manualcasecreation',
    roles: [...menuAccessRoles?.manualCase],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  pcnNumberReservation: {
    displayText: 'Case Reservation',
    menuItemId: 'pcnNumberReservation',
    redirectURL: '/casenumberreservation',
    roles: [...menuAccessRoles?.manualCase],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  clampAndRemoval: {
    displayText: `Clamp & Removal`,
    imageURLactive: clampAndRemovalBlue,
    imageURLinactive: clampRemovalGrey,
    roles: [...menuAccessRoles?.clampAndRemoval],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
    displayBasedOnFlagKey: 'isCandREnabled', // added only for parent as it is same for all child
    groupItems: ['jobs', 'truckMapping', 'pounds', 'crReporting'],
  },
  jobs: {
    displayText: 'Jobs',
    redirectURL: '/jobs',
    roles: [...menuAccessRoles?.clampAndRemoval],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  truckMapping: {
    displayText: 'Truck Mapping',
    redirectURL: '/truckmapping',
    roles: [...menuAccessRoles?.clampAndRemoval],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  pounds: {
    displayText: 'Pounds',
    redirectURL: '/pounds',
    roles: [...menuAccessRoles?.clampAndRemoval],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  crReporting: {
    displayText: 'C&R Reporting',
    menuItemId: 'crReporting',
    redirectURL: '/crreporting',
    roles: [...menuAccessRoles?.clampAndRemoval],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  users: {
    displayText: 'Users',
    redirectURL: '/manageuser',
    roles: [...menuAccessRoles?.configuration],

    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  templates: {
    displayText: 'Templates',
    redirectURL: '/templates',
    roles: [...menuAccessRoles?.configuration],

    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  zone: {
    displayText: 'Zone',
    redirectURL: '/zone',
    roles: [...menuAccessRoles?.configuration],

    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  location: {
    displayText: 'Location',
    redirectURL: '/location',
    roles: [...menuAccessRoles?.configuration],

    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  vehicleType: {
    displayText: 'Vehicle Type',
    redirectURL: '/vehicletype',
    roles: [...menuAccessRoles?.configuration, contractTypes?.privateLand],

    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  contraventionCodes: {
    displayText: 'Contravention Codes',
    redirectURL: '/contraventions',
    roles: [...menuAccessRoles?.configuration],

    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  faq: {
    displayText: 'FAQ',
    redirectURL: '/faqList',
    roles: [...menuAccessRoles?.configuration],

    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  cancellationReasons: {
    displayText: 'Cancellation Reasons',
    redirectURL: '/cancellationreason',
    roles: [...menuAccessRoles?.configuration],

    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  rejectReasons: {
    displayText: 'Reject Reasons',
    redirectURL: '/rejectreason',
    roles: [...menuAccessRoles?.configuration],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  crRepGrounds: {
    displayText: 'CP Rep. Grounds',
    redirectURL: '/groundslist',
    roles: [...menuAccessRoles?.configuration],

    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  holdReasons: {
    displayText: 'Hold',
    redirectURL: '/holdreasonslist',
    roles: [...menuAccessRoles?.configuration],

    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  reportConfiguration: {
    displayText: 'Reporting',
    redirectURL: '/reportconfiguration',
    roles: [...menuAccessRoles?.configuration],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  nonWorkingDays: {
    displayText: 'Non-Working Days',
    redirectURL: '/nonworkingdays',
    roles: [...menuAccessRoles?.configuration],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  geoFencingSetup: {
    displayText: 'Geo Fencing Setup',
    redirectURL: '/geofencingsetuplist',
    roles: [...menuAccessRoles?.configuration],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.fpnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  enforcementMap: {
    displayText: 'Enforcement Route Map',
    redirectURL: '/enforcementmap',
    imageURLactive: enforcementMapEnable,
    imageURLinactive: enforcementMapDisable,
    roles: [...menuAccessRoles?.enforcementMap],
    contractType: [contractTypes?.pcnName, contractTypes?.scotlandPcnName],
    displayBasedOnFlagKey: 'isMapEnabled',
  },
  cpzList: {
    displayText: 'Controlled Parking Zone',
    redirectURL: '/cpzlist',
    roles: [...menuAccessRoles?.configuration],
    contractType: [
      contractTypes?.pcnName,
      contractTypes?.privateLand,
      contractTypes?.scotlandPcnName,
      contractTypes?.ecnName,
    ],
  },
  manualCaseForFpn: {
    displayText: 'Manual Case',
    imageURLactive: manualCaseBlue,
    imageURLinactive: manualCaseGrey,
    roles: [...menuAccessRoles?.caseReservationForPreCases],
    contractType: [contractTypes?.fpnName],
    groupItems: ['caseNumberReservationForFpn'],
  },
  caseNumberReservationForFpn: {
    displayText: 'Case Reservation',
    redirectURL: '/casenumberreservation',
    roles: [...menuAccessRoles?.caseReservationForPreCases],
    contractType: [contractTypes?.fpnName],
  },
  preCases: {
    imageURLactive: preCasesBlue,
    imageURLinactive: preCasesGrey,
    displayText: 'Pre-Cases',
    redirectURL: '/precases',
    roles: [...menuAccessRoles?.preCases],
    contractType: [contractTypes?.fpnName],
  },
  cctvConfiguration: {
    displayText: 'ANPR Configuration',
    imageURLactive: cctvConfigurationBlue,
    imageURLinactive: cctvConfurationGrey,
    roles: [...menuAccessRoles?.cctvConfiguration],
    contractType: [contractTypes?.privateLand],
    groupItems: [
      'cctvSite',
      'taskReservation',
      'discardReasonlist',
      'workflowList',
    ],
  },
  cctvSite: {
    displayText: 'ANPR Site',
    redirectURL: '/cctvsite/list',
    roles: [...menuAccessRoles?.configuration],
    contractType: [contractTypes?.privateLand],
  },
  taskReservation: {
    displayText: 'Task Reservation',
    redirectURL: '/taskreservationlist',
    roles: [...menuAccessRoles?.cctvConfiguration],
    contractType: [contractTypes?.privateLand],
  },
  discardReasonlist: {
    displayText: 'Discard Reasons',
    redirectURL: '/discardreasonlist',
    roles: [...menuAccessRoles?.configuration],
    contractType: [contractTypes?.privateLand],
  },
  workflowList: {
    displayText: 'Workflow',
    redirectURL: '/workflowlist',
    roles: [...(menuAccessRoles.configuration || [])],
    contractType: [contractTypes?.privateLand],
  },
  cctvReview: {
    displayText: 'ANPR Review',
    imageURLactive: cctvReviewBlue,
    imageURLinactive: cctvReviewGrey,
    roles: [...menuAccessRoles?.cctvReviewMenu],
    contractType: [contractTypes?.privateLand],
    groupItems: [
      'workflowOverview',
      'preCaseAllocation',
      'manualReview',
      'onHold',
      'discardedCases',
      'cctvReports',
    ],
  },
  workflowOverview: {
    displayText: 'Workflow Overview',
    redirectURL: '/workflowoverview',
    roles: [...menuAccessRoles?.cctvReview],
    contractType: [contractTypes?.privateLand],
  },
  preCaseAllocation: {
    displayText: 'Precase Allocation',
    redirectURL: '/precaseallocation',
    roles: [...menuAccessRoles?.precaseAllocation],
    contractType: [contractTypes?.privateLand],
  },
  manualReview: {
    displayText: 'Manual Review',
    redirectURL: '/manualreview',
    roles: [...menuAccessRoles?.cctvReview],
    contractType: [contractTypes?.privateLand],
  },
  onHold: {
    displayText: 'On Hold',
    redirectURL: '/onhold',
    roles: [...menuAccessRoles?.cctvReview],
    contractType: [contractTypes?.privateLand],
  },
  discardedCases: {
    displayText: 'Discarded Cases',
    redirectURL: '/discardedcases',
    roles: [...menuAccessRoles?.discardedCases],
    contractType: [contractTypes?.privateLand],
  },
  cctvReports: {
    displayText: 'Reports',
    roles: [...menuAccessRoles?.cctvReview],
    contractType: [contractTypes?.privateLand],
    groupItems: ['anprOperationalReport'],
  },
  anprOperationalReport: {
    displayText: 'ANPR Operational Report',
    redirectURL: `/anproperationalreport?reportName=${encryptData(
      'anprOperational'
    )}`,
    roles: [...menuAccessRoles?.cctvReview],
    contractType: [contractTypes?.privateLand],
    groupItems: [],
  },
}

const contractDependentMenuItemList = [
  'contractName',
  'search',
  'paymentPlan',
  'reporting',
  'revealBI',
  'myWorkflow',
  'repReview',
  'workflowManager',
  'approvalQueue',
  'abandonedVehicles',
  'noticeProcessing',
  'messaging',
  'tracking',
  'manualCase',
  'manualCaseForFpn', //manual case menu for FPN. Separated as FPN menu has different roles with only one subMenu
  'clampAndRemoval',
  'enforcementMap',
  'preCases',
  'cctvReview',
  'supportRequest',
  'abandonedVehiclesForAbandonedVehicleContract',
]
const contractIndependentMenuItemList = [
  'home',
  'configuration',
  'clientSetup',
  'crConfiguration',
  'airaConfiguration',
  'cctvConfiguration',
  'printAdmin',
  'caseLock',
]
const orgMenuItemList = ['home', 'organizationName', 'reporting']
const hideTitleBarForPaths = [
  '/favourites',
  '/casestatus',
  '/outboundcorrespondence',
  '/inboundcorrespondence',
  '/enforcementteam',
  '/financial',
  '/auditandtracking',
  '/monitoring',
  '/performancemonitoring',
  '/revealdashboard',
  '/anproperationalreport',
]
const reportSubMenuPaths = [
  '/favourites',
  '/casestatus',
  '/outboundcorrespondence',
  '/inboundcorrespondence',
  '/enforcementteam',
  '/financial',
  '/auditandtracking',
  '/monitoring',
  '/performancemonitoring',
  '/anproperationalreport',
  // '/revealdashboard',
  // '/ceo-path-tracking',
  // '/ceo-activity-heatmap',
]
const airaPagesPaths = [
  '/reasonconfiguration',
  '/questionmasterlist',
  '/pcntypecreation',
  '/teamcreation',
  '/masterdatacount',
  '/usagereport',
  '/userjourney',
]

function holdExpiryDateParseHandler(dateValue) {
  return parse(dateValue, 'dd/MM/yyyy HH:mm:ss', new Date())
}

function formatFileSize(bytes) {
  // const binaryString = atob(bytes)

  // Get byte length
  const byteLength = bytes?.length
  if (byteLength === 0) return 0
  const byteSize = 1024

  const contentSize = byteLength / byteSize / byteSize
  return contentSize
}
const widgetTypes = {
  organisation: 'Organisation',
  contract: 'Contract',
}
const timeStringToMinutes = (timeString, fieldValue) => {
  if (!timeString || typeof timeString !== 'string') return null

  // Match HH:MM or HH:MM:SS
  const timeParts = timeString.split(':')
  if (timeParts.length !== 2 && timeParts.length !== 3) return null

  const [hoursStr, minutesStr, secondsStr = '0'] = timeParts

  const hours = Number(hoursStr)
  const minutes = Number(minutesStr)
  const seconds = Number(secondsStr)

  if (
    !Number.isInteger(hours) ||
    hours < 0 ||
    hours > 23 ||
    !Number.isInteger(minutes) ||
    minutes < 0 ||
    minutes > 59 ||
    !Number.isInteger(seconds) ||
    seconds < 0 ||
    seconds > 59
  ) {
    return null
  }

  return hours * 60 + minutes + seconds / 60
}
const isTabTitleCharactersValid = (trimmedValue) =>
  !/^[A-Za-z0-9\s\-_.,:;(){}[\]&@#!\\'"?\/\*+=₹\$\€£]*$/.test(trimmedValue)

const sortByDisplayName = (a, b) => {
  const nameA = (a?.displayName || '').toLowerCase()
  const nameB = (b?.displayName || '').toLowerCase()
  return nameA.localeCompare(nameB)
}

const shouldCloseSliderOnPathChange = ({
  previousPathname,
  currentPathname,
  isSliderOpen,
}) => {
  return (
    Boolean(isSliderOpen) &&
    previousPathname !== undefined &&
    previousPathname !== currentPathname
  )
}

const closeCaseValidationSlider = ({
  setSliderstate,
  setPcnNumber,
  setToken,
}) => {
  if (typeof setSliderstate === 'function') {
    setSliderstate(false)
  }
  if (typeof setPcnNumber === 'function') {
    setPcnNumber('')
  }
  if (typeof setToken === 'function') {
    setToken('')
  }
}

const DaysValue = [
  {
    id: '7 Days',
    value: 7,
  },
  {
    id: '14 Days',
    value: 14,
  },
  {
    id: '21 Days',
    value: 21,
  },
  {
    id: '28 Days',
    value: 28,
  },
]

const cpzMessageContentList = {
  cancelConfirmationText: 'Are you sure you want to cancel',
}
const getContractLandingPath = (contractType) =>
  contractType?.toString()?.toLowerCase() === contractTypes?.abandonedVehicle
    ? '/nonintegratedabandonedvehicles'
    : '/caseoverview'

export default {
  menuItemDatasourceBase,
  contractDependentMenuItemList,
  contractIndependentMenuItemList,
  sortArrayOfObjects,
  sortByDisplayName,
  handleBeforeUnload,
  confirmwindow,
  utcDateFormat,
  commonDateTimeFormat,
  commonDateTimeFormatForReactMultiDatePicker,
  commonDateTimePlaceHolder,
  permissionMessage,
  convertArrOfObjectToArrOfString,
  dateAndTimeConversion,
  convertSystemDateTimeToDate,
  convertDateTimePickerDateTimeToCustomFormat,
  convertUtcDateTimeToFormattedDateAgGrid,
  convertUtcDateTimeToFormattedDateTimeAgGrid,
  convertUtcDateTimeToFormattedDateTime,
  returnCurrentDateTimeBasedOnTimezone,
  returnCurrentDateBasedOnTimezoneWithoutFormatting,
  returnCurrentDateTimeInUTC,
  returnCurrentDateTimeSpecificFormatBasedOnTimezone,
  menuAccessRoles,
  subMenuAccessRoles,
  menuList,
  getRoleFromAssignedContractJSON,
  checkUserRole,
  checkPermittedRoleOrNot,
  dataSaveWarnTitle,
  dataCloseConfirmation,
  rejectTitle,
  dataSaveWarnMessage,
  dataEditTitle,
  dataEditMessage,
  commonPostBodyDateTimeFormat,
  convertUtcDateTimeToTimezoneDateTime,
  convertUtcDateTimeToTimezoneDate,
  convertUtcDateTimeToTimezoneTime,
  convertUtcDateTimeToTimezoneCustomFormat,
  convertTimezoneDateTimeToUtcDateTime,
  convertDateTimeWithAMPM,
  gridFilterOptions,
  apiResponseCode,
  apiDomainName,
  isValidEmailAddress,
  isAlphaNumeric,
  isAlphaOrNumeric,
  tptetaNoticeType,
  setDecimalValue,
  defaultVQ4RetryDays,
  minVQ4RetryDays,
  maxVQ4RetryDays,
  userRoleCheck,
  userRoleCheckAdmin,
  defaultHHDAutoLogOut,
  minHHDAutoLogOut,
  maxHHDAutoLogOut,
  defaultVQ4RetryDays,
  minVQ4RetryDays,
  maxVQ4RetryDays,
  removeKeyFromObject,
  isValidPhoneNumber,
  isValidCEOBadgeNumber,
  deleteConfirmation,
  deleteConfirmationTitle,
  isValidURL,
  messageTitle,
  deleteMessage,
  browserIdentify,
  infectedVirusMessage,
  scanFailed,
  scanOnProgress,
  generateNewRequest,
  processMessage,
  discountApplied,
  discountCannotApply,
  stageTextMap,
  statusTextMap,
  sourceTextMap,
  generateNewRequestHeading,
  batchCreatedSuccessMessage,
  debtRestrictionBannerTitle,
  debtRestrictionInvalidKeeperAddress,
  debtRestrictionMissingChargeCertDate,
  batchCreatedErrorMessage,
  generateNewRequestDebt,
  representationReview,
  representationReviewMoreInfo,
  generateNewRequestBailiff,
  tokenExpiredMessage,
  fileDownloaderByURL,
  createObjectUrlAndDownload,
  processCancel,
  csvDownLoadClickHandler,
  constMessageObject,
  applicationInactiveTimeLapse,
  refreshTokenMinutesEarlier,
  toastAutoHideDuration,
  cancellationReasonWarnMessage,
  iconFontSize,
  isAssociatedWithTask,
  reAssignUserMessage,
  assignUSerMessage,
  completedTaskMessage,
  deleteTaskConfirmMessage,
  inputSaveMessage,
  noteSavedSuccessMessage,
  workFlowAssignMessage,
  workFlowCompleteConfirmationMessage,
  workFlowCompleteSuccessMessage,
  cancellationWarningMessage,
  exportDataError,
  cancellationReasonDelete,
  completeSuccessMessage,
  workFlowReAssignMessage,
  messageDisplayItems,
  workFlowUnassignMessage,
  keeperConfirmation,
  keeperUpdateConfirmation,
  RegOffenderAddress,
  actionButton,
  // toastSuccessStatus,
  bailiffMessages,
  repReviewActionStatus,
  templateData,
  contractData,
  integrationServices,
  brandingMenu,
  branding,
  invalidGPS,
  toastStatus,
  confirmWindowMessage,
  allowedFileLimit,
  newGenerateRequestMessage,
  newGenerateRequestMessageFalse,
  tribunalActionEnabledStages,
  locationNameCheck,
  SuccessMsgs,
  getLabel,
  uRLCheck,
  domainURLCheck,
  brandingDeleteSuccess,
  domainSettings,
  noRecordsFound,
  configurationStep1,
  txtbutton,
  brandingContractHeading,
  errorOnGettingData,
  roleList,
  mediaExtensions,
  fileSequenceNoHelpText,
  searchFieldDataSource,
  sampleFileZone,
  attachmentDropZoneStaticData,
  contractSetupToggleOptions,
  discountButtonValue,
  isShowPreviouslyIssuedPcnValues,
  getFileSizeInKb,
  applyPreviewData,
  setBackgroundImage,
  setBackground,
  setBgImageProperty,
  acceptPartPayment,
  updatedSuccessfully,
  tooltipTheme,
  defaultButtonBGColor,
  defaultButtonTextColor,
  customTooltipThemeData,
  dvlaXMLFileDownloadSuccess,
  xmlFileDownLoadClickHandler,
  noValuesChanged,
  userPermissionMessage,
  emptyFunction,
  inputFieldNameListLoweredCase,
  contractSetupFieldErrors,
  modes,
  roleList,
  fileUploadStateValue,
  attachmentInvalidErrors,
  bulkUpdateMessages,
  maxNotesLength,
  caseSettingsPage,
  maximumCharacterLimitReached,
  placeHolderText,
  getBase64StringDetail,
  purgePage,
  apiMethods,
  revertedType,
  officerTrackingMessage,
  signOutTheUser,
  messagePopupStaticData,
  representationReviewPage,
  workFlowManagerPage,
  scanImportPage,
  workflow,
  linkedCases,
  getBase64FromBlobURL,
  addAttachmentPage,
  templateSetupPage,
  RemoveBase64FileType,
  generateZipFromCloud,
  localPrintingMessage,
  getCurrentDateTime,
  contraventionCode,
  persistantEvaderLabel,
  holdReasonStaticData,
  debtReviewCancelCases,
  caseActionsStaticData,
  challengeAction,
  reClosefollowingPaidCorrespondenceChallenge,
  toggleHorizontalLine,
  onHoldOffHoldPCN,
  stageAndStatus,
  ContraventionFileUploadPage,
  reallocationAttachment,
  exceedRows,
  pcnNumberReservationPage,
  cancellationReasonFileUploadPage,
  manualCase,
  getDisplayTypeOfAttachment,
  locationFileUploadPage,
  tecActions,
  location,
  additionalTab,
  monitoringReports,
  anprOperationalReport,
  exceedRows,
  hhdSourceType,
  challengeActionDisable,
  prefixSetupPage,
  isValidPcnNumber,
  taskPreview,
  contractTypes,
  dvlaMismatchStaticData,
  fpnBasedChanges,
  preCaseChanges,
  mismatchProgressWarnMessage,
  cancelCaseWarnMessage,
  changeBasedOnTheFPNContract,
  buttonDisabledValidation,
  isDisabledBasedOnCurrentStageStatus,
  clampAndRemovalSource,
  contractDisablingToggles,
  cancellationReasonPopup,
  cancellationReasonList,
  liveTrackingTimelineTabPermittedRoles,
  configurationPagePermittedRoles,
  poundLocationList,
  prioritySettingsList,
  associatedPCNList,
  isThisDropdownFieldValid,
  Base64ToPdfDownLoad,
  encryptPasscode,
  decryptPasscode,
  generateRandomNumbers,
  deleteConfirmationValues,
  // jobAssignmentType,
  pushNotificationjobAssignmentType,
  signalRNotificationMethods,
  clampAndRemovalAccessRoles,
  markAsPaidContent,
  debtorReview,
  hotspotSource,
  commonDateTimeFormatMoment,
  returnCurrentDateTimeBasedOnTimezoneMoment,
  convertUtcDateTimeToTimezoneDateMoment,
  airaNames,
  discountRepReject21Days,
  watchCaseTabIndexOnCaseOverview,
  encryptData,
  decryptData,
  generateRandomPassword,
  getHashedData,
  hideTitleBarForPaths,
  reportSubMenuPaths,
  airaPagesPaths,
  allowedUserForAIRAContract,
  reportFlagKeys,
  fpnReportFilterOptions,
  holdExpiryDateParseHandler,
  printImages,
  formatFileSize,
  widgetTypes,
  orgMenuItemList,
  timeStringToMinutes,
  defaultDaysDataSource,
  keeperAddressRequiredToast,
  foreignAddressHintMessage,
  foreignAddressConfirmText,
  isTabTitleCharactersValid,
  shouldCloseSliderOnPathChange,
  closeCaseValidationSlider,
  defaultDaysDataSource,
  sortByDisplayName,
  allowedUserForSummarisationContract,
  allowedUserForSummarizationOrg,
  DaysValue,
  cpzMessageContentList,
  getContractLandingPath,
}
